import React, { useEffect, useState, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
const API_BASE = process.env.REACT_APP_API_URL || 'http://10.0.0.175:5000';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, DashboardStats, JobListItem, ContractorListItem, PricingRule, VendorDocumentItem, SubscriptionPlan, AdminCustomerListItem, AdminCustomerDetail, CustomerSubscriptionDetail, AdItem, QboConnectionStatus, QboAccount, QboItem, QboAccountMapping, QboSyncError, SalesforceConnectionStatus, CrmSyncError, CrmFieldMapping, CalendarJob, ContractorAvailabilityItem, UserManagementItem, AdminAllUsersResponse, ServiceCatalogItemDto, ServiceCatalogPricingType, InventoryItemDto, InventoryUsageDto, WorkOrderDto, JobLineItemDto, EstimateDto, CreateJobDto, QuoteResponse, ServiceFeeConfigDto, ProductPreorderResponse, ServiceAreaRuleResponse, BusinessLicenseResponse, EstimateBuilderDocDto, MessageItem, DonationCenterDto, ShreddingBinLocationDto } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useBranding } from '../../context/BrandingContext';
import { useFeature } from '../../context/FeatureFlagContext';
import { useIdleDisconnect } from '../../hooks/useIdleDisconnect';
import { Card, Btn, Badge, Alert, Spinner, StatCard, Input, Select, C } from '../../components/UI';
import { AIAccuracyWidget } from '../../components/AIAccuracyWidget';
import { ContractorMap } from '../../components/ContractorMap';
import { ServiceAreaMapPanel } from '../../components/ServiceAreaMapPanel';
import { EstimateBuilderPanel } from './EstimateBuilderPanel';
import { PropertyPanel } from './PropertyPanel';
import { ComplianceDocumentPanel } from './ComplianceDocumentPanel';
import { PhotoChecklistPanel } from './PhotoChecklistPanel';
import { JobSlaPanel } from './JobSlaPanel';
import { ClientAdminPanel } from './ClientAdminPanel';
import { DonationCentersPanel } from './DonationCentersPanel';
import { ShreddingBinsPanel } from './ShreddingBinsPanel';
import { GiftVouchersPanel } from './GiftVouchersPanel';
import { DisclosurePanel } from './DisclosurePanel';
import AppMarketplacePanel from './marketplace/AppMarketplacePanel';
import { AddressAutocomplete } from '../../components/AddressAutocomplete';
import { DateTimePicker } from '../../components/DateTimePicker';
import { CancelJobModal } from '../../components/CancelJobModal';
import { PaymentForm } from '../../components/PaymentForm';
import {
    LayoutDashboard, Briefcase, Users, Map as MapIcon, DollarSign, AlertTriangle,
    Shield, FileText, Package, ChevronLeft, ChevronRight, Menu,
    LogOut, RefreshCw, ChevronDown, Bell, Search, ClipboardList, Cloud,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';

interface AdminAlert {
    id: string;
    type: 'job' | 'dispute' | 'customer' | 'document' | 'estimateRequest';
    title: string;
    detail: string;
    time: Date;
    read: boolean;
}

type View = 'overview' | 'jobs' | 'contractors' | 'map' | 'pricing' | 'disputes' | 'background-checks' | 'documents' | 'dumpster' | 'subscriptions' | 'customers' | 'ads' | 'quickbooks' | 'salesforce' | 'calendar' | 'dispatch' | 'users' | 'servicecatalog' | 'inventory' | 'preorders' | 'servicearea' | 'estimates' | 'disclosures' | 'apps' | 'properties' | 'compliance-documents' | 'photo-checklists' | 'job-sla' | 'clients' | 'donationCenters' | 'shreddingBins' | 'giftVouchers';
type NavItem = { key: View; label: string; icon: React.ReactElement; badge?: number };
type NavSection = { title: string; items: NavItem[] };

// SIDEBAR_BG is now driven by branding.primaryColor at runtime

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    // Deep link from an admin notification email — /admin?estimateId=xxx lands directly on that
    // estimate's detail view instead of making the admin hunt for it in the list.
    const [searchParams] = useSearchParams();
    const deepLinkEstimateId = searchParams.get('estimateId');
    // /admin?jobId=xxx — from the "new job needs a Runner" admin email. Lands on the Jobs view;
    // unlike the estimateId deep link above, this doesn't select/highlight the specific row.
    const deepLinkJobId = searchParams.get('jobId');
    const branding = useBranding();
    const svcLabel = branding.serviceNoun ?? 'job';
    const [showHeaderBookModal, setShowHeaderBookModal] = useState(false);
    // Set when an admin picks "This is an estimate instead" partway through booking a job for
    // a customer — hands the already-identified customer's info to the Estimates view instead
    // of making them re-enter it from scratch in the separate Prospect-based estimate flow.
    const [newEstimatePrefill, setNewEstimatePrefill] = useState<{ name: string; email?: string; phone?: string } | null>(null);
    const contractorLabelPlural = branding.contractorNoun ? branding.contractorNoun + 's' : 'Contractors';
    const dumpsterEnabled = useFeature('dumpsterRental');
    const serviceCatalogEnabled = useFeature('serviceCatalogPricing');
    const inventoryTrackingEnabled = useFeature('inventoryTracking');
    const estimateBuilderEnabled = useFeature('estimateBuilder');
    const propertyRecordsEnabled = useFeature('propertyRecords');
    const complianceDocTrackingEnabled = useFeature('complianceDocTracking');
    const photoChecklistsEnabled = useFeature('photoChecklists');
    const jobSlaTrackingEnabled = useFeature('jobSlaTracking');
    const clientPortalEnabled = useFeature('clientPortal');
    const donationCentersEnabled = useFeature('donationCenters');
    const documentShreddingEnabled = useFeature('documentShredding');
    const accent = branding.accentColor ?? branding.secondaryColor;
    const navText = branding.navTextColor ?? '#ffffff';
    const [view, setView] = useState<View>(deepLinkEstimateId ? 'estimates' : deepLinkJobId ? 'jobs' : 'overview');
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [jobs, setJobs] = useState<JobListItem[]>([]);
    const [allDumpsterOrders, setAllDumpsterOrders] = useState<any[]>([]);
    const [allLoadingJobs, setAllLoadingJobs] = useState<any[]>([]);
    const [contractors, setContractors] = useState<ContractorListItem[]>([]);
    const [shreddingBinLocations, setShreddingBinLocations] = useState<ShreddingBinLocationDto[]>([]);
    const [bgChecks, setBgChecks] = useState<ContractorBgCheckItem[]>([]);
    const [bgChecksLoading, setBgChecksLoading] = useState(false);
    const [tierDist, setTierDist] = useState<{ apprentices: number; journeymen: number; masters: number } | null>(null);
    const [documents, setDocuments] = useState<ContractorDocumentItem[]>([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [expiringDocs, setExpiringDocs] = useState<ContractorDocumentItem[]>([]);
    const [dumpsterOrders, setDumpsterOrders] = useState<any[]>([]);
    const [dumpsterVendors, setDumpsterVendors] = useState<any[]>([]);
    const [dumpsterLoading, setDumpsterLoading] = useState(false);
    const [preorders, setPreorders] = useState<ProductPreorderResponse[]>([]);
    const [preordersLoading, setPreordersLoading] = useState(false);
    const [estimates, setEstimates] = useState<EstimateBuilderDocDto[]>([]);
    const [pricing, setPricing] = useState<PricingRule[]>([]);
    const [catalogCategories, setCatalogCategories] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [collapsed, setCollapsed] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [openSections, setOpenSections] = useState<Record<string, boolean>>({
        Main: true, Operations: true, 'User Management': true, Vendors: true, System: true,
    });
    const [navSearch, setNavSearch] = useState('');
    const [alerts, setAlerts] = useState<AdminAlert[]>([]);
    const [alertPanelOpen, setAlertPanelOpen] = useState(false);
    const knownJobIds = useRef<Set<string>>(new Set());
    const knownDisputeIds = useRef<Set<string>>(new Set());
    const knownCustomerIds = useRef<Set<string>>(new Set());
    const knownDocumentKeys = useRef<Set<string>>(new Set());
    const knownEstimateRequestIds = useRef<Set<string>>(new Set());
    const initialLoad = useRef(true);
    // Last successfully-fetched value for each list that detectNewAlerts diffs against — a
    // transient fetch failure (e.g. the API restarting mid-deploy) previously fell back to `[]`,
    // which detectNewAlerts couldn't tell apart from "this list is now genuinely empty." That
    // wiped the known-IDs tracking, and the next successful poll re-announced every pre-existing
    // customer/document/estimate-request as a brand new alert. Falling back to the last-known-good
    // value instead means a failed fetch just skips that category for one cycle.
    const lastGoodCustomers = useRef<AdminCustomerListItem[]>([]);
    const lastGoodDocs = useRef<ContractorDocumentItem[]>([]);
    const lastGoodEstimates = useRef<EstimateBuilderDocDto[]>([]);
    const unreadCount = alerts.filter(a => !a.read).length;

    useEffect(() => { if (!user || user.role !== 'Admin') navigate('/'); }, [user, navigate]);

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    useEffect(() => {
        if (view === 'background-checks') fetchBgChecks();
        if (view === 'documents') fetchDocuments();
        if (view === 'dumpster') fetchDumpsterOrders();
        if (view === 'preorders') fetchPreorders();
        if (view === 'map') api.getContractors().then(setContractors).catch(() => {});
    }, [view]);

    // Runs once on mount only. This used to depend on [view] from when its body checked
    // view === 'overview'/'jobs' inside a since-removed interval — the leftover dependency
    // caused fetchAll() (and its detectNewAlerts diff) to re-run on every sidebar click,
    // racing the 30s poll below and sometimes reverting the "known IDs" refs to a stale
    // snapshot, which made already-seen jobs/customers/etc. re-alert on the next poll.
    useEffect(() => {
        fetchAll();
        fetchBgChecks();
        fetchDocuments();
        api.getTierDistribution().then(setTierDist).catch(() => {});
        api.getExpiringDocuments(30).then(setExpiringDocs).catch(() => {});
    }, []);

    useEffect(() => {
        const style = document.createElement('style');
        style.id = 'admin-alert-anim';
        style.textContent = `
            @keyframes bellRing { 0%,100%{transform:rotate(0)} 15%{transform:rotate(-18deg)} 35%{transform:rotate(18deg)} 55%{transform:rotate(-12deg)} 75%{transform:rotate(12deg)} }
            @keyframes badgePop { 0%,100%{transform:scale(1)} 50%{transform:scale(1.25)} }
            .adm-bell-ring { animation: bellRing 0.7s ease infinite; transform-origin: top center; }
            .adm-badge-pop { animation: badgePop 1.2s ease infinite; }
        `;
        if (!document.getElementById('admin-alert-anim')) document.head.appendChild(style);
        return () => { document.getElementById('admin-alert-anim')?.remove(); };
    }, []);

    // Real-time contractor online/offline updates. Idle-gated (see useIdleDisconnect) — this
    // dashboard is the surface most likely to be left open indefinitely on a desk monitor, and an
    // always-open socket here was one of the things keeping the serverless DB from ever getting a
    // clean 60-minute pause window overnight, even with zero real traffic.
    const contractorStatusHubRef = useRef<signalR.HubConnection | null>(null);
    useEffect(() => {
        const token = localStorage.getItem('br_token');
        if (!token) return;
        const hub = new signalR.HubConnectionBuilder()
            .withUrl(`${API_BASE}/hubs/tracking?access_token=${token}`, { withCredentials: false })
            .withAutomaticReconnect()
            .build();
        hub.on('ContractorStatusChanged', (payload: { contractorId: string; isOnline: boolean; lat?: number; lng?: number }) => {
            setContractors(prev => prev.map(c =>
                c.id === payload.contractorId
                    ? { ...c, isOnline: payload.isOnline, currentLat: payload.lat ?? c.currentLat, currentLng: payload.lng ?? c.currentLng }
                    : c
            ));
        });
        contractorStatusHubRef.current = hub;
        hub.start().then(() => hub.invoke('JoinAdminPool').catch(() => {})).catch(() => {});
        return () => { hub.stop(); contractorStatusHubRef.current = null; };
    }, []);

    useIdleDisconnect({
        onIdle: () => { contractorStatusHubRef.current?.stop().catch(() => {}); },
        onActive: () => {
            const hub = contractorStatusHubRef.current;
            if (hub && hub.state === signalR.HubConnectionState.Disconnected) {
                hub.start().then(() => hub.invoke('JoinAdminPool').catch(() => {})).catch(() => {});
            }
        },
    });

    const fetchDumpsterOrders = async () => {
        setDumpsterLoading(true);
        try {
            const [orders, vendors] = await Promise.all([
                api.getAllDumpsterOrders(),
                api.getAllDumpsterVendors(),
            ]);
            setDumpsterOrders(orders);
            setDumpsterVendors(vendors);
        } catch (e) { console.error(e); }
        finally { setDumpsterLoading(false); }
    };

    const fetchPreorders = async () => {
        setPreordersLoading(true);
        try { setPreorders(await api.getAllProductPreorders()); }
        catch (e) { console.error(e); }
        finally { setPreordersLoading(false); }
    };

    const fetchDocuments = async () => {
        setDocumentsLoading(true);
        try {
            const data = await api.getContractorDocuments();
            setDocuments(data);
        } catch (e) { console.error(e); }
        finally { setDocumentsLoading(false); }
    };

    const fetchBgChecks = async () => {
        setBgChecksLoading(true);
        try {
            const data = await api.getBackgroundChecks();
            setBgChecks(data);
        } catch (e) { console.error(e); }
        finally { setBgChecksLoading(false); }
    };

    const requestedEstimateIds = (list: EstimateBuilderDocDto[]) =>
        list.filter(e => e.status === 'Requested' && !e.assignedContractorId).map(e => e.id);

    const detectNewAlerts = (newJobs: JobListItem[], newCustomers: AdminCustomerListItem[], newDocs: ContractorDocumentItem[], newEstimates: EstimateBuilderDocDto[], categoriesFresh: boolean = true) => {
        if (initialLoad.current) {
            knownJobIds.current = new Set(newJobs.map(j => j.id));
            knownDisputeIds.current = new Set(newJobs.filter(j => j.priceAdjustmentStatus === 'Disputed').map(j => j.id));
            knownCustomerIds.current = new Set(newCustomers.map(c => c.id));
            knownDocumentKeys.current = new Set(newDocs.flatMap(d => pendingDocKeys(d)));
            knownEstimateRequestIds.current = new Set(requestedEstimateIds(newEstimates));
            // If customers/docs/estimates all transiently failed on this very first load (e.g. the
            // admin opens the dashboard during the same deploy-restart window b27a044 defends
            // against), lastGoodCustomers/Docs/Estimates are still their empty initial value —
            // establishing an empty baseline here would flip initialLoad false on bad data, and
            // the *next* successful poll would then flood every pre-existing item as "new,"
            // reproducing the exact bug that fix closed, just moved to "wrong baseline" instead of
            // "corrupted baseline." Only commit to this baseline once every category came back real.
            if (categoriesFresh) initialLoad.current = false;
            return;
        }
        const now = new Date();
        const newAlerts: AdminAlert[] = [];

        newJobs.forEach(j => {
            if (!knownJobIds.current.has(j.id))
                newAlerts.push({ id: `job-${j.id}`, type: 'job', title: 'New job booked', detail: `${j.customerName} — ${j.serviceType}`, time: now, read: false });
        });
        knownJobIds.current = new Set(newJobs.map(j => j.id));

        newJobs.filter(j => j.priceAdjustmentStatus === 'Disputed').forEach(j => {
            if (!knownDisputeIds.current.has(j.id))
                newAlerts.push({ id: `dispute-${j.id}`, type: 'dispute', title: 'Price dispute filed', detail: `${j.customerName} — ${j.serviceType}`, time: now, read: false });
        });
        knownDisputeIds.current = new Set(newJobs.filter(j => j.priceAdjustmentStatus === 'Disputed').map(j => j.id));

        newCustomers.forEach(c => {
            if (!knownCustomerIds.current.has(c.id))
                newAlerts.push({ id: `customer-${c.id}`, type: 'customer', title: 'New customer registered', detail: c.name || c.email, time: now, read: false });
        });
        knownCustomerIds.current = new Set(newCustomers.map(c => c.id));

        if (newDocs.length > 0) {
            newDocs.forEach(d => {
                pendingDocKeys(d).forEach(key => {
                    if (!knownDocumentKeys.current.has(key)) {
                        const docType = key.split('-')[1];
                        newAlerts.push({ id: `doc-${key}`, type: 'document', title: 'Document submitted', detail: `${d.name} submitted ${docType} for review`, time: now, read: false });
                    }
                });
            });
            knownDocumentKeys.current = new Set(newDocs.flatMap(d => pendingDocKeys(d)));
        }

        const newEstimateIds = requestedEstimateIds(newEstimates);
        newEstimateIds.forEach(id => {
            if (!knownEstimateRequestIds.current.has(id)) {
                const e = newEstimates.find(x => x.id === id)!;
                newAlerts.push({ id: `estreq-${id}`, type: 'estimateRequest', title: 'New estimate request', detail: `${e.prospect.name} — ${e.jobSiteAddress}`, time: now, read: false });
            }
        });
        knownEstimateRequestIds.current = new Set(newEstimateIds);

        if (newAlerts.length > 0) setAlerts(prev => [...newAlerts, ...prev].slice(0, 100));
    };

    // Returns a unique key for each pending document slot — only fires when status is Pending
    const pendingDocKeys = (d: ContractorDocumentItem): string[] => {
        const keys: string[] = [];
        if (d.driversLicenseStatus === 'Pending') keys.push(`${d.contractorId}-license`);
        if (d.insuranceStatus === 'Pending') keys.push(`${d.contractorId}-insurance`);
        if (d.vehicleStatus === 'Pending') keys.push(`${d.contractorId}-vehicle`);
        return keys;
    };

    // Silent background poll — fetches fresh jobs/customers/documents every 30s to fire the
    // alert bell. It was discarding that data after computing the diff instead of applying it,
    // so the visible Jobs table never reflected a status change (accept, complete, dispute, etc.)
    // until the admin manually reloaded the page. Does not touch setLoading so the page doesn't
    // flicker every 30s.
    const detectRef = useRef(detectNewAlerts);
    detectRef.current = detectNewAlerts;
    const estimateBuilderEnabledRef = useRef(estimateBuilderEnabled);
    estimateBuilderEnabledRef.current = estimateBuilderEnabled;
    const pollFnRef = useRef<() => void>(() => {});
    const pollIntervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);
    useEffect(() => {
        const poll = async () => {
            try {
                const [j, customersResult, docsResult, estimatesResult] = await Promise.all([
                    api.getAllJobs(),
                    api.adminGetCustomers('').then(v => ({ ok: true as const, v }), () => ({ ok: false as const })),
                    api.getContractorDocuments().then(v => ({ ok: true as const, v }), () => ({ ok: false as const })),
                    estimateBuilderEnabledRef.current
                        ? api.getEstimateBuilderDocs().then(v => ({ ok: true as const, v }), () => ({ ok: false as const }))
                        : Promise.resolve({ ok: true as const, v: [] as EstimateBuilderDocDto[] }),
                ]);
                const customers = customersResult.ok ? customersResult.v : lastGoodCustomers.current;
                const docs = docsResult.ok ? docsResult.v : lastGoodDocs.current;
                const estimatesData = estimatesResult.ok ? estimatesResult.v : lastGoodEstimates.current;
                lastGoodCustomers.current = customers;
                lastGoodDocs.current = docs;
                lastGoodEstimates.current = estimatesData;

                detectRef.current(j, customers, docs, estimatesData, customersResult.ok && docsResult.ok && estimatesResult.ok);
                setJobs(j);
                setDocuments(docs);
                setEstimates(estimatesData);
            } catch { /* silent */ }
        };
        pollFnRef.current = poll;
        const id = setInterval(poll, 30_000);
        pollIntervalIdRef.current = id;
        return () => { clearInterval(id); pollIntervalIdRef.current = null; };
    }, []);

    // Idle-gated: stop hitting the API every 30s once the admin has genuinely stepped away
    // (see useIdleDisconnect) — resumes immediately, with a fresh poll, on real activity.
    useIdleDisconnect({
        onIdle: () => {
            if (pollIntervalIdRef.current) { clearInterval(pollIntervalIdRef.current); pollIntervalIdRef.current = null; }
        },
        onActive: () => {
            if (!pollIntervalIdRef.current) {
                pollFnRef.current();
                pollIntervalIdRef.current = setInterval(() => pollFnRef.current(), 30_000);
            }
        },
    });

    // Bin locations for the Live Map's bin layer — scoped to the map tab specifically rather than
    // folded into fetchAll's big combined poll, since nothing else needs this data and most admin
    // sessions never open the map tab at all.
    useEffect(() => {
        if (view !== 'map' || !documentShreddingEnabled) return;
        const load = () => api.getShreddingBinLocations().then(setShreddingBinLocations).catch(() => {});
        load();
        const id = setInterval(load, 30_000);
        return () => clearInterval(id);
    }, [view, documentShreddingEnabled]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [s, j, dumpOrders, loadJobs, c, p, customersResult, docsResult, estimatesResult, catalog] = await Promise.all([
                api.getStats(),
                api.getAllJobs(),
                api.getAllDumpsterOrders().catch(() => []),
                api.getAdminLoadingJobs().catch(() => []),
                api.getContractors(),
                api.getPricing(),
                api.adminGetCustomers('').then(v => ({ ok: true as const, v }), () => ({ ok: false as const })),
                api.getContractorDocuments().then(v => ({ ok: true as const, v }), () => ({ ok: false as const })),
                estimateBuilderEnabled
                    ? api.getEstimateBuilderDocs().then(v => ({ ok: true as const, v }), () => ({ ok: false as const }))
                    : Promise.resolve({ ok: true as const, v: [] as EstimateBuilderDocDto[] }),
                serviceCatalogEnabled ? api.getServiceCatalogAll().catch(() => [] as ServiceCatalogItemDto[]) : Promise.resolve([] as ServiceCatalogItemDto[]),
            ]);
            // See lastGoodCustomers/lastGoodDocs/lastGoodEstimates above — a transient fetch
            // failure falls back to the last-known-good value rather than `[]`, so it can't wipe
            // detectNewAlerts' known-IDs tracking and cause a flood of "new" alerts for
            // pre-existing items on the next successful fetch.
            const customers = customersResult.ok ? customersResult.v : lastGoodCustomers.current;
            const docs = docsResult.ok ? docsResult.v : lastGoodDocs.current;
            const estimatesData = estimatesResult.ok ? estimatesResult.v : lastGoodEstimates.current;
            lastGoodCustomers.current = customers;
            lastGoodDocs.current = docs;
            lastGoodEstimates.current = estimatesData;

            setStats(s); setJobs(j); setContractors(c); setPricing(p);
            setAllDumpsterOrders(dumpOrders);
            setAllLoadingJobs(loadJobs);
            setEstimates(estimatesData);
            // Contractor per-service pay rate overrides need to be settable for Service Catalog
            // categories too, not just the legacy PricingRule types — otherwise a converted
            // Estimate Builder job's ServiceTypeName (derived from its line items' catalog
            // categories) can never match any configurable override.
            setCatalogCategories(Array.from(new Set(catalog.filter(i => i.isActive).map(i => i.category).filter(Boolean))));
            detectNewAlerts(j, customers, docs, estimatesData, customersResult.ok && docsResult.ok && estimatesResult.ok);
        } catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    const disputedJobs = jobs.filter(j => j.priceAdjustmentStatus === 'Disputed');
    const considerCount = bgChecks.filter(b => b.backgroundCheckStatus === 'Consider').length;
    const pendingDocCount = documents.filter(d =>
        d.driversLicenseStatus === 'Submitted' ||
        d.insuranceStatus === 'Submitted' ||
        d.vehicleStatus === 'Submitted'
    ).length;
    const requestedEstimateCount = estimates.filter(e => e.status === 'Requested' && !e.assignedContractorId).length;
    const estimateNavBadgeCount = requestedEstimateCount + estimates.filter(e => e.status === 'Approved' && !e.convertedJobId).length;

    const navSections: NavSection[] = [
        {
            title: 'Main',
            items: [
                { key: 'overview', label: 'Dashboard', icon: <LayoutDashboard size={16} /> },
            ],
        },
        {
            title: 'Operations',
            items: [
                { key: 'jobs', label: 'Jobs', icon: <Briefcase size={16} />, badge: jobs.length || undefined },
                { key: 'dispatch', label: 'Dispatch & Calendar', icon: <ChevronRight size={16} /> },
                { key: 'disputes', label: 'Disputes', icon: <AlertTriangle size={16} />, badge: disputedJobs.length || undefined },
                { key: 'map', label: 'Live Map', icon: <MapIcon size={16} /> },
            ],
        },
        {
            title: 'User Management',
            items: [
                { key: 'customers', label: 'Customers', icon: <Users size={16} /> },
                { key: 'contractors', label: contractorLabelPlural, icon: <Users size={16} />, badge: contractors.length || undefined },
                { key: 'background-checks', label: 'Background Checks', icon: <Shield size={16} />, badge: considerCount || undefined },
                { key: 'documents', label: 'Onboarding Docs', icon: <FileText size={16} />, badge: pendingDocCount || undefined },
                { key: 'users', label: 'User Accounts', icon: <Users size={16} /> },
            ],
        },
        ...(dumpsterEnabled ? [{
            title: 'Vendors',
            items: [
                { key: 'dumpster' as View, label: 'Dumpster Orders', icon: <Package size={16} />, badge: dumpsterOrders.length || undefined },
            ],
        }] : []),
        {
            title: 'Product & Pricing',
            items: [
                ...(serviceCatalogEnabled ? [{ key: 'servicecatalog' as View, label: 'Service Catalog', icon: <ClipboardList size={16} /> }] : []),
                { key: 'pricing', label: 'Standard Job Pricing', icon: <DollarSign size={16} /> },
                { key: 'giftVouchers', label: 'Gift Vouchers', icon: <DollarSign size={16} /> },
                ...(donationCentersEnabled ? [{ key: 'donationCenters' as View, label: 'Donation Centers', icon: <MapIcon size={16} /> }] : []),
                ...(documentShreddingEnabled ? [{ key: 'shreddingBins' as View, label: 'Shredding Bins', icon: <Package size={16} /> }] : []),
                ...(inventoryTrackingEnabled ? [{ key: 'inventory' as View, label: 'Inventory', icon: <Package size={16} /> }] : []),
                { key: 'subscriptions', label: 'Subscription Plans', icon: <Package size={16} /> },
            ],
        },
        {
            title: 'Sales & Marketing',
            items: [
                { key: 'preorders' as View, label: 'Preorders', icon: <Package size={16} />, badge: preorders.filter(p => p.fulfillmentStatus === 'Pending').length || undefined },
                ...(estimateBuilderEnabled ? [{ key: 'estimates' as View, label: 'Estimates', icon: <FileText size={16} />, badge: estimateNavBadgeCount || undefined }] : []),
                { key: 'ads', label: 'Ad Management', icon: <Briefcase size={16} /> },
            ],
        },
        ...(propertyRecordsEnabled || clientPortalEnabled ? [{
            title: 'Properties',
            items: [
                ...(propertyRecordsEnabled ? [{ key: 'properties' as View, label: 'Properties', icon: <MapIcon size={16} /> }] : []),
                ...(clientPortalEnabled ? [{ key: 'clients' as View, label: 'Clients', icon: <Users size={16} /> }] : []),
            ],
        }] : []),
        // Own section, not nested under "Properties" — ComplianceDocument is a generic
        // holder-agnostic primitive (Tenant/Contractor/Vendor/Property), not mortgage-specific.
        // A Document Shredding tenant with no Property Records enabled would otherwise see its
        // own certifications filed under a "Properties" heading, which makes no sense for them.
        ...(complianceDocTrackingEnabled ? [{
            title: 'Compliance',
            items: [
                { key: 'compliance-documents' as View, label: 'Compliance Documents', icon: <FileText size={16} /> },
            ],
        }] : []),
        {
            title: 'Apps',
            items: [
                { key: 'apps' as View, label: 'App Marketplace', icon: <Package size={16} /> },
            ],
        },
        {
            title: 'Company Settings',
            items: [
                { key: 'servicearea' as View, label: 'Service Area', icon: <MapIcon size={16} /> },
                { key: 'disclosures' as View, label: 'Disclosures', icon: <FileText size={16} /> },
                ...(photoChecklistsEnabled ? [{ key: 'photo-checklists' as View, label: 'Photo Checklists', icon: <ClipboardList size={16} /> }] : []),
                ...(jobSlaTrackingEnabled ? [{ key: 'job-sla' as View, label: 'Job SLA Tracking', icon: <AlertTriangle size={16} /> }] : []),
                { key: 'quickbooks', label: 'QuickBooks', icon: <DollarSign size={16} /> },
                { key: 'salesforce' as View, label: 'Salesforce', icon: <Cloud size={16} /> },
            ],
        },
    ];

    const sidebarWidth = isMobile ? 240 : (collapsed ? 60 : 220);

    const handleNav = (key: View) => {
        setView(key);
        if (isMobile) setMobileOpen(false);
    };

    const iconOnly = collapsed && !isMobile;

    const navSearchResults = navSearch.trim()
        ? navSections.flatMap(s => s.items).filter(item => item.label.toLowerCase().includes(navSearch.trim().toLowerCase()))
        : null;

    const sidebarContent = (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Nav search */}
            {!iconOnly && (
                <div style={{ padding: '12px 14px', borderBottom: `1px solid ${navText}1f`, flexShrink: 0 }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: navText, opacity: 0.6 }} />
                        <input
                            value={navSearch}
                            onChange={e => setNavSearch(e.target.value)}
                            placeholder="Search…"
                            style={{
                                width: '100%', boxSizing: 'border-box', padding: '8px 10px 8px 30px',
                                borderRadius: 7, border: `1px solid ${navText}33`, background: 'rgba(255,255,255,0.08)',
                                color: navText, fontSize: 13, outline: 'none',
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Nav sections */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
                {navSearchResults ? (
                    navSearchResults.length === 0 ? (
                        <div style={{ padding: '12px 16px', fontSize: 13, color: navText, opacity: 0.6 }}>No matches</div>
                    ) : navSearchResults.map(item => {
                        const isActive = view === item.key;
                        return (
                            <button
                                key={item.key}
                                onClick={() => { handleNav(item.key); setNavSearch(''); }}
                                style={{
                                    width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px',
                                    background: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                                    border: 'none', cursor: 'pointer', color: navText, fontSize: 13,
                                    fontWeight: isActive ? 600 : 400, textAlign: 'left',
                                }}>
                                <span style={{ flexShrink: 0, display: 'flex' }}>{item.icon}</span>
                                <span style={{ flex: 1 }}>{item.label}</span>
                            </button>
                        );
                    })
                ) : navSections.map(section => (
                    <div key={section.title}>
                        {!iconOnly && (
                            <button
                                onClick={() => setOpenSections(s => ({ ...s, [section.title]: !s[section.title] }))}
                                style={{
                                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '10px 16px 4px', background: 'transparent', border: 'none', cursor: 'pointer',
                                    color: accent, fontSize: 10, fontWeight: 700,
                                    letterSpacing: '0.08em', textTransform: 'uppercase',
                                }}>
                                {section.title}
                                <ChevronDown size={11} style={{
                                    transform: openSections[section.title] ? 'rotate(0deg)' : 'rotate(-90deg)',
                                    transition: 'transform 0.15s',
                                }} />
                            </button>
                        )}
                        {(iconOnly || openSections[section.title]) && section.items.map(item => {
                            const isActive = view === item.key;
                            return (
                                <button
                                    key={item.key}
                                    onClick={() => handleNav(item.key)}
                                    title={iconOnly ? item.label : undefined}
                                    style={{
                                        width: '100%', display: 'flex', alignItems: 'center',
                                        gap: 9, padding: iconOnly ? '11px 0' : '9px 14px',
                                        justifyContent: iconOnly ? 'center' : 'flex-start',
                                        background: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                                        border: 'none', cursor: 'pointer',
                                        color: navText,
                                        fontSize: 13, fontWeight: isActive ? 600 : 400,
                                        borderLeft: iconOnly ? 'none' : (isActive ? `3px solid ${accent}` : '3px solid transparent'),
                                        transition: 'background 0.1s',
                                        textAlign: 'left',
                                    }}>
                                    <span style={{ flexShrink: 0, display: 'flex' }}>{item.icon}</span>
                                    {!iconOnly && <span style={{ flex: 1 }}>{item.label}</span>}
                                    {!iconOnly && item.badge ? (
                                        <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 700 }}>
                                            {item.badge}
                                        </span>
                                    ) : null}
                                </button>
                            );
                        })}
                        <div style={{ height: 4 }} />
                    </div>
                ))}
            </div>

            {/* Collapse toggle (desktop only) */}
            {!isMobile && (
                <button
                    onClick={() => setCollapsed(c => !c)}
                    style={{
                        width: '100%', display: 'flex', alignItems: 'center',
                        justifyContent: iconOnly ? 'center' : 'flex-end',
                        padding: '12px 16px', background: 'transparent', border: 'none',
                        borderTop: `1px solid ${navText}1f`,
                        cursor: 'pointer', color: navText, flexShrink: 0,
                    }}>
                    {iconOnly
                        ? <ChevronRight size={15} />
                        : <><ChevronLeft size={15} /><span style={{ fontSize: 12, marginLeft: 6 }}>Collapse</span></>}
                </button>
            )}
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: C.bg }}>
            {/* Header bar */}
            <header style={{
                height: 56, background: branding.primaryColor, borderBottom: '1px solid rgba(0,0,0,0.15)',
                display: 'flex', alignItems: 'center', padding: '0 20px', gap: 12,
                flexShrink: 0, zIndex: 100,
            }}>
                {isMobile && (
                    <button onClick={() => setMobileOpen(o => !o)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6, display: 'flex', alignItems: 'center', color: navText }}>
                        <Menu size={20} />
                    </button>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {!isMobile && branding.logoUrl && (
                        <img src={branding.logoUrl} alt={branding.appName} style={{ height: 34, maxWidth: 140, objectFit: 'contain' }} />
                    )}
                    {!isMobile && (
                        <span style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontStyle: 'italic', fontSize: 16, letterSpacing: 1, color: navText }}>
                            {branding.appName}
                            <span style={{ fontFamily: "'Inter', sans-serif", color: navText, opacity: 0.75, fontWeight: 400, fontStyle: 'normal', fontSize: 13, marginLeft: 8 }}>Admin</span>
                        </span>
                    )}
                </div>
                <div style={{ flex: 1 }} />
                <button onClick={() => setShowHeaderBookModal(true)} style={{ background: C.green, border: 'none', borderRadius: 7, cursor: 'pointer', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#fff' }}>
                    📞 {!isMobile && 'Book for Customer'}
                </button>
                <button onClick={fetchAll} style={{ background: 'transparent', border: `1px solid ${navText}26`, borderRadius: 7, cursor: 'pointer', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: navText }}>
                    <RefreshCw size={13} />
                    {!isMobile && 'Refresh'}
                </button>
                <button
                    onClick={() => {
                        setAlertPanelOpen(o => !o);
                        if (!alertPanelOpen) setAlerts(prev => prev.map(a => ({ ...a, read: true })));
                    }}
                    style={{ position: 'relative', background: 'transparent', border: `1px solid ${unreadCount > 0 ? C.coral : navText + '26'}`, borderRadius: 7, cursor: 'pointer', padding: '6px 10px', display: 'flex', alignItems: 'center', color: unreadCount > 0 ? C.coral : navText + 'b3' }}
                >
                    <Bell size={16} className={unreadCount > 0 ? 'adm-bell-ring' : ''} />
                    {unreadCount > 0 && (
                        <span className="adm-badge-pop" style={{ position: 'absolute', top: -5, right: -5, background: C.coral, color: '#fff', borderRadius: '50%', minWidth: 17, height: 17, fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
                            {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                    )}
                </button>
                <div style={{ width: 1, height: 24, background: navText + '1a', margin: '0 4px' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13 }}>
                        {(user as any)?.name?.[0]?.toUpperCase() ?? 'A'}
                    </div>
                    {!isMobile && <span style={{ fontSize: 13, fontWeight: 500, color: navText }}>{(user as any)?.name ?? 'Admin'}</span>}
                </div>
                <button onClick={() => { logout(); navigate('/'); }} style={{ background: 'transparent', border: `1px solid ${accent}`, borderRadius: 7, cursor: 'pointer', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: accent }}>
                    <LogOut size={13} />
                    {!isMobile && 'Sign out'}
                </button>
            </header>

            {showHeaderBookModal && (
                <AdminBookJobModal
                    svcLabel={svcLabel}
                    onClose={() => setShowHeaderBookModal(false)}
                    onCreated={() => { setShowHeaderBookModal(false); fetchAll(); }}
                    onRouteToEstimate={estimateBuilderEnabled ? (customer) => {
                        setShowHeaderBookModal(false);
                        setNewEstimatePrefill(customer);
                        setView('estimates');
                    } : undefined}
                />
            )}

            {/* Alert panel */}
            {alertPanelOpen && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 400 }}>
                    <div onClick={() => setAlertPanelOpen(false)} style={{ position: 'absolute', inset: 0 }} />
                    <div style={{ position: 'absolute', top: 56, right: 0, bottom: 0, width: 360, background: C.card, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', boxShadow: '-6px 0 24px rgba(0,0,0,0.1)', zIndex: 1 }}>
                        {/* Panel header */}
                        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>Activity Alerts</div>
                                <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>Since last refresh</div>
                            </div>
                            {alerts.length > 0 && (
                                <button onClick={() => setAlerts([])} style={{ background: 'transparent', border: 'none', fontSize: 12, color: C.textMuted, cursor: 'pointer', padding: '4px 8px' }}>
                                    Clear all
                                </button>
                            )}
                        </div>
                        {/* Alert list */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                            {alerts.length === 0 ? (
                                <div style={{ padding: 40, textAlign: 'center', color: C.textMuted }}>
                                    <div style={{ fontSize: 32, marginBottom: 12 }}>🔔</div>
                                    <div style={{ fontSize: 14, fontWeight: 600, color: C.textSub }}>No alerts yet</div>
                                    <div style={{ fontSize: 12, marginTop: 4 }}>Hit Refresh to check for new activity</div>
                                </div>
                            ) : alerts.map(a => (
                                <div key={a.id} style={{ padding: '12px 20px', borderBottom: `1px solid ${C.borderLight}`, display: 'flex', gap: 12, alignItems: 'flex-start', background: a.read ? 'transparent' : `${C.greenPale}` }}>
                                    <div style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>
                                        {a.type === 'job' ? '📦' : a.type === 'dispute' ? '⚠️' : a.type === 'document' ? '📋' : a.type === 'estimateRequest' ? '🧾' : '👤'}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 700, fontSize: 13, color: a.type === 'dispute' ? '#854F0B' : C.text }}>{a.title}</div>
                                        <div style={{ fontSize: 12, color: C.textSub, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.detail}</div>
                                        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>{a.time.toLocaleTimeString()}</div>
                                    </div>
                                    {a.type === 'job' && (
                                        <button onClick={() => { setView('jobs'); setAlertPanelOpen(false); }} style={{ background: 'transparent', border: 'none', fontSize: 11, color: C.green, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', padding: 0 }}>
                                            View →
                                        </button>
                                    )}
                                    {a.type === 'dispute' && (
                                        <button onClick={() => { setView('disputes'); setAlertPanelOpen(false); }} style={{ background: 'transparent', border: 'none', fontSize: 11, color: '#854F0B', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', padding: 0 }}>
                                            Review →
                                        </button>
                                    )}
                                    {a.type === 'customer' && (
                                        <button onClick={() => { setView('customers'); setAlertPanelOpen(false); }} style={{ background: 'transparent', border: 'none', fontSize: 11, color: C.green, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', padding: 0 }}>
                                            View →
                                        </button>
                                    )}
                                    {a.type === 'document' && (
                                        <button onClick={() => { setView('documents'); setAlertPanelOpen(false); }} style={{ background: 'transparent', border: 'none', fontSize: 11, color: C.green, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', padding: 0 }}>
                                            Review →
                                        </button>
                                    )}
                                    {a.type === 'estimateRequest' && (
                                        <button onClick={() => { setView('estimates'); setAlertPanelOpen(false); }} style={{ background: 'transparent', border: 'none', fontSize: 11, color: C.green, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', padding: 0 }}>
                                            Review →
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        {/* Footer */}
                        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.textMuted, flexShrink: 0 }}>
                            Alerts detected since last manual refresh
                        </div>
                    </div>
                </div>
            )}

            {/* Body */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
                {/* Mobile overlay */}
                {isMobile && mobileOpen && (
                    <div onClick={() => setMobileOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200 }} />
                )}

                {/* Sidebar */}
                <aside style={{
                    width: sidebarWidth,
                    background: branding.primaryColor,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowX: 'hidden',
                    ...(isMobile ? {
                        position: 'fixed',
                        top: 56, left: 0, bottom: 0,
                        zIndex: 201,
                        transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
                        transition: 'transform 0.22s ease',
                    } : {
                        transition: 'width 0.2s ease',
                    }),
                }}>
                    {sidebarContent}
                </aside>

                {/* Main content */}
                <main style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '20px 16px' : '28px 32px', minWidth: 0 }}>
                    <Alert message={error} />
                    {loading ? <Spinner /> : (
                        <>
                            {view === 'overview' && stats && <OverviewPanel stats={stats} jobs={jobs} contractors={contractors} bgChecks={bgChecks} tierDist={tierDist} expiringDocs={expiringDocs} dumpsterOrders={allDumpsterOrders} loadingJobs={allLoadingJobs} documents={documents} estimates={estimates} estimateBuilderEnabled={estimateBuilderEnabled} preorders={preorders} onNavigate={setView} />}
                            {view === 'jobs' && (
                                <JobsPanel
                                    jobs={jobs} dumpsterOrders={allDumpsterOrders} loadingJobs={allLoadingJobs} onRefresh={fetchAll}
                                    onRouteToEstimate={estimateBuilderEnabled ? (customer) => { setNewEstimatePrefill(customer); setView('estimates'); } : undefined}
                                />
                            )}
                            {view === 'contractors' && <ContractorsPanel contractors={contractors} documents={documents} pricing={pricing} catalogCategories={catalogCategories} />}
                            {view === 'map' && <ContractorMap contractors={contractors} jobs={jobs} bins={documentShreddingEnabled ? shreddingBinLocations : undefined} />}
                            {view === 'background-checks' && <BackgroundChecksPanel checks={bgChecks} loading={bgChecksLoading} onRefresh={fetchBgChecks} />}
                            {view === 'documents' && <DocumentsPanel documents={documents} loading={documentsLoading} expiringDocs={expiringDocs} onRefresh={fetchDocuments} />}
                            {view === 'dumpster' && dumpsterEnabled && <DumpsterAdminPanel orders={dumpsterOrders} vendors={dumpsterVendors} loading={dumpsterLoading} onRefresh={fetchDumpsterOrders} />}
                            {view === 'servicearea' && <ServiceAreaPanel />}
                            {view === 'estimates' && estimateBuilderEnabled && (
                                <EstimateBuilderPanel
                                    initialSelectedId={deepLinkEstimateId}
                                    initialNewProspect={newEstimatePrefill}
                                    onConsumeNewProspect={() => setNewEstimatePrefill(null)}
                                />
                            )}
                            {view === 'disclosures' && <DisclosurePanel />}
                            {view === 'preorders' && (
                                <ProductPreorderAdminPanel preorders={preorders} loading={preordersLoading} onRefresh={fetchPreorders} />
                            )}
                            {view === 'servicecatalog' && serviceCatalogEnabled && <ServiceCatalogPanel />}
                            {view === 'inventory' && inventoryTrackingEnabled && <InventoryPanel />}
                            {view === 'properties' && propertyRecordsEnabled && <PropertyPanel />}
                            {view === 'compliance-documents' && complianceDocTrackingEnabled && <ComplianceDocumentPanel />}
                            {view === 'photo-checklists' && photoChecklistsEnabled && <PhotoChecklistPanel />}
                            {view === 'job-sla' && jobSlaTrackingEnabled && <JobSlaPanel />}
                            {view === 'clients' && clientPortalEnabled && <ClientAdminPanel />}
                            {view === 'disputes' && <DisputesPanel jobs={jobs} onResolved={fetchAll} />}
                            {view === 'pricing' && <PricingPanel rules={pricing} onSaved={fetchAll} />}
                            {view === 'donationCenters' && donationCentersEnabled && <DonationCentersPanel />}
                            {view === 'shreddingBins' && documentShreddingEnabled && <ShreddingBinsPanel />}
                            {view === 'giftVouchers' && <GiftVouchersPanel />}
                            {view === 'subscriptions' && <SubscriptionPlansAdmin />}
                            {view === 'customers' && <CustomersPanel />}
                            {view === 'ads' && <AdsPanel />}
                            {view === 'quickbooks' && <QuickBooksPanel />}
                            {view === 'salesforce' && <SalesforcePanel />}
                            {view === 'apps' && <AppMarketplacePanel />}
                            {(view === 'calendar' || view === 'dispatch') && <DispatchCalendarPanel contractors={contractors} pricing={pricing} />}
                            {view === 'users' && <UserManagementPanel />}
                        </>
                    )}
                </main>
            </div>
        </div>
    );
}

// ── Chart helpers ─────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label, prefix = '' }: any) {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            {label && <div style={{ color: '#898781', marginBottom: 4 }}>{label}</div>}
            {payload.map((p: any, i: number) => (
                <div key={i} style={{ color: p.color, fontWeight: 600 }}>
                    {p.name}: {prefix}{typeof p.value === 'number' ? p.value.toLocaleString('en-US', { minimumFractionDigits: prefix === '$' ? 2 : 0, maximumFractionDigits: prefix === '$' ? 2 : 0 }) : p.value}
                </div>
            ))}
        </div>
    );
}

// ── User Management Panel ─────────────────────────────────────────────────────

type UserTab = 'admins' | 'customers' | 'contractors' | 'vendors';

function UserManagementPanel() {
    const branding = useBranding();
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const contractorLabelPlural = branding.contractorNoun ? branding.contractorNoun + 's' : 'Contractors';
    const [tab, setTab] = useState<UserTab>('customers');
    const [data, setData] = useState<AdminAllUsersResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [msg, setMsg] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', role: 'Customer', phoneNumber: '' });
    const [createLoading, setCreateLoading] = useState(false);
    const [editingUser, setEditingUser] = useState<UserManagementItem | null>(null);
    const [editForm, setEditForm] = useState({ name: '', email: '', phoneNumber: '' });
    const [editLoading, setEditLoading] = useState(false);

    const fetchUsers = async (q?: string) => {
        setLoading(true);
        try {
            const res = await api.adminGetAllUsers(q);
            setData(res);
        } catch {
            setErrorMsg('Failed to load users.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchUsers(); }, []);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        fetchUsers(search || undefined);
    };

    const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

    const toggleActive = async (u: UserManagementItem, active: boolean) => {
        try {
            if (active) await api.adminActivateUser(u.id, u.role);
            else await api.adminDeactivateUser(u.id, u.role);
            flash(`${u.name} ${active ? 'activated' : 'deactivated'}.`);
            fetchUsers(search || undefined);
        } catch { setErrorMsg('Failed to update user.'); }
    };

    const resetPassword = async (u: UserManagementItem) => {
        try {
            await api.adminResetUserPassword(u.id, u.role);
            flash(`Password reset email sent to ${u.email}.`);
        } catch { setErrorMsg('Failed to send reset email.'); }
    };

    const deleteUser = async (u: UserManagementItem) => {
        if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
        try {
            await api.adminDeleteUser(u.id, u.role);
            flash(`${u.name} deleted.`);
            fetchUsers(search || undefined);
        } catch { setErrorMsg('Failed to delete user.'); }
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setCreateLoading(true);
        try {
            await api.adminCreateUser(createForm);
            flash(`${createForm.role} account created for ${createForm.name}.`);
            setShowCreate(false);
            setCreateForm({ name: '', email: '', password: '', role: 'Customer', phoneNumber: '' });
            fetchUsers(search || undefined);
        } catch (err: any) {
            setErrorMsg(err.message ?? 'Failed to create user.');
        } finally {
            setCreateLoading(false);
        }
    };

    const startEdit = (u: UserManagementItem) => {
        setShowCreate(false);
        setEditingUser(u);
        setEditForm({ name: u.name, email: u.email, phoneNumber: u.phone ?? '' });
    };

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingUser) return;
        setEditLoading(true);
        try {
            await api.adminUpdateUser(editingUser.id, editingUser.role, editForm);
            flash(`${editForm.name} updated.`);
            setEditingUser(null);
            fetchUsers(search || undefined);
        } catch (err: any) {
            setErrorMsg(err.message ?? 'Failed to update user.');
        } finally {
            setEditLoading(false);
        }
    };

    const rows: UserManagementItem[] = !data ? [] : (
        tab === 'admins' ? data.admins :
        tab === 'customers' ? data.customers :
        tab === 'contractors' ? data.contractors :
        data.vendors
    );

    const tabs: { key: UserTab; label: string }[] = [
        { key: 'customers', label: 'Customers' },
        { key: 'contractors', label: contractorLabelPlural },
        { key: 'vendors', label: 'Vendors' },
        { key: 'admins', label: 'Admins' },
    ];

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>User Management</h2>
                <Btn onClick={() => setShowCreate(!showCreate)} style={{ fontSize: 13 }}>+ Add User</Btn>
            </div>

            <Alert message={errorMsg} />
            {msg && <div style={{ background: C.greenBg, color: C.greenMid, borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>{msg}</div>}

            {showCreate && (
                <Card style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 600, marginBottom: 14 }}>Create Account</div>
                    <form onSubmit={handleCreate}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                            <Input label="Full name" value={createForm.name} onChange={v => setCreateForm(f => ({ ...f, name: v }))} required />
                            <Input label="Email" type="email" value={createForm.email} onChange={v => setCreateForm(f => ({ ...f, email: v }))} required />
                            <Input label="Password (optional — auto-generated + emailed if blank)" type="password" value={createForm.password} onChange={v => setCreateForm(f => ({ ...f, password: v }))} />
                            <Input label="Phone (optional)" value={createForm.phoneNumber} onChange={v => setCreateForm(f => ({ ...f, phoneNumber: v }))} />
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <select value={createForm.role} onChange={e => setCreateForm(f => ({ ...f, role: e.target.value }))}
                                style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: C.bg }}>
                                <option value="Customer">Customer</option>
                                <option value="Contractor">{contractorLabel}</option>
                                <option value="Admin">Admin</option>
                            </select>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Btn type="submit" disabled={createLoading}>{createLoading ? 'Creating…' : 'Create'}</Btn>
                            <button type="button" onClick={() => setShowCreate(false)}
                                style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'none', cursor: 'pointer', fontSize: 13 }}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </Card>
            )}

            {editingUser && (
                <Card style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 600, marginBottom: 14 }}>Edit {editingUser.role} — {editingUser.name}</div>
                    <form onSubmit={handleUpdate}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                            <Input label="Full name" value={editForm.name} onChange={v => setEditForm(f => ({ ...f, name: v }))} required />
                            <Input label="Email" type="email" value={editForm.email} onChange={v => setEditForm(f => ({ ...f, email: v }))} required />
                            {editingUser.role !== 'Admin' && (
                                <Input label="Phone (optional)" value={editForm.phoneNumber} onChange={v => setEditForm(f => ({ ...f, phoneNumber: v }))} />
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Btn type="submit" disabled={editLoading}>{editLoading ? 'Saving…' : 'Save'}</Btn>
                            <button type="button" onClick={() => setEditingUser(null)}
                                style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'none', cursor: 'pointer', fontSize: 13 }}>
                                Cancel
                            </button>
                        </div>
                    </form>
                </Card>
            )}

            <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <input placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)}
                    style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: C.bg }} />
                <Btn type="submit">Search</Btn>
                {search && <button type="button" onClick={() => { setSearch(''); fetchUsers(); }}
                    style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'none', cursor: 'pointer', fontSize: 13 }}>Clear</button>}
            </form>

            <div style={{ display: 'flex', gap: 2, marginBottom: 16, background: C.card, borderRadius: 8, padding: 4, width: 'fit-content', border: `1px solid ${C.border}` }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        style={{ padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: tab === t.key ? 700 : 400,
                            background: tab === t.key ? C.green : 'transparent', color: tab === t.key ? '#fff' : C.textSub }}>
                        {t.label} {data && <span style={{ opacity: 0.7 }}>({(tab === t.key ? rows : (
                            t.key === 'admins' ? data.admins : t.key === 'customers' ? data.customers : t.key === 'contractors' ? data.contractors : data.vendors
                        )).length})</span>}
                    </button>
                ))}
            </div>

            {loading ? <Spinner /> : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: C.card, borderBottom: `1px solid ${C.border}` }}>
                                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: C.textSub }}>Name</th>
                                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: C.textSub }}>Email</th>
                                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: C.textSub }}>Phone</th>
                                    {tab === 'contractors' && <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: C.textSub }}>BG Check</th>}
                                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: C.textSub }}>Status</th>
                                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: C.textSub }}>Joined</th>
                                    <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: C.textSub }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr><td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: C.textMuted }}>No users found.</td></tr>
                                )}
                                {rows.map((u, i) => (
                                    <tr key={u.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? 'transparent' : C.card }}>
                                        <td style={{ padding: '10px 14px', fontWeight: 500 }}>{u.name}</td>
                                        <td style={{ padding: '10px 14px', color: C.textSub }}>{u.email}</td>
                                        <td style={{ padding: '10px 14px', color: C.textSub }}>{u.phone ?? '—'}</td>
                                        {tab === 'contractors' && <td style={{ padding: '10px 14px' }}><Badge label={u.status ?? 'Unknown'} /></td>}
                                        <td style={{ padding: '10px 14px' }}>
                                            <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                                                background: u.isActive ? C.greenBg : '#FEE2E2', color: u.isActive ? C.greenMid : '#DC2626' }}>
                                                {u.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 14px', color: C.textSub }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                                        <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                                            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                                <button onClick={() => startEdit(u)}
                                                    style={{ padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.border}`, background: 'none', cursor: 'pointer', fontSize: 11, color: C.textSub }}>
                                                    Edit
                                                </button>
                                                {tab !== 'contractors' && (
                                                    <button onClick={() => toggleActive(u, !u.isActive)}
                                                        style={{ padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.border}`, background: 'none', cursor: 'pointer', fontSize: 11, color: C.textSub }}>
                                                        {u.isActive ? 'Deactivate' : 'Activate'}
                                                    </button>
                                                )}
                                                <button onClick={() => resetPassword(u)}
                                                    style={{ padding: '3px 8px', borderRadius: 5, border: `1px solid ${C.border}`, background: 'none', cursor: 'pointer', fontSize: 11, color: C.textSub }}>
                                                    Reset PW
                                                </button>
                                                {(tab === 'customers' || tab === 'admins') && (
                                                    <button onClick={() => deleteUser(u)}
                                                        style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid #FCA5A5', background: 'none', cursor: 'pointer', fontSize: 11, color: '#DC2626' }}>
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    );
}

function SparkCard({ title, value, sub, data, color }: { title: string; value: string; sub?: string; data: { v: number }[]; color: string }) {
    return (
        <div style={{ background: C.card, borderRadius: 12, padding: '14px 16px', border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{title}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 8 }}>
                <div>
                    <div style={{ fontSize: 24, fontWeight: 800, color: C.text, lineHeight: 1 }}>{value}</div>
                    {sub && <div style={{ fontSize: 11, color: C.textSub, marginTop: 3 }}>{sub}</div>}
                </div>
                <div style={{ width: 80, height: 36, flexShrink: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data}>
                            <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

// ── Overview ──────────────────────────────────────────────────────────────────

function OverviewPanel({ stats, jobs, contractors, bgChecks, tierDist, expiringDocs, dumpsterOrders = [], loadingJobs = [], documents, estimates, estimateBuilderEnabled, preorders = [], onNavigate }: { stats: DashboardStats; jobs: JobListItem[]; contractors: ContractorListItem[]; bgChecks: ContractorBgCheckItem[]; tierDist: { apprentices: number; journeymen: number; masters: number } | null; expiringDocs: ContractorDocumentItem[]; dumpsterOrders?: any[]; loadingJobs?: any[]; documents: ContractorDocumentItem[]; estimates: EstimateBuilderDocDto[]; estimateBuilderEnabled: boolean; preorders?: ProductPreorderResponse[]; onNavigate: (v: View) => void }) {
    const branding = useBranding();
    const contractorLabelPlural = branding.contractorNoun ? branding.contractorNoun + 's' : 'Contractors';
    const [range, setRange] = useState<'today' | 'week' | 'month'>('week');
    const now = new Date();

    const allJobs = [
        ...jobs.map(j => ({ date: new Date(j.createdAt), revenue: j.totalPrice, type: j.serviceType, status: j.status })),
        ...dumpsterOrders.map((o: any) => ({ date: new Date(o.requestedDeliveryDate || o.createdAt || Date.now()), revenue: o.totalCost ?? 0, type: 'Dumpster', status: o.status ?? '' })),
        ...loadingJobs.map((j: any) => ({ date: new Date(j.loadingScheduledAt || j.createdAt || Date.now()), revenue: j.finalCost ?? 0, type: 'Loading', status: j.status ?? '' })),
    ];

    const periodMs = range === 'today' ? 86400000 : range === 'week' ? 7 * 86400000 : 30 * 86400000;
    const periodStart = new Date(now.getTime() - periodMs);
    const priorStart = new Date(periodStart.getTime() - periodMs);

    const periodJobs = allJobs.filter(j => j.date >= periodStart && j.date <= now);
    const priorJobs = allJobs.filter(j => j.date >= priorStart && j.date < periodStart);

    // Job counts intentionally include every status (booking volume), but revenue must only
    // count Completed jobs/orders — Cancelled ones keep their quoted price on the record (only
    // the tip is refunded), so summing them here would inflate the revenue KPI/chart.
    const isRevenueCounted = (status: string) => status === 'Completed';
    const curJobCount = periodJobs.length;
    const curRevenue = periodJobs.filter(j => isRevenueCounted(j.status)).reduce((s, j) => s + j.revenue, 0);
    const priorJobCount = priorJobs.length;
    const priorRevenue = priorJobs.filter(j => isRevenueCounted(j.status)).reduce((s, j) => s + j.revenue, 0);
    const pct = (cur: number, prior: number) => prior === 0 ? null : ((cur - prior) / prior) * 100;

    const considerCount = bgChecks.filter(b => b.backgroundCheckStatus === 'Consider').length;
    const disputedCount = jobs.filter(j => j.priceAdjustmentStatus === 'Disputed').length;
    const pendingDocCount = documents.filter(d =>
        d.driversLicenseStatus === 'Submitted' ||
        d.insuranceStatus === 'Submitted' ||
        d.vehicleStatus === 'Submitted'
    ).length;
    const requestedEstimateCount = estimates.filter(e => e.status === 'Requested' && !e.assignedContractorId).length;
    const approvedUnconvertedCount = estimates.filter(e => e.status === 'Approved' && !e.convertedJobId).length;
    const needsAssignmentCount = jobs.filter(j => j.status === 'Pending' && !j.contractorName).length;
    const pendingPreorderCount = preorders.filter(p => p.fulfillmentStatus === 'Pending').length;

    const taskItems: { key: string; label: string; count: number; view: View; icon: string }[] = [
        ...(estimateBuilderEnabled ? [{ key: 'estimates', label: 'New estimate requests', count: requestedEstimateCount, view: 'estimates' as View, icon: '🧾' }] : []),
        ...(estimateBuilderEnabled ? [{ key: 'estimates-convert', label: 'Approved estimates ready to convert', count: approvedUnconvertedCount, view: 'estimates' as View, icon: '➡️' }] : []),
        // Dispatch, not Jobs — the Jobs panel has no way to actually assign a Runner to a job.
        { key: 'needs-assignment', label: 'Jobs needing a Runner assigned', count: needsAssignmentCount, view: 'dispatch' as View, icon: '🚚' },
        { key: 'disputes', label: 'Price disputes to review', count: disputedCount, view: 'disputes' as View, icon: '⚠️' },
        { key: 'documents', label: 'Documents submitted for review', count: pendingDocCount, view: 'documents' as View, icon: '📋' },
        { key: 'bgchecks', label: 'Background checks needing a decision', count: considerCount, view: 'background-checks' as View, icon: '🛡️' },
        { key: 'expiring', label: 'Documents expiring soon', count: expiringDocs.length, view: 'documents' as View, icon: '⏳' },
        { key: 'preorders-fulfill', label: 'Preorders awaiting fulfillment', count: pendingPreorderCount, view: 'preorders' as View, icon: '📦' },
    ].filter(t => t.count > 0);
    const totalTasks = taskItems.reduce((s, t) => s + t.count, 0);

    // Daily chart buckets (7 days for today/week, 30 for month)
    const days = range === 'month' ? 30 : 7;
    const chartData = Array.from({ length: days }, (_, i) => {
        const dayEnd = new Date(now.getTime() - (days - 1 - i) * 86400000);
        const dayStart = new Date(dayEnd.getTime() - 86400000);
        const bucket = allJobs.filter(j => j.date >= dayStart && j.date < dayEnd);
        return {
            label: dayEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
            jobs: bucket.length,
            revenue: bucket.filter(j => isRevenueCounted(j.status)).reduce((s, j) => s + j.revenue, 0),
        };
    });

    // Service type donut (max 5 slices)
    const PIE_COLORS = ['#2a78d6', '#1baf7a', '#eda100', '#4a3aa7', '#e34948'];
    const stCounts = periodJobs.reduce((acc, j) => { acc[j.type] = (acc[j.type] || 0) + 1; return acc; }, {} as Record<string, number>);
    const stEntries = Object.entries(stCounts).sort(([, a], [, b]) => b - a);
    const pieData = stEntries.length === 0 ? [] :
        stEntries.length <= 5
            ? stEntries.map(([name, value]) => ({ name, value }))
            : [...stEntries.slice(0, 4).map(([name, value]) => ({ name, value })), { name: 'Other', value: stEntries.slice(4).reduce((s, [, v]) => s + v, 0) }];

    // 8-week sparkline bins
    const sparkWeeks = Array.from({ length: 8 }, (_, i) => {
        const wEnd = new Date(now.getTime() - i * 7 * 86400000);
        const wStart = new Date(now.getTime() - (i + 1) * 7 * 86400000);
        const wj = jobs.filter(j => { const d = new Date(j.createdAt); return d >= wStart && d < wEnd; });
        const wDone = wj.filter(j => j.status === 'Completed' && j.startedAt && j.completedAt);
        return {
            v: wj.length,
            completion: wj.length ? (wj.filter(j => j.status === 'Completed').length / wj.length) * 100 : 0,
            cancellation: wj.length ? (wj.filter(j => j.status === 'Cancelled').length / wj.length) * 100 : 0,
            avgDuration: wDone.length ? wDone.reduce((s, j) => s + (new Date(j.completedAt!).getTime() - new Date(j.startedAt!).getTime()) / 60000, 0) / wDone.length : 0,
        };
    }).reverse();

    const completedWithDuration = jobs.filter(j => j.status === 'Completed' && j.startedAt && j.completedAt);
    const avgDurationMins = completedWithDuration.length
        ? Math.round(completedWithDuration.reduce((s, j) => s + (new Date(j.completedAt!).getTime() - new Date(j.startedAt!).getTime()) / 60000, 0) / completedWithDuration.length)
        : 0;
    const avgDurationLabel = avgDurationMins === 0 ? '—' : avgDurationMins < 60 ? `${avgDurationMins}m` : `${Math.floor(avgDurationMins / 60)}h ${avgDurationMins % 60}m`;

    const totalCompleted = jobs.filter(j => j.status === 'Completed').length;
    const totalCancelled = jobs.filter(j => j.status === 'Cancelled').length;
    const completionRate = jobs.length ? Math.round((totalCompleted / jobs.length) * 100) : 0;
    const cancellationRate = jobs.length ? Math.round((totalCancelled / jobs.length) * 100) : 0;

    // Recent jobs list
    const recentAll = [
        ...jobs.map(j => ({ id: j.id, type: 'pickup', name: j.customerName, sub: `${j.serviceType} · ${j.pickupAddress.split(',')[0]}`, status: j.status, amount: j.totalPrice, date: new Date(j.createdAt) })),
        ...dumpsterOrders.map((o: any) => ({ id: o.id, type: 'dumpster', name: o.customerName || 'Customer', sub: `Dumpster ${o.dumpsterYards}yd · ${o.deliveryAddress?.split(',')[0]}`, status: o.status, amount: o.totalCost, date: new Date(o.requestedDeliveryDate) })),
        ...loadingJobs.filter((j: any) => j.contractorName).map((j: any) => ({ id: j.id, type: 'loading', name: j.contractorName, sub: `Loading · ${j.deliveryAddress?.split(',')[0]}`, status: j.status, amount: j.finalCost, date: new Date(j.loadingScheduledAt || j.createdAt || Date.now()) })),
    ].sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 6);

    const downloadCSV = () => {
        const rows = [['Date', 'Jobs', 'Revenue'], ...chartData.map(r => [r.label, r.jobs, r.revenue.toFixed(2)])];
        const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob); a.download = `dashboard-${range}.csv`; a.click();
    };

    return (
        <div>
            {/* Header + range toggle */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Dashboard Overview</h2>
                    <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>Updated {now.toLocaleTimeString()}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    {(['today', 'week', 'month'] as const).map(r => (
                        <button key={r} onClick={() => setRange(r)} style={{ padding: '5px 12px', borderRadius: 7, border: `1px solid ${range === r ? C.green : C.border}`, background: range === r ? C.greenBg : 'transparent', color: range === r ? C.green : C.textSub, fontWeight: range === r ? 700 : 400, fontSize: 12, cursor: 'pointer' }}>
                            {r === 'today' ? 'Today' : r === 'week' ? 'This Week' : 'This Month'}
                        </button>
                    ))}
                    <button onClick={downloadCSV} style={{ padding: '5px 12px', borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', color: C.textSub, fontSize: 12, cursor: 'pointer' }}>
                        ↓ CSV
                    </button>
                </div>
            </div>

            {/* Tasks — everything needing admin action, consolidated in one place instead of
                scattered across separate KPI cards/badges */}
            <Card style={{ marginBottom: 24, border: `1px solid ${totalTasks > 0 ? '#EF9F27' : C.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: taskItems.length > 0 ? 14 : 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Tasks</div>
                    {totalTasks > 0 && (
                        <span style={{ background: '#FDECC8', color: '#854F0B', borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                            {totalTasks} need{totalTasks === 1 ? 's' : ''} attention
                        </span>
                    )}
                </div>
                {taskItems.length === 0 ? (
                    <div style={{ fontSize: 13, color: C.textSub }}>All caught up — nothing needs your attention right now.</div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {taskItems.map(t => (
                            <button key={t.key} onClick={() => onNavigate(t.view)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'left', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', cursor: 'pointer' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 600, color: C.text }}>
                                    <span style={{ fontSize: 16 }}>{t.icon}</span>{t.label}
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <span style={{ background: C.coral, color: '#fff', borderRadius: 12, minWidth: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, padding: '0 6px' }}>
                                        {t.count}
                                    </span>
                                    <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>Review →</span>
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </Card>

            {/* KPI cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
                {[
                    { label: 'Jobs', value: String(curJobCount), trend: pct(curJobCount, priorJobCount) },
                    { label: 'Revenue', value: `$${curRevenue.toFixed(0)}`, trend: pct(curRevenue, priorRevenue) },
                    { label: `Active ${contractorLabelPlural}`, value: String(stats.activeContractors), sub: `${stats.onlineContractors} online`, trend: null },
                ].map(k => (
                    <div key={k.label} style={{ background: C.card, borderRadius: 12, padding: '16px 18px', border: `1px solid ${(k as any).warn ? '#EF9F27' : C.border}` }}>
                        <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{k.label}</div>
                        <div style={{ fontSize: 26, fontWeight: 800, color: C.text, lineHeight: 1 }}>{k.value}</div>
                        {k.trend !== null && k.trend !== undefined && (
                            <div style={{ fontSize: 12, marginTop: 5, color: k.trend >= 0 ? '#006300' : '#e34948', fontWeight: 600 }}>
                                {k.trend >= 0 ? '↑' : '↓'} {Math.abs(k.trend).toFixed(1)}%
                            </div>
                        )}
                        {(k as any).sub && <div style={{ fontSize: 11, color: C.textSub, marginTop: 4 }}>{(k as any).sub}</div>}
                    </div>
                ))}
            </div>

            {/* Charts row: two stacked area charts (not dual-axis) + donut */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, marginBottom: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <Card style={{ padding: '14px 16px 6px' }}>
                        <div style={{ fontSize: 11, color: C.textSub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Jobs</div>
                        <ResponsiveContainer width="100%" height={150}>
                            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="gJobs" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#1baf7a" stopOpacity={0.2} />
                                        <stop offset="95%" stopColor="#1baf7a" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#e1e0d9" strokeWidth={0.5} vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#898781' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                                <YAxis tick={{ fontSize: 10, fill: '#898781' }} tickLine={false} axisLine={false} allowDecimals={false} />
                                <Tooltip content={<ChartTooltip />} />
                                <Area type="monotone" dataKey="jobs" name="Jobs" stroke="#1baf7a" strokeWidth={2} fill="url(#gJobs)" dot={false} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Card>
                    <Card style={{ padding: '14px 16px 6px' }}>
                        <div style={{ fontSize: 11, color: C.textSub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Revenue ($)</div>
                        <ResponsiveContainer width="100%" height={150}>
                            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="gRevenue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#2a78d6" stopOpacity={0.2} />
                                        <stop offset="95%" stopColor="#2a78d6" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#e1e0d9" strokeWidth={0.5} vertical={false} />
                                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#898781' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                                <YAxis tick={{ fontSize: 10, fill: '#898781' }} tickLine={false} axisLine={false} tickFormatter={(v: number) => `$${v}`} />
                                <Tooltip content={<ChartTooltip prefix="$" />} />
                                <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#2a78d6" strokeWidth={2} fill="url(#gRevenue)" dot={false} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </Card>
                </div>

                {/* Donut: jobs by service type */}
                <Card style={{ padding: '14px 16px' }}>
                    <div style={{ fontSize: 11, color: C.textSub, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>By Service Type</div>
                    {pieData.length === 0 ? (
                        <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted, fontSize: 13 }}>No data</div>
                    ) : (
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie data={pieData} cx="50%" cy="44%" innerRadius={55} outerRadius={85} paddingAngle={2} dataKey="value" nameKey="name">
                                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                </Pie>
                                <Tooltip content={<ChartTooltip />} />
                                <Legend iconType="circle" iconSize={8} formatter={(v: string) => <span style={{ fontSize: 11, color: C.textSub }}>{v}</span>} />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                    {expiringDocs.length > 0 && (
                        <div style={{ marginTop: 8, padding: '7px 10px', background: '#FFF7E6', borderRadius: 7, fontSize: 11, color: '#854F0B', fontWeight: 600 }}>
                            ⚠ {expiringDocs.length} doc{expiringDocs.length > 1 ? 's' : ''} expiring — see Documents tab
                        </div>
                    )}
                    {bgChecks.filter(b => b.backgroundCheckStatus === 'Consider').length > 0 && (
                        <div style={{ marginTop: 6, padding: '7px 10px', background: '#FFF7E6', borderRadius: 7, fontSize: 11, color: '#854F0B', fontWeight: 600 }}>
                            ⚠ {bgChecks.filter(b => b.backgroundCheckStatus === 'Consider').length} bg check{bgChecks.filter(b => b.backgroundCheckStatus === 'Consider').length > 1 ? 's' : ''} need review
                        </div>
                    )}
                </Card>
            </div>

            {/* Sparklines */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
                <SparkCard title="Avg Rating" value={stats.averageRating.toFixed(1)} sub="across all jobs" data={sparkWeeks.map(w => ({ v: w.v }))} color="#1baf7a" />
                <SparkCard title="Completion Rate" value={`${completionRate}%`} sub="of all jobs" data={sparkWeeks.map(w => ({ v: w.completion }))} color="#2a78d6" />
                <SparkCard title="Cancellation Rate" value={`${cancellationRate}%`} sub="of all jobs" data={sparkWeeks.map(w => ({ v: w.cancellation }))} color="#e34948" />
                <SparkCard title="Avg Job Duration" value={avgDurationLabel} sub="completed jobs" data={sparkWeeks.map(w => ({ v: w.avgDuration }))} color="#eda100" />
            </div>

            {/* Recent jobs + top contractors */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <Card>
                    <h3 style={{ margin: '0 0 16px', fontSize: 13, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>Recent jobs</h3>
                    {recentAll.map(j => (
                        <div key={`${j.type}-${j.id}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${C.borderLight}` }}>
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 600 }}>{j.name}</div>
                                <div style={{ fontSize: 11, color: C.textMuted }}>{j.sub}</div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <Badge label={j.status} />
                                <div style={{ fontSize: 12, color: C.textSub, marginTop: 4 }}>{j.amount != null ? `$${j.amount.toFixed(2)}` : '—'}</div>
                            </div>
                        </div>
                    ))}
                </Card>
                <Card>
                    <h3 style={{ margin: '0 0 16px', fontSize: 13, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>Top {contractorLabelPlural.toLowerCase()}</h3>
                    {contractors.slice(0, 6).map(c => (
                        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${C.borderLight}` }}>
                            <div style={{ width: 34, height: 34, borderRadius: '50%', background: C.greenBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: C.green, fontSize: 12, flexShrink: 0 }}>
                                {c.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                                <div style={{ fontSize: 11, color: C.textMuted }}>{c.totalJobsCompleted} jobs · ★ {c.averageRating.toFixed(1)}</div>
                            </div>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: c.isOnline ? C.greenMid : C.border }} />
                        </div>
                    ))}
                </Card>
            </div>
        </div>
    );
}

// ── Jobs panel ────────────────────────────────────────────────────────────────

function JobsPanel({ jobs, dumpsterOrders = [], loadingJobs = [], onRefresh, onRouteToEstimate }: { jobs: JobListItem[]; dumpsterOrders?: any[]; loadingJobs?: any[]; onRefresh?: () => void; onRouteToEstimate?: (customer: { name: string; email?: string; phone?: string }) => void }) {
    const branding = useBranding();
    const svcLabel = branding.serviceNoun ?? 'job';
    const svcLabelPlural = branding.serviceNoun ? branding.serviceNoun + 's' : 'Pickups';
    const svcLabelPluralCap = svcLabelPlural.charAt(0).toUpperCase() + svcLabelPlural.slice(1);
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const contractorLabelPlural = branding.contractorNoun ? branding.contractorNoun + 's' : 'Contractors';
    const dumpsterEnabled = useFeature('dumpsterRental');
    const loadingEnabled = useFeature('loadingJobs');
    const [showBookModal, setShowBookModal] = useState(false);
    const [filter, setFilter] = useState('All');
    const [contractorFilter, setContractorFilter] = useState('All');
    const [tab, setTab] = useState<'pickups' | 'dumpster' | 'loading'>('pickups');
    const statuses = ['All', 'Pending', 'Accepted', 'EnRoute', 'Arrived', 'InProgress', 'Completed', 'Cancelled'];
    const dumpsterStatuses = ['All', 'Pending', 'VendorAccepted', 'Delivered', 'LoadingInProgress', 'PickupRequested', 'Completed', 'Cancelled'];
    const loadingStatuses = ['All', 'Open', 'Accepted', 'EnRoute', 'OnSite', 'InProgress', 'PendingApproval', 'Completed', 'Cancelled'];
    const currentStatuses = tab === 'pickups' ? statuses : tab === 'dumpster' ? dumpsterStatuses : loadingStatuses;
    const contractorNames = Array.from(new Set(jobs.map(j => j.contractorName).filter(Boolean) as string[])).sort();
    // CSV export intentionally still pulls from the full unpaginated jobs prop — it's a
    // deliberate bulk action, not something that should silently only cover the visible page.
    const filteredForExport = jobs
        .filter(j => filter === 'All' || j.status === filter)
        .filter(j => contractorFilter === 'All' || j.contractorName === contractorFilter);
    const filteredDumpster = filter === 'All' ? dumpsterOrders : dumpsterOrders.filter((o: any) => o.status === filter);
    const filteredLoading = filter === 'All' ? loadingJobs : loadingJobs.filter((j: any) => j.status === filter);

    // The pickups table itself no longer renders every job the tenant has ever had — it fetches
    // one page at a time from the server, filtered server-side by status/contractor.
    const PAGE_SIZE = 25;
    const [page, setPage] = useState(1);
    const [pagedItems, setPagedItems] = useState<JobListItem[]>([]);
    const [pagedTotal, setPagedTotal] = useState(0);
    const [pagedLoading, setPagedLoading] = useState(true);
    const [historyJob, setHistoryJob] = useState<JobListItem | null>(null);

    useEffect(() => { setPage(1); }, [filter, contractorFilter, tab]);

    const loadPage = useCallback(() => {
        if (tab !== 'pickups') return;
        setPagedLoading(true);
        api.getJobsPaged(page, PAGE_SIZE, filter, contractorFilter)
            .then(res => { setPagedItems(res.items); setPagedTotal(res.totalCount); })
            .catch(() => { setPagedItems([]); setPagedTotal(0); })
            .finally(() => setPagedLoading(false));
    }, [tab, page, filter, contractorFilter]);

    useEffect(() => { loadPage(); }, [loadPage]);

    const totalPages = Math.max(1, Math.ceil(pagedTotal / PAGE_SIZE));

    const exportJobsCSV = () => {
        const headers = ['Date', 'Customer', 'Service Type', 'Address', contractorLabel, 'Total', 'Status', 'Duration'];
        const rows = filteredForExport.map(j => [
            new Date(j.createdAt).toLocaleDateString(),
            `"${csvFormulaSafe(j.customerName).replace(/"/g, '""')}"`,
            j.serviceType,
            `"${csvFormulaSafe(j.pickupAddress).replace(/"/g, '""')}"`,
            j.contractorName ? `"${csvFormulaSafe(j.contractorName).replace(/"/g, '""')}"` : '',
            j.totalPrice.toFixed(2),
            j.status,
            fmtJobDuration(j.startedAt, j.completedAt),
        ]);
        const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `jobs-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
    };

    const tabs = [
        { key: 'pickups' as const, label: `📦 ${svcLabelPluralCap} (${jobs.length})` },
        ...(dumpsterEnabled ? [{ key: 'dumpster' as const, label: `🚛 Dumpster (${dumpsterOrders.length})` }] : []),
        ...(loadingEnabled ? [{ key: 'loading' as const, label: `💪 Loading Jobs (${loadingJobs.length})` }] : []),
    ];

    return (
        <div>
            {showBookModal && (
                <AdminBookJobModal
                    svcLabel={svcLabel}
                    onClose={() => setShowBookModal(false)}
                    onCreated={() => { onRefresh?.(); setPage(1); loadPage(); }}
                    onRouteToEstimate={onRouteToEstimate ? (customer) => { setShowBookModal(false); onRouteToEstimate(customer); } : undefined}
                />
            )}
            {historyJob && <JobHistoryModal job={historyJob} contractorLabel={contractorLabel} onClose={() => setHistoryJob(null)} />}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => { setTab(t.key); setFilter('All'); setContractorFilter('All'); }}
                        style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${tab === t.key ? C.greenMid : C.border}`, background: tab === t.key ? C.greenBg : '#fff', color: tab === t.key ? C.green : C.textSub, fontSize: 13, fontWeight: tab === t.key ? 700 : 400, cursor: 'pointer' }}>
                        {t.label}
                    </button>
                ))}
                {tab === 'pickups' && (
                    <button
                        onClick={() => setShowBookModal(true)}
                        style={{ marginLeft: 'auto', padding: '8px 16px', borderRadius: 8, border: 'none', background: C.green, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                    >
                        📞 Book for Customer
                    </button>
                )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                {currentStatuses.map(s => (
                    <button key={s} onClick={() => setFilter(s)} style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${filter === s ? C.greenMid : C.border}`, background: filter === s ? C.greenBg : '#fff', color: filter === s ? C.green : C.textSub, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>{s}</button>
                ))}
            </div>
            {tab === 'pickups' && (
                <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
                    <select
                        value={contractorFilter}
                        onChange={e => setContractorFilter(e.target.value)}
                        style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, color: C.text, background: '#fff', cursor: 'pointer', minWidth: 180 }}
                    >
                        <option value="All">All {contractorLabelPlural.toLowerCase()}</option>
                        {contractorNames.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <button
                        onClick={exportJobsCSV}
                        style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#fff', color: C.textSub, fontSize: 12, cursor: 'pointer', marginLeft: 'auto' }}
                    >
                        ↓ Export CSV
                    </button>
                </div>
            )}

            {tab === 'pickups' && (
                <>
                    <Card style={{ padding: 0, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead><tr style={{ background: C.bg }}>{['Customer', 'Service', 'Address', contractorLabel, 'Total', 'Status', 'Date', 'Duration'].map(h => (<th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>))}</tr></thead>
                            <tbody>{pagedItems.map((j, i) => (<tr key={j.id} onClick={() => setHistoryJob(j)} style={{ background: i % 2 === 0 ? '#fff' : C.bg, cursor: 'pointer' }}><td style={{ padding: '12px 16px', fontWeight: 500 }}>{j.customerName}</td><td style={{ padding: '12px 16px' }}><Badge label={j.serviceType} /></td><td style={{ padding: '12px 16px', color: C.textSub, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.pickupAddress}</td><td style={{ padding: '12px 16px', color: C.textSub }}>{j.contractorName || '—'}</td><td style={{ padding: '12px 16px', fontWeight: 600, color: C.green }}>${j.totalPrice.toFixed(2)}</td><td style={{ padding: '12px 16px' }}><Badge label={j.status} /></td><td style={{ padding: '12px 16px', color: C.textMuted }}>{new Date(j.createdAt).toLocaleDateString()}</td><td style={{ padding: '12px 16px', color: C.textSub, fontVariantNumeric: 'tabular-nums' }}>{fmtJobDuration(j.startedAt, j.completedAt)}</td></tr>))}</tbody>
                        </table>
                        {pagedLoading ? <div style={{ padding: 32 }}><Spinner /></div> : pagedItems.length === 0 && (
                            <div style={{ textAlign: 'center', padding: 32, color: C.textMuted }}>No {svcLabelPlural.toLowerCase()} match this filter.</div>
                        )}
                        {pagedTotal > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: `1px solid ${C.border}` }}>
                                <div style={{ fontSize: 12, color: C.textMuted }}>
                                    {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, pagedTotal)} of {pagedTotal}
                                </div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                                        style={{ padding: '4px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#fff', color: page <= 1 ? C.textMuted : C.text, fontSize: 12, cursor: page <= 1 ? 'default' : 'pointer' }}>
                                        ← Prev
                                    </button>
                                    <span style={{ fontSize: 12, color: C.textSub }}>Page {page} of {totalPages}</span>
                                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                                        style={{ padding: '4px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: '#fff', color: page >= totalPages ? C.textMuted : C.text, fontSize: 12, cursor: page >= totalPages ? 'default' : 'pointer' }}>
                                        Next →
                                    </button>
                                </div>
                            </div>
                        )}
                    </Card>
                    <div style={{ marginTop: 24 }}><AIAccuracyWidget jobs={jobs} /></div>
                </>
            )}

            {tab === 'dumpster' && (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead><tr style={{ background: C.bg }}>{['Customer', 'Vendor', 'Size', 'Address', 'Rental', 'Total', 'Status', 'Date'].map(h => (<th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>))}</tr></thead>
                        <tbody>{filteredDumpster.map((o: any, i: number) => (<tr key={o.id} style={{ background: i % 2 === 0 ? '#fff' : C.bg }}><td style={{ padding: '12px 16px', fontWeight: 500 }}>{o.customerName || '—'}</td><td style={{ padding: '12px 16px', color: C.textSub }}>{o.vendorName || '—'}</td><td style={{ padding: '12px 16px' }}><Badge label={`${o.dumpsterYards}yd`} /></td><td style={{ padding: '12px 16px', color: C.textSub, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.deliveryAddress}</td><td style={{ padding: '12px 16px', color: C.textSub }}>{o.rentalDays}d</td><td style={{ padding: '12px 16px', fontWeight: 600, color: C.green }}>${o.totalCost?.toFixed(2)}</td><td style={{ padding: '12px 16px' }}><Badge label={o.status} /></td><td style={{ padding: '12px 16px', color: C.textMuted }}>{new Date(o.requestedDeliveryDate).toLocaleDateString()}</td></tr>))}</tbody>
                    </table>
                    {filteredDumpster.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: C.textMuted }}>No dumpster orders match this filter.</div>}
                </Card>
            )}

            {tab === 'loading' && (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead><tr style={{ background: C.bg }}>{[contractorLabel, 'Address', 'Scheduled', 'Clocked In', 'Hours', 'Earned', 'Status'].map(h => (<th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>))}</tr></thead>
                        <tbody>{filteredLoading.map((j: any, i: number) => (<tr key={j.id} style={{ background: i % 2 === 0 ? '#fff' : C.bg }}><td style={{ padding: '12px 16px', fontWeight: 500 }}>{j.contractorName || '⏳ Open'}</td><td style={{ padding: '12px 16px', color: C.textSub, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.deliveryAddress}</td><td style={{ padding: '12px 16px', color: C.textSub }}>{j.loadingScheduledAt ? new Date(j.loadingScheduledAt).toLocaleDateString() : '—'}</td><td style={{ padding: '12px 16px', color: C.textSub }}>{j.clockedInAt ? new Date(j.clockedInAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</td><td style={{ padding: '12px 16px', color: C.textSub }}>{j.hoursWorked?.toFixed(2) || '—'}</td><td style={{ padding: '12px 16px', fontWeight: 600, color: C.green }}>{j.finalCost ? `$${j.finalCost.toFixed(2)}` : '—'}</td><td style={{ padding: '12px 16px' }}><Badge label={j.status} /></td></tr>))}</tbody>
                    </table>
                    {filteredLoading.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: C.textMuted }}>No loading jobs match this filter.</div>}
                </Card>
            )}
        </div>
    );
}

// ── Job history modal (summary + read-only chat) — reached by clicking a row in the Jobs
// panel table, same chat-thread rendering as the Disputes panel's audit view. ─────────────

function JobHistoryModal({ job, contractorLabel, onClose }: { job: JobListItem; contractorLabel: string; onClose: () => void }) {
    const [messages, setMessages] = useState<MessageItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.getMessages(job.id).then(setMessages).catch(() => setMessages([])).finally(() => setLoading(false));
    }, [job.id]);

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
            <div style={{ width: 520, maxWidth: '92vw', maxHeight: '86vh', overflowY: 'auto', background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>Job history</div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: C.textMuted }}>×</button>
                </div>
                <div style={{ fontSize: 13, color: C.textSub, marginBottom: 16 }}>{job.pickupAddress}</div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    <Badge label={job.serviceType} />
                    <Badge label={job.status} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20, fontSize: 13 }}>
                    <div><div style={{ color: C.textMuted, fontSize: 11, textTransform: 'uppercase' }}>Customer</div><div style={{ fontWeight: 600 }}>{job.customerName}</div></div>
                    <div><div style={{ color: C.textMuted, fontSize: 11, textTransform: 'uppercase' }}>{contractorLabel}</div><div style={{ fontWeight: 600 }}>{job.contractorName || '—'}</div></div>
                    <div><div style={{ color: C.textMuted, fontSize: 11, textTransform: 'uppercase' }}>Total</div><div style={{ fontWeight: 600, color: C.green }}>${job.totalPrice.toFixed(2)}</div></div>
                    <div><div style={{ color: C.textMuted, fontSize: 11, textTransform: 'uppercase' }}>Booked</div><div style={{ fontWeight: 600 }}>{new Date(job.createdAt).toLocaleString()}</div></div>
                </div>

                <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Chat history</div>
                {loading ? <Spinner /> : messages.length === 0 ? (
                    <div style={{ fontSize: 13, color: C.textMuted, fontStyle: 'italic' }}>No messages were exchanged on this job.</div>
                ) : (
                    <div style={{ background: C.bg, borderRadius: 8, padding: 12, maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {messages.map(m => (
                            <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: m.senderRole === 'Customer' ? 'flex-start' : 'flex-end' }}>
                                <div style={{
                                    maxWidth: '80%', borderRadius: 10, padding: '8px 12px', fontSize: 13,
                                    background: m.senderRole === 'Customer' ? '#fff' : C.greenBg,
                                    border: `1px solid ${C.border}`,
                                }}>
                                    {m.text}
                                </div>
                                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>
                                    {m.senderRole === 'Customer' ? 'Customer' : contractorLabel} · {new Date(m.sentAt).toLocaleString()}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Book job on behalf of a customer (TREK-131 follow-up, CSR/phone bookings) ──

function AdminBookJobModal({ svcLabel, onClose, onCreated, onRouteToEstimate }: {
    svcLabel: string; onClose: () => void; onCreated: () => void;
    onRouteToEstimate?: (customer: { name: string; email?: string; phone?: string }) => void;
}) {
    const [step, setStep] = useState<'customer' | 'job' | 'payment'>('customer');
    const [createdJobId, setCreatedJobId] = useState<string | null>(null);
    const [paymentClientSecret, setPaymentClientSecret] = useState('');
    const [search, setSearch] = useState('');
    const [searching, setSearching] = useState(false);
    const [results, setResults] = useState<UserManagementItem[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<UserManagementItem | null>(null);
    const [showNewCustomer, setShowNewCustomer] = useState(false);
    const [newName, setNewName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newPhone, setNewPhone] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const [serviceTypes, setServiceTypes] = useState<{ value: string; label: string; unitLabel: string; requiresDonationCenter: boolean }[]>([]);
    const [serviceType, setServiceType] = useState('');
    const [donationCenters, setDonationCenters] = useState<DonationCenterDto[]>([]);
    const [donationCenterId, setDonationCenterId] = useState('');
    const [address, setAddress] = useState('');
    const [pickupLat, setPickupLat] = useState(0);
    const [pickupLng, setPickupLng] = useState(0);
    const [pickupCity, setPickupCity] = useState<string | undefined>();
    const [pickupState, setPickupState] = useState<string | undefined>();
    const [pickupCounty, setPickupCounty] = useState<string | undefined>();
    const [pickupPostalCode, setPickupPostalCode] = useState<string | undefined>();
    const [pickupCountry, setPickupCountry] = useState<string | undefined>();
    const [units, setUnits] = useState('1');
    const [timeSlot, setTimeSlot] = useState('');
    const [tipAmount, setTipAmount] = useState('0');
    const [quote, setQuote] = useState<QuoteResponse | null>(null);

    useEffect(() => {
        api.getServiceTypes().then(types => { setServiceTypes(types); if (types[0]) setServiceType(types[0].value); }).catch(() => {});
    }, []);

    const selectedServiceType = serviceTypes.find(t => t.value === serviceType);
    const donationCenterRequired = selectedServiceType?.requiresDonationCenter ?? false;

    useEffect(() => {
        if (!donationCenterRequired) { setDonationCenterId(''); return; }
        api.getDonationCenters().then(setDonationCenters).catch(() => setDonationCenters([]));
    }, [donationCenterRequired]);

    useEffect(() => {
        if (step !== 'job' || !serviceType) { setQuote(null); return; }
        const u = parseInt(units);
        if (isNaN(u) || u <= 0) { setQuote(null); return; }
        // Without this guard, quickly changing service type/units fires overlapping requests —
        // if an earlier (slower) one resolves after a newer one, its now-stale response silently
        // overwrites the correct quote, showing a price that doesn't match the current selection.
        let cancelled = false;
        api.getQuote(serviceType, u, { pickupCity, pickupState, pickupCounty, pickupPostalCode, pickupCountry })
            .then(q => { if (!cancelled) setQuote(q); })
            .catch(() => { if (!cancelled) setQuote(null); });
        return () => { cancelled = true; };
    }, [serviceType, units, step, pickupCity, pickupState, pickupCounty, pickupPostalCode, pickupCountry]);

    const runSearch = async () => {
        setSearching(true); setError('');
        try {
            const res = await api.adminGetAllUsers(search);
            setResults(res.customers);
        } catch (e: any) { setError(e.message); }
        finally { setSearching(false); }
    };

    const createCustomer = async () => {
        setError('');
        if (!newName.trim() || !newEmail.trim() || !newPassword) { setError('Name, email, and a temporary password are required.'); return; }
        setSaving(true);
        try {
            const res = await api.adminCreateUser({ name: newName.trim(), email: newEmail.trim(), password: newPassword, role: 'Customer', phoneNumber: newPhone.trim() || undefined });
            setSelectedCustomer({ id: res.id, name: res.name, email: res.email, role: 'Customer', isActive: true, createdAt: new Date().toISOString() });
            setStep('job');
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const submitJob = async () => {
        if (!selectedCustomer) return;
        if (!address.trim()) { setError(`${svcLabel[0].toUpperCase()}${svcLabel.slice(1)} address is required.`); return; }
        const u = parseInt(units);
        if (isNaN(u) || u <= 0) { setError('Enter a valid unit count.'); return; }
        if (donationCenterRequired && !donationCenterId) { setError('Please select a donation center.'); return; }
        setError(''); setSaving(true);
        try {
            const job = await api.adminCreateJob(selectedCustomer.id, {
                serviceType, pickupAddress: address.trim(), pickupLat, pickupLng,
                estimatedUnits: u, scheduledTimeSlot: timeSlot || undefined, tipAmount: parseFloat(tipAmount) || 0,
                pickupCity, pickupState, pickupCounty, pickupPostalCode, pickupCountry,
                donationCenterId: donationCenterId || undefined,
            });
            // Without collecting payment here the job sits at PaymentStatus.Pending forever and
            // never shows up as an available job for a contractor to accept.
            setCreatedJobId(job.id);
            const intent = await api.adminCreateJobPaymentIntent(job.id);
            setPaymentClientSecret(intent.clientSecret);
            setStep('payment');
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
            <div style={{ width: 440, maxWidth: '92vw', maxHeight: '86vh', overflowY: 'auto', background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }} onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>📞 Book for Customer</div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: C.textMuted }}>×</button>
                </div>
                <Alert message={error} />

                {step === 'customer' && (
                    <div>
                        {selectedCustomer ? (
                            <div style={{ background: C.greenBg, borderRadius: 8, padding: 12, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 13 }}>{selectedCustomer.name}</div>
                                    <div style={{ fontSize: 12, color: C.textSub }}>{selectedCustomer.email}</div>
                                </div>
                                <button onClick={() => setSelectedCustomer(null)} style={{ background: 'none', border: 'none', color: C.textSub, cursor: 'pointer', fontSize: 12 }}>Change</button>
                            </div>
                        ) : !showNewCustomer ? (
                            <>
                                <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-end' }}>
                                    <div style={{ flex: 1 }}>
                                        <Input label="Search customers" value={search} onChange={setSearch} placeholder="Name, email, or phone" />
                                    </div>
                                    <Btn size="sm" onClick={runSearch} disabled={searching} style={{ marginBottom: 14 }}>{searching ? '...' : 'Search'}</Btn>
                                </div>
                                <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 10 }}>
                                    {results.map(c => (
                                        <div key={c.id} onClick={() => setSelectedCustomer(c)} style={{ padding: '8px 10px', borderRadius: 6, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', border: `1px solid ${C.borderLight}`, marginBottom: 6 }}>
                                            <span style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</span>
                                            <span style={{ fontSize: 12, color: C.textSub }}>{c.email}</span>
                                        </div>
                                    ))}
                                    {results.length === 0 && !searching && <div style={{ fontSize: 12, color: C.textMuted, padding: '8px 0' }}>Search to find an existing customer.</div>}
                                </div>
                                <Btn variant="outline" fullWidth onClick={() => setShowNewCustomer(true)}>+ New Customer</Btn>
                            </>
                        ) : (
                            <div>
                                <Input label="Name" value={newName} onChange={setNewName} />
                                <Input label="Email" value={newEmail} onChange={setNewEmail} type="email" />
                                <Input label="Phone" value={newPhone} onChange={setNewPhone} />
                                <Input label="Temporary password" value={newPassword} onChange={setNewPassword} placeholder="Customer can reset later" />
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Btn variant="outline" onClick={() => setShowNewCustomer(false)}>Back</Btn>
                                    <Btn onClick={createCustomer} disabled={saving} fullWidth>{saving ? 'Creating...' : 'Create & Continue'}</Btn>
                                </div>
                            </div>
                        )}
                        {selectedCustomer && (
                            <>
                                <Btn fullWidth onClick={() => setStep('job')} style={{ marginTop: 4 }}>Continue →</Btn>
                                {onRouteToEstimate && (
                                    <button
                                        onClick={() => onRouteToEstimate({ name: selectedCustomer.name, email: selectedCustomer.email, phone: selectedCustomer.phone })}
                                        style={{ width: '100%', marginTop: 8, background: 'none', border: 'none', color: C.textSub, fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
                                    >
                                        This is an estimate instead →
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                )}

                {step === 'job' && selectedCustomer && (
                    <div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>Service type</label>
                            <select value={serviceType} onChange={e => setServiceType(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}>
                                {serviceTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                        {donationCenterRequired && (
                            <div style={{ marginBottom: 14 }}>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>Donation center</label>
                                {donationCenters.length === 0 ? (
                                    <div style={{ fontSize: 12, color: C.textSub }}>No donation centers are set up yet.</div>
                                ) : (
                                    <select value={donationCenterId} onChange={e => setDonationCenterId(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14 }}>
                                        <option value="">— Select a donation center —</option>
                                        {donationCenters.map(dc => <option key={dc.id} value={dc.id}>{dc.name} — {dc.address}</option>)}
                                    </select>
                                )}
                            </div>
                        )}
                        <AddressAutocomplete
                            label={`${svcLabel[0].toUpperCase()}${svcLabel.slice(1)} address`}
                            value={address}
                            onChange={r => {
                                setAddress(r.address); setPickupLat(r.lat); setPickupLng(r.lng);
                                setPickupCity(r.city); setPickupState(r.state); setPickupCounty(r.county); setPickupPostalCode(r.postalCode); setPickupCountry(r.country);
                            }}
                            placeholder="123 Main St, City, ST"
                            required
                        />
                        <Input label={serviceTypes.find(t => t.value === serviceType)?.unitLabel || 'Units'} value={units} onChange={setUnits} type="number" />
                        <DateTimePicker label={`Preferred ${svcLabel} time (optional)`} value={timeSlot} onChange={setTimeSlot} />
                        <Input label="Tip (optional)" value={tipAmount} onChange={setTipAmount} type="number" />
                        {quote && (
                            <div style={{ background: C.bg, borderRadius: 8, padding: 10, marginBottom: 14, fontSize: 13 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: C.textSub }}>Base</span>
                                    <span>${quote.total.toFixed(2)}</span>
                                </div>
                                {quote.serviceFeeAmount > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: C.textSub }}>{quote.serviceFeeLabel}</span>
                                        <span>${quote.serviceFeeAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                {quote.taxAmount > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: C.textSub }}>Tax</span>
                                        <span>${quote.taxAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, paddingTop: 4, borderTop: `1px solid ${C.borderLight}` }}>
                                    <span style={{ color: C.textSub }}>Estimated total</span>
                                    <span style={{ fontWeight: 700 }}>${quote.grandTotal.toFixed(2)}</span>
                                </div>
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: 8 }}>
                            <Btn variant="outline" onClick={() => setStep('customer')}>Back</Btn>
                            <Btn onClick={submitJob} disabled={saving} fullWidth>{saving ? 'Booking...' : 'Continue to payment ->'}</Btn>
                        </div>
                    </div>
                )}

                {step === 'payment' && createdJobId && paymentClientSecret && (
                    <PaymentForm
                        jobId={createdJobId}
                        amount={(quote?.grandTotal ?? 0) + (parseFloat(tipAmount) || 0)}
                        clientSecret={paymentClientSecret}
                        onSuccess={async () => {
                            try { await api.adminConfirmJobAuthorization(createdJobId); } catch { }
                            onCreated();
                            onClose();
                        }}
                        onCancel={() => {
                            // Job already exists (unpaid, Pending) — admin can still dispatch it
                            // manually later. Just close; nothing further to undo here.
                            onCreated();
                            onClose();
                        }}
                    />
                )}
            </div>
        </div>
    );
}

// ── Contractors panel ─────────────────────────────────────────────────────────

const SERVICE_TYPES = ['Cardboard', 'Furniture', 'YardDebris', 'General', 'LoadRunner'];

function formatRateLabel(rateType?: string, rateAmount?: number | null): string {
    if (rateType === 'PerJob' && rateAmount != null) return `$${rateAmount.toFixed(2)}/job`;
    if (rateType === 'PerHour' && rateAmount != null) return `$${rateAmount.toFixed(2)}/hr`;
    if (rateAmount != null) return `${rateAmount}/${100 - rateAmount} split`;
    return '80/20 split';
}

const RATE_OPTIONS = [
    { value: 'PercentageSplit', label: '% Split' },
    { value: 'PerJob', label: 'Per Job ($)' },
    { value: 'PerHour', label: 'Per Hour ($)' },
];

function RateEditor({ id, initialType, initialAmount, onSave, onCancel, label }: {
    id: string; initialType: string; initialAmount: string;
    onSave: (rateType: string, amount: number) => Promise<void>;
    onCancel: () => void; label: string;
}) {
    const branding = useBranding();
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const [rateType, setRateType] = React.useState(initialType);
    const [rateAmount, setRateAmount] = React.useState(initialAmount);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');

    const save = async () => {
        setSaving(true); setError('');
        try {
            const amount = parseFloat(rateAmount);
            if (isNaN(amount) || amount <= 0) { setError('Enter a valid amount.'); return; }
            if (rateType === 'PercentageSplit' && (amount <= 0 || amount >= 100)) { setError('Split must be between 1 and 99.'); return; }
            await onSave(rateType, amount);
        } catch (e: any) {
            setError(e.message ?? 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ marginTop: 8, padding: 12, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: C.textSub }}>{label}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                {RATE_OPTIONS.map(opt => (
                    <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12 }}>
                        <input type="radio" name={`rate-${id}`} value={opt.value} checked={rateType === opt.value}
                            onChange={() => { setRateType(opt.value); setRateAmount(opt.value === 'PercentageSplit' ? '80' : ''); }} />
                        {opt.label}
                    </label>
                ))}
            </div>
            <input
                type="number" min="0"
                step={rateType === 'PercentageSplit' ? '1' : '0.01'}
                max={rateType === 'PercentageSplit' ? '99' : undefined}
                placeholder={rateType === 'PercentageSplit' ? `${contractorLabel} % (e.g. 80)` : rateType === 'PerJob' ? 'Amount per job' : 'Amount per hour'}
                value={rateAmount} onChange={e => setRateAmount(e.target.value)}
                style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, marginBottom: 6, boxSizing: 'border-box' as const }}
            />
            {rateType === 'PercentageSplit' && rateAmount && (
                <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6 }}>
                    Contractor gets {rateAmount}%, platform keeps {100 - parseFloat(rateAmount || '0')}%
                </div>
            )}
            {error && <div style={{ fontSize: 12, color: '#DC2626', marginBottom: 6 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={save} disabled={saving}
                    style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: C.green, color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
                    {saving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={onCancel}
                    style={{ padding: '5px 12px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', fontWeight: 600, fontSize: 12, cursor: 'pointer', color: C.textSub }}>
                    Cancel
                </button>
            </div>
        </div>
    );
}

function ContractorCard({ c, serviceTypes = SERVICE_TYPES, onRateUpdated }: { c: ContractorListItem; serviceTypes?: string[]; onRateUpdated?: (id: string, rateType: string, rateAmount: number | null) => void }) {
    const certTrackingEnabled = useFeature('contractorCertTracking');
    const [showGlobalEditor, setShowGlobalEditor] = React.useState(false);
    const [serviceRates, setServiceRates] = React.useState(c.serviceRates ?? []);
    const [showAddService, setShowAddService] = React.useState(false);
    const [newSvcType, setNewSvcType] = React.useState(serviceTypes[0] ?? '');
    const [skills, setSkills] = React.useState(c.skills ?? []);
    const [newSkill, setNewSkill] = React.useState('');
    const [skillError, setSkillError] = React.useState('');
    const [hasPayoutAccount, setHasPayoutAccount] = React.useState(c.hasPayoutAccount ?? false);
    const [payoutOnboardingComplete, setPayoutOnboardingComplete] = React.useState(c.payoutOnboardingComplete ?? false);
    const [resettingPayout, setResettingPayout] = React.useState(false);
    const [payoutResetError, setPayoutResetError] = React.useState('');

    // Opt-in territory (Contractor.ServiceArea* in Models.cs) — null/unset means this contractor
    // sees every job tenant-wide exactly as before; only a configured radius restricts them.
    const [showServiceAreaEditor, setShowServiceAreaEditor] = React.useState(false);
    const [serviceArea, setServiceArea] = React.useState({
        address: c.serviceAreaAddress ?? '', lat: c.serviceAreaLat != null ? String(c.serviceAreaLat) : '',
        lng: c.serviceAreaLng != null ? String(c.serviceAreaLng) : '',
        radiusMiles: c.serviceAreaRadiusMiles != null ? String(c.serviceAreaRadiusMiles) : '',
    });
    const [savingServiceArea, setSavingServiceArea] = React.useState(false);
    const [serviceAreaError, setServiceAreaError] = React.useState('');

    const saveServiceArea = async () => {
        setServiceAreaError('');
        const radius = parseFloat(serviceArea.radiusMiles);
        if (!serviceArea.address.trim() || isNaN(radius) || radius <= 0) {
            setServiceAreaError('Enter an address and a radius greater than 0.');
            return;
        }
        setSavingServiceArea(true);
        try {
            await api.adminSetContractorServiceArea(c.id, {
                serviceAreaAddress: serviceArea.address, serviceAreaLat: parseFloat(serviceArea.lat),
                serviceAreaLng: parseFloat(serviceArea.lng), serviceAreaRadiusMiles: radius,
            });
            setShowServiceAreaEditor(false);
        } catch (e: any) { setServiceAreaError(e.message); }
        finally { setSavingServiceArea(false); }
    };

    const clearServiceArea = async () => {
        setSavingServiceArea(true);
        setServiceAreaError('');
        try {
            await api.adminSetContractorServiceArea(c.id, {
                serviceAreaAddress: null, serviceAreaLat: null, serviceAreaLng: null, serviceAreaRadiusMiles: null,
            });
            setServiceArea({ address: '', lat: '', lng: '', radiusMiles: '' });
            setShowServiceAreaEditor(false);
        } catch (e: any) { setServiceAreaError(e.message); }
        finally { setSavingServiceArea(false); }
    };

    const resetPayoutAccount = async () => {
        if (!window.confirm(`Reset ${c.name}'s payout account? This unlinks their current Stripe bank account (and deletes it in Stripe if possible) so they'll need to reconnect a new one before their next payout.`)) return;
        setResettingPayout(true);
        setPayoutResetError('');
        try {
            await api.resetContractorPayoutAccount(c.id);
            setHasPayoutAccount(false);
            setPayoutOnboardingComplete(false);
        } catch (e: any) {
            setPayoutResetError(e.message);
        } finally {
            setResettingPayout(false);
        }
    };

    const addSkill = async () => {
        setSkillError('');
        const name = newSkill.trim();
        if (!name) return;
        try {
            const skill = await api.addContractorSkill(c.id, name);
            setSkills(prev => [...prev, skill].sort((a, b) => a.name.localeCompare(b.name)));
            setNewSkill('');
        } catch (e: any) { setSkillError(e.message); }
    };

    const removeSkill = async (skillId: string) => {
        try {
            await api.removeContractorSkill(c.id, skillId);
            setSkills(prev => prev.filter(s => s.id !== skillId));
        } catch { }
    };

    const globalRateLabel = formatRateLabel(c.rateType, c.rateAmount);

    const saveGlobalRate = async (rateType: string, amount: number) => {
        await api.setContractorRate(c.id, rateType, amount);
        onRateUpdated?.(c.id, rateType, amount);
        setShowGlobalEditor(false);
    };

    const saveServiceRate = async (rateType: string, amount: number) => {
        await api.setContractorRate(c.id, rateType, amount, newSvcType);
        setServiceRates(prev => {
            const without = prev.filter(r => r.serviceType !== newSvcType);
            return [...without, { serviceType: newSvcType, rateType, rateAmount: amount }];
        });
        setShowAddService(false);
        setNewSvcType(serviceTypes[0] ?? '');
    };

    const deleteServiceRate = async (serviceType: string) => {
        try {
            await api.deleteContractorServiceRate(c.id, serviceType);
            setServiceRates(prev => prev.filter(r => r.serviceType !== serviceType));
        } catch { }
    };

    const availableSvcTypes = serviceTypes.filter(t => !serviceRates.some(r => r.serviceType === t));

    return (
        <Card>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{ width: 44, height: 44, borderRadius: '50%', background: C.greenBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: C.green, fontSize: 14, flexShrink: 0 }}>
                    {c.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                </div>
                <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</span>
                        <span style={{ background: c.isOnline ? C.greenBg : C.bg, color: c.isOnline ? C.green : C.textMuted, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{c.isOnline ? 'Online' : 'Offline'}</span>
                        {c.tier && c.tier !== 'Apprentice' && (
                            <span style={{ fontSize: 11, background: c.tier === 'Master' ? '#FEF3C7' : '#EEF2FF', color: c.tier === 'Master' ? '#B45309' : '#3730A3', borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>
                                {c.tierIcon} {c.tier}
                            </span>
                        )}
                        {c.backgroundCheckStatus === 'Clear' && (
                            <span style={{ fontSize: 11, background: C.greenBg, color: C.green, borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>🛡 Verified</span>
                        )}
                        {c.backgroundCheckStatus === 'Consider' && (
                            <span style={{ fontSize: 11, background: '#FFF7E6', color: '#854F0B', borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>⚠ Review</span>
                        )}
                        {c.backgroundCheckStatus === 'Suspended' && (
                            <span style={{ fontSize: 11, background: '#FDECEA', color: '#9B1C1C', borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>🚫 Suspended</span>
                        )}
                    </div>
                    <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>{c.email} · {c.phoneNumber || 'No phone'}</div>
                    {c.vehicleDescription && <div style={{ fontSize: 13, color: C.textSub }}>🚚 {c.vehicleDescription}</div>}

                    {/* Global rate */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                        <span style={{ fontSize: 12, color: C.textMuted }}>Default rate: <strong style={{ color: C.text }}>{globalRateLabel}</strong></span>
                        <button onClick={() => setShowGlobalEditor(v => !v)} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', color: C.textSub }}>
                            {showGlobalEditor ? 'Cancel' : 'Edit'}
                        </button>
                    </div>
                    {showGlobalEditor && (
                        <RateEditor
                            id={`global-${c.id}`}
                            initialType={c.rateType ?? 'PercentageSplit'}
                            initialAmount={c.rateAmount?.toString() ?? '80'}
                            label="Default Pay Rate"
                            onSave={saveGlobalRate}
                            onCancel={() => setShowGlobalEditor(false)}
                        />
                    )}

                    {/* Per-service rates */}
                    <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 4, fontWeight: 600 }}>Service rates</div>
                        {serviceRates.length === 0 && (
                            <div style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>No overrides — using default rate for all services</div>
                        )}
                        {serviceRates.map(r => (
                            <div key={r.serviceType} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                <span style={{ fontSize: 12, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: '2px 8px', color: C.text }}>
                                    {r.serviceType}: <strong>{formatRateLabel(r.rateType, r.rateAmount)}</strong>
                                </span>
                                <button onClick={() => deleteServiceRate(r.serviceType)}
                                    style={{ fontSize: 11, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}
                                    title="Remove override">×</button>
                            </div>
                        ))}
                        {availableSvcTypes.length > 0 && (
                            <>
                                {!showAddService ? (
                                    <button onClick={() => setShowAddService(true)}
                                        style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', color: C.textSub, marginTop: 4 }}>
                                        + Add service rate
                                    </button>
                                ) : (
                                    <div style={{ marginTop: 6, padding: 12, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: C.textSub }}>Add Service Rate Override</div>
                                        <select value={newSvcType} onChange={e => setNewSvcType(e.target.value)}
                                            style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, marginBottom: 8, boxSizing: 'border-box' as const }}>
                                            {availableSvcTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        <RateEditor
                                            id={`svc-${c.id}-${newSvcType}`}
                                            initialType="PercentageSplit"
                                            initialAmount="80"
                                            label=""
                                            onSave={saveServiceRate}
                                            onCancel={() => setShowAddService(false)}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Skills & certifications (TREK-75) */}
                    {certTrackingEnabled && (
                        <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 4, fontWeight: 600 }}>Skills & certifications</div>
                            <Alert message={skillError} />
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                                {skills.map(s => (
                                    <span key={s.id} style={{ fontSize: 12, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 20, padding: '2px 10px', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {s.name}
                                        <button onClick={() => removeSkill(s.id)} title="Remove"
                                            style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0 }}>×</button>
                                    </span>
                                ))}
                                {skills.length === 0 && <span style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>No skills added</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <input value={newSkill} onChange={e => setNewSkill(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') addSkill(); }}
                                    placeholder="e.g. Forklift Certified"
                                    style={{ flex: 1, padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12 }} />
                                <button onClick={addSkill} style={{ fontSize: 11, padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', color: C.textSub }}>+ Add</button>
                            </div>
                        </div>
                    )}

                    {/* Payout account (Stripe Connect) */}
                    <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 4, fontWeight: 600 }}>Payout account</div>
                        <Alert message={payoutResetError} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            {!hasPayoutAccount ? (
                                <span style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>Not connected</span>
                            ) : (
                                <span style={{
                                    fontSize: 11, borderRadius: 20, padding: '2px 8px', fontWeight: 600,
                                    background: payoutOnboardingComplete ? C.greenBg : '#FFF7E6',
                                    color: payoutOnboardingComplete ? C.green : '#854F0B',
                                }}>
                                    {payoutOnboardingComplete ? '✓ Connected' : '⏳ Onboarding incomplete'}
                                </span>
                            )}
                            {hasPayoutAccount && (
                                <button onClick={resetPayoutAccount} disabled={resettingPayout}
                                    style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, border: `1px solid ${C.border}`, background: 'transparent', cursor: resettingPayout ? 'default' : 'pointer', color: '#DC2626', opacity: resettingPayout ? 0.6 : 1 }}>
                                    {resettingPayout ? 'Resetting…' : 'Reset payout account'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Service area / territory (opt-in) — unset means this contractor sees every
                        job tenant-wide exactly as before; only a configured radius restricts them. */}
                    <div style={{ marginTop: 10 }}>
                        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 4, fontWeight: 600 }}>Service area</div>
                        <Alert message={serviceAreaError} />
                        {!showServiceAreaEditor ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                {serviceArea.address ? (
                                    <span style={{ fontSize: 12, color: C.textSub }}>{serviceArea.address} · {serviceArea.radiusMiles}mi radius</span>
                                ) : (
                                    <span style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>Unrestricted — sees every job tenant-wide</span>
                                )}
                                <button onClick={() => setShowServiceAreaEditor(true)}
                                    style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', color: C.greenMid }}>
                                    {serviceArea.address ? 'Edit' : 'Set territory'}
                                </button>
                            </div>
                        ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, maxWidth: 420 }}>
                                <AddressAutocomplete
                                    label="Service area center address"
                                    value={serviceArea.address}
                                    onChange={({ address, lat, lng }) => setServiceArea(f => ({ ...f, address, lat: String(lat), lng: String(lng) }))}
                                    placeholder="Enter a city or address"
                                />
                                <Input label="Radius (miles)" value={serviceArea.radiusMiles}
                                    onChange={v => setServiceArea(f => ({ ...f, radiusMiles: v }))} type="number" />
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <Btn size="sm" onClick={saveServiceArea} disabled={savingServiceArea}>{savingServiceArea ? 'Saving…' : 'Save'}</Btn>
                                    {serviceArea.address && (
                                        <button onClick={clearServiceArea} disabled={savingServiceArea}
                                            style={{ fontSize: 12, color: '#DC2626', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                                            Clear territory
                                        </button>
                                    )}
                                    <button onClick={() => setShowServiceAreaEditor(false)}
                                        style={{ fontSize: 12, color: C.textMuted, background: 'transparent', border: 'none', cursor: 'pointer' }}>
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: C.yellow }}>★ {c.averageRating.toFixed(1)}</div>
                    <div style={{ fontSize: 12, color: C.textMuted }}>{c.totalRatings} ratings</div>
                    <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>{c.totalJobsCompleted} jobs done</div>
                </div>
            </div>
        </Card>
    );
}

function ContractorsPanel({ contractors, documents, pricing = [], catalogCategories = [] }: { contractors: ContractorListItem[]; documents: ContractorDocumentItem[]; pricing: PricingRule[]; catalogCategories?: string[] }) {
    const branding = useBranding();
    const contractorLabelPlural = branding.contractorNoun ? branding.contractorNoun + 's' : 'Contractors';
    const [localContractors, setLocalContractors] = React.useState(contractors);
    React.useEffect(() => setLocalContractors(contractors), [contractors]);

    const handleRateUpdated = (id: string, rateType: string, rateAmount: number | null) => {
        setLocalContractors(prev => prev.map(c => c.id === id ? { ...c, rateType, rateAmount: rateAmount ?? undefined } : c));
    };

    // Merges the legacy PricingRule types with Service Catalog categories — a converted Estimate
    // Builder job's ServiceTypeName is derived from its line items' catalog categories (see
    // EstimateConversionService.ConvertToJobAsync), so an override has to be settable against
    // those category names too, not just the fixed booking-flow service types.
    const serviceTypes = Array.from(new Set([
        ...pricing.filter(p => p.isActive).map(p => p.serviceTypeName),
        ...catalogCategories,
    ]));

    const docMap = new Map(documents.map(d => [d.contractorId, d.onboardingStatus]));

    const groups = [
        { label: 'Approved', status: 'Approved', color: C.green, bg: C.greenBg, items: [] as ContractorListItem[] },
        { label: 'Under Review', status: 'UnderReview', color: '#3730A3', bg: '#EEF2FF', items: [] as ContractorListItem[] },
        { label: 'Incomplete', status: 'Incomplete', color: C.textMuted, bg: C.bg, items: [] as ContractorListItem[] },
    ];

    for (const c of localContractors) {
        const status = docMap.get(c.id) ?? 'Incomplete';
        const group = groups.find(g => g.status === status) ?? groups[2];
        group.items.push(c);
    }

    return (
        <div>
            {groups.filter(g => g.items.length > 0).map(group => (
                <div key={group.status} style={{ marginBottom: 28 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: group.color }}>{group.label}</h3>
                        <span style={{ background: group.bg, color: group.color, borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>{group.items.length}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {group.items.map(c => <ContractorCard key={c.id} c={c} serviceTypes={serviceTypes} onRateUpdated={handleRateUpdated} />)}
                    </div>
                </div>
            ))}
            {contractors.length === 0 && (
                <Card style={{ textAlign: 'center', padding: 48 }}>
                    <div style={{ fontSize: 40 }}>🚚</div>
                    <div style={{ fontSize: 16, fontWeight: 600, marginTop: 12 }}>No {contractorLabelPlural.toLowerCase()} yet</div>
                </Card>
            )}
        </div>
    );
}

// ── Pricing panel ─────────────────────────────────────────────────────────────

interface PricingPanelProps {
    rules: PricingRule[];
    onSaved: () => void;
}

function LoadingRateConfig() {
    const loadingEnabled = useFeature('loadingJobs');
    if (!loadingEnabled) return null;
    const [rate, setRate] = React.useState('');
    const [loaded, setLoaded] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [success, setSuccess] = React.useState('');

    React.useEffect(() => {
        api.getPlatformConfig().then(c => { setRate(c.loadingHourlyRate.toString()); setLoaded(true); });
    }, []);

    const save = async () => {
        setSaving(true); setError(''); setSuccess('');
        try {
            const val = parseFloat(rate);
            if (isNaN(val) || val <= 0) { setError('Enter a valid rate greater than 0.'); return; }
            await api.updatePlatformConfig(val);
            setSuccess(`Loading rate updated to $${val.toFixed(2)}/hr`);
        } catch (e: any) {
            setError(e.message ?? 'Failed to save.');
        } finally {
            setSaving(false);
        }
    };

    if (!loaded) return null;

    return (
        <Card style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: C.text }}>Loading Runner Platform Rate</div>
            <div style={{ fontSize: 13, color: C.textSub, marginBottom: 10 }}>
                The hourly rate charged to customers for loading runners. Contractor PerHour rates cannot exceed this value.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, color: C.textMuted }}>$</span>
                <input type="number" min="1" step="0.50" value={rate} onChange={e => setRate(e.target.value)}
                    style={{ width: 100, padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 14 }} />
                <span style={{ fontSize: 13, color: C.textMuted }}>/hr</span>
                <button onClick={save} disabled={saving}
                    style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: C.green, color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                    {saving ? 'Saving…' : 'Save'}
                </button>
            </div>
            {error && <div style={{ fontSize: 12, color: '#DC2626', marginTop: 6 }}>{error}</div>}
            {success && <div style={{ fontSize: 12, color: C.green, marginTop: 6 }}>{success}</div>}
        </Card>
    );
}

// Renders unconditionally (not gated by useFeature) since the admin needs to be able to turn
// the fee/tax on in the first place — useFeature only hides it from the customer-facing checkout.
function ServiceFeeConfig() {
    const branding = useBranding();
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const contractorLabelLower = contractorLabel.toLowerCase();
    const [cfg, setCfg] = React.useState<ServiceFeeConfigDto | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [success, setSuccess] = React.useState('');

    React.useEffect(() => { api.getServiceFeeConfig().then(setCfg).catch(() => {}); }, []);

    const save = async () => {
        if (!cfg) return;
        setError(''); setSuccess('');
        if (cfg.serviceFeeAmount < 0) { setError('Fee amount cannot be negative.'); return; }
        if (cfg.serviceFeeType === 'Percentage' && cfg.serviceFeeAmount > 100) { setError('A percentage fee cannot exceed 100%.'); return; }
        if (cfg.serviceFeeContractorSplitPercent < 0 || cfg.serviceFeeContractorSplitPercent > 100) { setError(`${contractorLabel} split must be between 0 and 100.`); return; }
        if (!cfg.serviceFeeLabel.trim()) { setError('Fee label cannot be empty.'); return; }
        setSaving(true);
        try {
            const saved = await api.updateServiceFeeConfig(cfg);
            setCfg(saved);
            setSuccess('Saved.');
        } catch (e: any) { setError(e.message ?? 'Failed to save.'); }
        finally { setSaving(false); }
    };

    if (!cfg) return null;

    return (
        <Card style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: C.text }}>Job Fee</div>
            <div style={{ fontSize: 13, color: C.textSub, marginBottom: 12 }}>
                An optional fee added to every job's charge, shown as its own line item at checkout — e.g. a delivery or pickup fee. Has its own {contractorLabelLower}/platform split, separate from the {contractorLabelLower}'s job payout rate.
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13 }}>
                <input type="checkbox" checked={cfg.serviceFeeEnabled} onChange={e => setCfg({ ...cfg, serviceFeeEnabled: e.target.checked })} />
                Enable job fee
            </label>
            {cfg.serviceFeeEnabled && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 4 }}>
                    <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>Line item label</label>
                        <input type="text" value={cfg.serviceFeeLabel} onChange={e => setCfg({ ...cfg, serviceFeeLabel: e.target.value })}
                            placeholder="e.g. Delivery Fee, Pickup Fee, Service Fee"
                            style={{ width: '100%', maxWidth: 280, padding: '8px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13 }} />
                    </div>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                            <input type="radio" checked={cfg.serviceFeeType === 'Flat'} onChange={() => setCfg({ ...cfg, serviceFeeType: 'Flat' })} />
                            Flat $
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                            <input type="radio" checked={cfg.serviceFeeType === 'Percentage'} onChange={() => setCfg({ ...cfg, serviceFeeType: 'Percentage' })} />
                            % of subtotal
                        </label>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 13, color: C.textMuted }}>{cfg.serviceFeeType === 'Flat' ? '$' : ''}</span>
                        <input type="number" min="0" step="0.01" value={cfg.serviceFeeAmount}
                            onChange={e => setCfg({ ...cfg, serviceFeeAmount: parseFloat(e.target.value) || 0 })}
                            style={{ width: 100, padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 14 }} />
                        <span style={{ fontSize: 13, color: C.textMuted }}>{cfg.serviceFeeType === 'Percentage' ? '%' : 'per job'}</span>
                    </div>
                    <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>{contractorLabel}'s share of the fee</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <input type="number" min="0" max="100" step="1" value={cfg.serviceFeeContractorSplitPercent}
                                onChange={e => setCfg({ ...cfg, serviceFeeContractorSplitPercent: parseFloat(e.target.value) || 0 })}
                                style={{ width: 80, padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 14 }} />
                            <span style={{ fontSize: 13, color: C.textMuted }}>% to {contractorLabelLower} (rest stays with the platform)</span>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ borderTop: `1px solid ${C.borderLight}`, marginTop: 16, paddingTop: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: C.text }}>Sales Tax</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13 }}>
                    <input type="checkbox" checked={cfg.salesTaxEnabled} onChange={e => setCfg({ ...cfg, salesTaxEnabled: e.target.checked })} />
                    Enable sales tax (Stripe Tax)
                </label>
                <div style={{ fontSize: 12, color: C.textMuted }}>
                    Before enabling, you must first turn on Stripe Tax and add at least one tax registration in your{' '}
                    <a href="https://dashboard.stripe.com/tax/registrations" target="_blank" rel="noreferrer" style={{ color: C.green }}>Stripe Dashboard</a>.
                    Tax is calculated automatically based on the job's address once a registration is active.
                </div>
            </div>

            <button onClick={save} disabled={saving}
                style={{ marginTop: 16, padding: '8px 20px', borderRadius: 6, border: 'none', background: C.green, color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                {saving ? 'Saving…' : 'Save'}
            </button>
            {error && <div style={{ fontSize: 12, color: '#DC2626', marginTop: 8 }}>{error}</div>}
            {success && <div style={{ fontSize: 12, color: C.green, marginTop: 8 }}>{success}</div>}
        </Card>
    );
}

function PricingPanel({ rules, onSaved }: PricingPanelProps) {
    const certTrackingEnabled = useFeature('contractorCertTracking');
    const [editing, setEditing] = useState<string | null>(null);
    const [base, setBase] = useState('');
    const [perUnit, setPerUnit] = useState('');
    const [unitLabel, setUnitLabel] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [allowPhotoUpload, setAllowPhotoUpload] = useState(false);
    const [requiresDonationCenter, setRequiresDonationCenter] = useState(false);
    const [estimateWholePile, setEstimateWholePile] = useState(false);
    const [requiresDocumentCategories, setRequiresDocumentCategories] = useState(false);
    const [shreddingRetrievalFee, setShreddingRetrievalFee] = useState('0');
    const [allowGifting, setAllowGifting] = useState(true);
    const [description, setDescription] = useState('');
    const [categoryName, setCategoryName] = useState('');
    const [requiredSkills, setRequiredSkills] = useState('');
    const [preorderEnabled, setPreorderEnabled] = useState(false);
    const [launchDate, setLaunchDate] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Add new category state
    const [showAdd, setShowAdd] = useState(false);
    const [newName, setNewName] = useState('');
    const [newBase, setNewBase] = useState('');
    const [newPerUnit, setNewPerUnit] = useState('');
    const [newUnit, setNewUnit] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [newAllowPhotoUpload, setNewAllowPhotoUpload] = useState(false);
    const [newRequiresDonationCenter, setNewRequiresDonationCenter] = useState(false);
    const [newEstimateWholePile, setNewEstimateWholePile] = useState(false);
    const [newRequiresDocumentCategories, setNewRequiresDocumentCategories] = useState(false);
    const [newShreddingRetrievalFee, setNewShreddingRetrievalFee] = useState('0');
    const [newAllowGifting, setNewAllowGifting] = useState(true);
    const [newRequiredSkills, setNewRequiredSkills] = useState('');
    const [newPreorderEnabled, setNewPreorderEnabled] = useState(false);
    const [newLaunchDate, setNewLaunchDate] = useState('');
    const [adding, setAdding] = useState(false);

    const parseSkillsInput = (v: string) => v.split(',').map(s => s.trim()).filter(s => s.length > 0);

    const startEdit = (r: PricingRule) => {
        setEditing(r.id);
        setBase(r.basePrice.toString());
        setPerUnit(r.pricePerUnit.toString());
        setUnitLabel(r.unitLabel);
        setIsActive(r.isActive ?? true);
        setAllowPhotoUpload(r.allowPhotoUpload ?? false);
        setRequiresDonationCenter(r.requiresDonationCenter ?? false);
        setEstimateWholePile(r.estimateWholePile ?? false);
        setRequiresDocumentCategories(r.requiresDocumentCategories ?? false);
        setShreddingRetrievalFee((r.shreddingRetrievalFee ?? 0).toString());
        setAllowGifting(r.allowGifting ?? true);
        setDescription(r.description || '');
        setCategoryName(r.serviceTypeName || r.serviceType);
        setRequiredSkills((r.requiredSkills || []).join(', '));
        setPreorderEnabled(r.preorderEnabled ?? false);
        setLaunchDate(r.launchDate ? r.launchDate.slice(0, 10) : '');
        setError(''); setSuccess('');
    };

    const save = async () => {
        if (!editing) return;
        setSaving(true); setError(''); setSuccess('');
        try {
            await api.updatePricingById(editing, parseFloat(base), parseFloat(perUnit), unitLabel, isActive, description, categoryName, allowPhotoUpload, certTrackingEnabled ? parseSkillsInput(requiredSkills) : undefined, preorderEnabled, launchDate || null, requiresDonationCenter, estimateWholePile, requiresDocumentCategories, parseFloat(shreddingRetrievalFee) || 0, allowGifting);
            setSuccess('Pricing updated!');
            setEditing(null);
            onSaved();
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const addCategory = async () => {
        if (!newName.trim() || !newBase || !newPerUnit || !newUnit.trim()) {
            setError('All fields are required.');
            return;
        }
        setAdding(true); setError(''); setSuccess('');
        try {
            await api.addPricingRule(newName, parseFloat(newBase), parseFloat(newPerUnit), newUnit, newDescription, newAllowPhotoUpload, certTrackingEnabled ? parseSkillsInput(newRequiredSkills) : undefined, newPreorderEnabled, newLaunchDate || null, newRequiresDonationCenter, newEstimateWholePile, newRequiresDocumentCategories, parseFloat(newShreddingRetrievalFee) || 0, newAllowGifting);
            setSuccess(`${newName} category added!`);
            setShowAdd(false);
            setNewName(''); setNewBase(''); setNewPerUnit(''); setNewUnit(''); setNewDescription(''); setNewAllowPhotoUpload(false); setNewRequiredSkills(''); setNewPreorderEnabled(false); setNewLaunchDate(''); setNewRequiresDonationCenter(false); setNewEstimateWholePile(false); setNewRequiresDocumentCategories(false); setNewShreddingRetrievalFee('0'); setNewAllowGifting(true);
            onSaved();
        } catch (e: any) { setError(e.message); }
        finally { setAdding(false); }
    };

    const deleteRule = async (id: string, name: string) => {
        if (!window.confirm(`Delete pricing for ${name}? This cannot be undone.`)) return;
        setError(''); setSuccess('');
        try {
            await api.deletePricingRule(id);
            setSuccess(`${name} deleted.`);
            onSaved();
        } catch (e: any) { setError(e.message); }
    };

    return (
        <div style={{ maxWidth: 640 }}>
            <LoadingRateConfig />
            <ServiceFeeConfig />
            <Alert message={error} />
            <Alert message={success} type="success" />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: C.textSub }}>
                    {rules.length} service {rules.length === 1 ? 'category' : 'categories'}
                </div>
                <Btn size="sm" onClick={() => { setShowAdd(true); setError(''); }}>
                    + Add category
                </Btn>
            </div>

            {/* Add new category form */}
            {showAdd && (
                <Card style={{ marginBottom: 20, borderLeft: `4px solid ${C.greenMid}` }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>➕ New service category</h3>
                    <Input
                        label="Category name"
                        value={newName}
                        onChange={setNewName}
                        placeholder="e.g. IT Support, Moving Help, Cleaning"
                    />
                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                            <Input label="Base price ($)" value={newBase} onChange={setNewBase} type="number" placeholder="10.00" />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Input label="Per unit price ($)" value={newPerUnit} onChange={setNewPerUnit} type="number" placeholder="2.50" />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Input label="Unit label" value={newUnit} onChange={setNewUnit} placeholder="item, hour, bag" />
                        </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 12px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={newAllowPhotoUpload}
                            onChange={e => setNewAllowPhotoUpload(e.target.checked)}
                            style={{ accentColor: C.greenMid, width: 16, height: 16 }}
                        />
                        <span style={{ fontSize: 13, color: C.text }}>
                            Allow photo upload — customer can attach a photo during booking
                        </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 12px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={newRequiresDonationCenter}
                            onChange={e => setNewRequiresDonationCenter(e.target.checked)}
                            style={{ accentColor: C.greenMid, width: 16, height: 16 }}
                        />
                        <span style={{ fontSize: 13, color: C.text }}>
                            Requires donation center — customer picks one from the Donation Centers list when booking
                        </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 12px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={newEstimateWholePile}
                            onChange={e => setNewEstimateWholePile(e.target.checked)}
                            style={{ accentColor: C.greenMid, width: 16, height: 16 }}
                        />
                        <span style={{ fontSize: 13, color: C.text }}>
                            Estimate from whole pile — AI photo estimate itemizes a mixed pile of items instead of counting individual boxes
                        </span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 12px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={newRequiresDocumentCategories}
                            onChange={e => setNewRequiresDocumentCategories(e.target.checked)}
                            style={{ accentColor: C.greenMid, width: 16, height: 16 }}
                        />
                        <span style={{ fontSize: 13, color: C.text }}>
                            Requires document categories — customer declares what's being destroyed (HR, financial, PHI, etc.) at booking, contractor confirms at completion
                        </span>
                    </label>
                    {newRequiresDocumentCategories && (
                        <div style={{ margin: '4px 0 12px', padding: '10px 12px', background: C.bg, borderRadius: 8 }}>
                            <div style={{ fontSize: 12, color: C.textSub, marginBottom: 8 }}>
                                Base price and per-unit price above aren't used for this service — pricing is per bin size instead. Manage sizes in the Shredding Bins → Bin Sizes panel.
                            </div>
                            <Input
                                label="Bin retrieval fee $ (cancellation/damage pickup — not tied to size)"
                                value={newShreddingRetrievalFee}
                                onChange={setNewShreddingRetrievalFee}
                                type="number"
                            />
                        </div>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 12px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={newAllowGifting}
                            onChange={e => setNewAllowGifting(e.target.checked)}
                            style={{ accentColor: C.greenMid, width: 16, height: 16 }}
                        />
                        <span style={{ fontSize: 13, color: C.text }}>
                            Allow gifting — shows up in the customer "Send a Gift" picker
                        </span>
                    </label>
                    <Input
                        label="Description (optional)"
                        value={newDescription}
                        onChange={setNewDescription}
                        placeholder="e.g. Ideal for moving boxes, paper, packaging material"
                    />
                    {certTrackingEnabled && (
                        <Input
                            label="Required skills (optional, comma-separated)"
                            value={newRequiredSkills}
                            onChange={setNewRequiredSkills}
                            placeholder="e.g. Hazmat Handling, Forklift Certified"
                        />
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 12px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={newPreorderEnabled}
                            onChange={e => setNewPreorderEnabled(e.target.checked)}
                            style={{ accentColor: C.greenMid, width: 16, height: 16 }}
                        />
                        <span style={{ fontSize: 13, color: C.text }}>
                            Enable preorder — sellable on the public /preorder page before it's a normal booking option
                        </span>
                    </label>
                    {newPreorderEnabled && (
                        <Input
                            label="Launch date (optional — hidden from normal booking until this date, always preorderable)"
                            value={newLaunchDate}
                            onChange={setNewLaunchDate}
                            type="date"
                        />
                    )}
                    {newBase && newPerUnit && newUnit && (
                        <div style={{ fontSize: 12, color: C.textSub, marginBottom: 12 }}>
                            Preview (10 {newUnit}s): <strong>${(parseFloat(newBase || '0') + parseFloat(newPerUnit || '0') * 10).toFixed(2)}</strong>
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 10 }}>
                        <Btn variant="outline" onClick={() => setShowAdd(false)}>Cancel</Btn>
                        <Btn onClick={addCategory} disabled={adding} fullWidth>
                            {adding ? 'Adding...' : 'Add category'}
                        </Btn>
                    </div>
                </Card>
            )}

            {/* Existing categories */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {rules.map(r => (
                    <Card key={r.id} style={{ opacity: r.isActive === false ? 0.6 : 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                                    <span style={{ fontWeight: 700, fontSize: 16 }}>
                                        {r.serviceTypeName || r.serviceType}
                                    </span>
                                    {r.isActive === false && (
                                        <span style={{ fontSize: 11, background: '#F1EFE8', color: '#5F5E5A', borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>
                                            Inactive
                                        </span>
                                    )}
                                </div>
                                {editing === r.id ? (
                                    <div>
                                        <div style={{ marginBottom: 12 }}>
                                            <Input label="Category name" value={categoryName} onChange={setCategoryName} placeholder="e.g. Cardboard, Furniture" />
                                        </div>
                                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                            <div style={{ flex: 1, minWidth: 120 }}>
                                                <Input label="Base price ($)" value={base} onChange={setBase} type="number" />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 120 }}>
                                                <Input label="Per unit price ($)" value={perUnit} onChange={setPerUnit} type="number" />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 120 }}>
                                                <Input label="Unit label" value={unitLabel} onChange={setUnitLabel} placeholder="item, hour, bag" />
                                            </div>
                                        </div>
                                        {/* Active toggle */}
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={isActive}
                                                onChange={e => setIsActive(e.target.checked)}
                                                style={{ accentColor: C.greenMid, width: 16, height: 16 }}
                                            />
                                            <span style={{ fontSize: 13, color: C.text }}>
                                                Active — visible in customer booking form
                                            </span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={allowPhotoUpload}
                                                onChange={e => setAllowPhotoUpload(e.target.checked)}
                                                style={{ accentColor: C.greenMid, width: 16, height: 16 }}
                                            />
                                            <span style={{ fontSize: 13, color: C.text }}>
                                                Allow photo upload — customer can attach a photo during booking
                                            </span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={requiresDonationCenter}
                                                onChange={e => setRequiresDonationCenter(e.target.checked)}
                                                style={{ accentColor: C.greenMid, width: 16, height: 16 }}
                                            />
                                            <span style={{ fontSize: 13, color: C.text }}>
                                                Requires donation center — customer picks one from the Donation Centers list when booking
                                            </span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={estimateWholePile}
                                                onChange={e => setEstimateWholePile(e.target.checked)}
                                                style={{ accentColor: C.greenMid, width: 16, height: 16 }}
                                            />
                                            <span style={{ fontSize: 13, color: C.text }}>
                                                Estimate from whole pile — AI photo estimate itemizes a mixed pile of items instead of counting individual boxes
                                            </span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={requiresDocumentCategories}
                                                onChange={e => setRequiresDocumentCategories(e.target.checked)}
                                                style={{ accentColor: C.greenMid, width: 16, height: 16 }}
                                            />
                                            <span style={{ fontSize: 13, color: C.text }}>
                                                Requires document categories — customer declares what's being destroyed (HR, financial, PHI, etc.) at booking, contractor confirms at completion
                                            </span>
                                        </label>
                                        {requiresDocumentCategories && (
                                            <div style={{ margin: '4px 0 12px', padding: '10px 12px', background: C.bg, borderRadius: 8 }}>
                                                <div style={{ fontSize: 12, color: C.textSub, marginBottom: 8 }}>
                                                    Base price and per-unit price above aren't used for this service — pricing is per bin size instead. Manage sizes in the Shredding Bins → Bin Sizes panel.
                                                </div>
                                                <Input
                                                    label="Bin retrieval fee $ (cancellation/damage pickup — not tied to size)"
                                                    value={shreddingRetrievalFee}
                                                    onChange={setShreddingRetrievalFee}
                                                    type="number"
                                                />
                                            </div>
                                        )}
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={allowGifting}
                                                onChange={e => setAllowGifting(e.target.checked)}
                                                style={{ accentColor: C.greenMid, width: 16, height: 16 }}
                                            />
                                            <span style={{ fontSize: 13, color: C.text }}>
                                                Allow gifting — shows up in the customer "Send a Gift" picker
                                            </span>
                                        </label>
                                        <Input
                                            label="Description (optional)"
                                            value={description}
                                            onChange={setDescription}
                                            placeholder="e.g. Ideal for moving boxes, paper, packaging material"
                                        />
                                        {certTrackingEnabled && (
                                            <Input
                                                label="Required skills (optional, comma-separated)"
                                                value={requiredSkills}
                                                onChange={setRequiredSkills}
                                                placeholder="e.g. Hazmat Handling, Forklift Certified"
                                            />
                                        )}
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 12px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={preorderEnabled}
                                                onChange={e => setPreorderEnabled(e.target.checked)}
                                                style={{ accentColor: C.greenMid, width: 16, height: 16 }}
                                            />
                                            <span style={{ fontSize: 13, color: C.text }}>
                                                Enable preorder — sellable on the public /preorder page before it's a normal booking option
                                            </span>
                                        </label>
                                        {preorderEnabled && (
                                            <Input
                                                label="Launch date (optional — hidden from normal booking until this date, always preorderable)"
                                                value={launchDate}
                                                onChange={setLaunchDate}
                                                type="date"
                                            />
                                        )}
                                        {base && perUnit && unitLabel && (
                                            <div style={{ fontSize: 12, color: C.textSub, marginBottom: 12 }}>
                                                Preview (10 {unitLabel}s): <strong>${(parseFloat(base || '0') + parseFloat(perUnit || '0') * 10).toFixed(2)}</strong>
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <Btn size="sm" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
                                            <Btn size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Btn>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ color: C.textSub, fontSize: 14 }}>
                                        Base: <strong>${r.basePrice.toFixed(2)}</strong>
                                        {' + '}
                                        <strong>${r.pricePerUnit.toFixed(2)}</strong> per {r.unitLabel}
                                        <span style={{ color: C.textMuted, fontSize: 12, marginLeft: 12 }}>
                                            Example (10 {r.unitLabel}s): ${(r.basePrice + r.pricePerUnit * 10).toFixed(2)}
                                        </span>

                                        {r.description && (
                                            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, fontStyle: 'italic' }}>
                                                {r.description}
                                            </div>
                                        )}
                                        {certTrackingEnabled && r.requiredSkills && r.requiredSkills.length > 0 && (
                                            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
                                                🔧 Requires: {r.requiredSkills.join(', ')}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {editing !== r.id && (
                                <div style={{ display: 'flex', gap: 8, marginLeft: 12 }}>
                                    <Btn size="sm" variant="outline" onClick={() => startEdit(r)}>Edit</Btn>
                                    <Btn size="sm" variant="danger" onClick={() => deleteRule(r.id, r.serviceTypeName || r.serviceType)}>Delete</Btn>
                                </div>
                            )}
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
}

// ── Service catalog panel (TREK-132) — wireframe screen 1 ──────────────────────

function ServiceCatalogPanel() {
    const branding = useBranding();
    const contractorLabelLower = (branding.contractorNoun || 'Contractor').toLowerCase();
    const [items, setItems] = useState<ServiceCatalogItemDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [laborRate, setLaborRate] = useState('');
    const [afterHoursMultiplier, setAfterHoursMultiplier] = useState('');
    const [savingRate, setSavingRate] = useState(false);

    const [showAdd, setShowAdd] = useState(false);
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    const [description, setDescription] = useState('');
    const [pricingType, setPricingType] = useState<ServiceCatalogPricingType>('Flat');
    const [price, setPrice] = useState('');
    const [unit, setUnit] = useState('');
    const [saving, setSaving] = useState(false);

    // Edit an existing item — PricingType is deliberately not editable here (matches the backend
    // UpdateServiceCatalogItemRequest, which doesn't accept it either): changing Flat/Hourly/
    // PartsMarkup changes what "price" even means, so that's a create-a-new-item decision, not
    // an edit. Everything else (name/category/description/price/unit) is editable.
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editName, setEditName] = useState('');
    const [editCategory, setEditCategory] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editPrice, setEditPrice] = useState('');
    const [editUnit, setEditUnit] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);

    const load = () => {
        setLoading(true); setError('');
        Promise.all([api.getServiceCatalogAll(), api.getLaborRate()])
            .then(([catalog, rate]) => {
                setItems(catalog);
                setLaborRate(rate.standardLaborRate.toString());
                setAfterHoursMultiplier(rate.afterHoursMultiplier.toString());
            })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const saveLaborRate = async () => {
        setSavingRate(true); setError(''); setSuccess('');
        try {
            const rate = parseFloat(laborRate);
            const mult = parseFloat(afterHoursMultiplier);
            if (isNaN(rate) || rate < 0) { setError('Enter a valid labor rate.'); return; }
            if (isNaN(mult) || mult < 1) { setError('After-hours multiplier must be at least 1.'); return; }
            await api.updateLaborRate(rate, mult);
            setSuccess('Labor rate saved.');
        } catch (e: any) { setError(e.message); }
        finally { setSavingRate(false); }
    };

    const resetAddForm = () => {
        setName(''); setCategory(''); setDescription(''); setPricingType('Flat'); setPrice(''); setUnit('');
    };

    const addItem = async () => {
        if (!name.trim() || !price) { setError('Name and price are required.'); return; }
        setSaving(true); setError(''); setSuccess('');
        try {
            await api.addServiceCatalogItem(name.trim(), category.trim(), description || null, pricingType, parseFloat(price), unit || null);
            setSuccess(`${name} added.`);
            setShowAdd(false);
            resetAddForm();
            load();
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const startEdit = (item: ServiceCatalogItemDto) => {
        setShowAdd(false);
        setEditingId(item.id);
        setEditName(item.name);
        setEditCategory(item.category);
        setEditDescription(item.description ?? '');
        setEditPrice(item.price.toString());
        setEditUnit(item.unit ?? '');
        setError('');
    };

    const cancelEdit = () => setEditingId(null);

    const saveEdit = async () => {
        if (!editingId) return;
        if (!editName.trim() || !editPrice) { setError('Name and price are required.'); return; }
        setSavingEdit(true); setError(''); setSuccess('');
        try {
            await api.updateServiceCatalogItem(editingId, editName.trim(), editCategory.trim(), editDescription || null, parseFloat(editPrice), editUnit || null);
            setSuccess(`${editName} updated.`);
            setEditingId(null);
            load();
        } catch (e: any) { setError(e.message); }
        finally { setSavingEdit(false); }
    };

    const toggleActive = async (item: ServiceCatalogItemDto) => {
        setError(''); setSuccess('');
        try {
            if (item.isActive) await api.deactivateServiceCatalogItem(item.id);
            else await api.activateServiceCatalogItem(item.id);
            load();
        } catch (e: any) { setError(e.message); }
    };

    const pricingLabel = (i: ServiceCatalogItemDto) => {
        if (i.pricingType === 'Hourly') return `Hourly · uses tenant labor rate`;
        if (i.pricingType === 'PartsMarkup') return `Parts+Markup · $${i.price.toFixed(2)} cost${i.unit ? `/${i.unit}` : ''}`;
        return `Flat · $${i.price.toFixed(2)}`;
    };

    if (loading) return <Spinner />;

    return (
        <div style={{ maxWidth: 640 }}>
            <Alert message={error} />
            <Alert message={success} type="success" />

            <Card style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: C.text }}>Standard Labor Rate</div>
                <div style={{ fontSize: 13, color: C.textSub, marginBottom: 10 }}>
                    Used to price catalog items marked "Hourly" — e.g. diagnostic time, repair labor.
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: C.textMuted }}>$</span>
                    <input type="number" min="0" step="0.50" value={laborRate} onChange={e => setLaborRate(e.target.value)}
                        style={{ width: 90, padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 14 }} />
                    <span style={{ fontSize: 13, color: C.textMuted }}>/hr &nbsp;·&nbsp; after-hours ×</span>
                    <input type="number" min="1" step="0.1" value={afterHoursMultiplier} onChange={e => setAfterHoursMultiplier(e.target.value)}
                        style={{ width: 70, padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 14 }} />
                    <button onClick={saveLaborRate} disabled={savingRate}
                        style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: C.green, color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
                        {savingRate ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </Card>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: C.textSub }}>
                    {items.length} catalog {items.length === 1 ? 'item' : 'items'}
                </div>
                <Btn size="sm" onClick={() => { setShowAdd(true); setEditingId(null); setError(''); }}>+ Add Item</Btn>
            </div>

            {showAdd && (
                <Card style={{ marginBottom: 20, borderLeft: `4px solid ${C.greenMid}` }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>➕ New catalog item</h3>
                    <Input label="Name" value={name} onChange={setName} placeholder="e.g. Diagnostic Fee, Capacitor" />
                    <Input label="Category" value={category} onChange={setCategory} placeholder="e.g. Diagnostics, Labor, Parts" />
                    <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: 12, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 5 }}>Pricing type</label>
                        <select value={pricingType} onChange={e => setPricingType(e.target.value as ServiceCatalogPricingType)}
                            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 14, boxSizing: 'border-box' as const }}>
                            <option value="Flat">Flat — fixed price</option>
                            <option value="Hourly">Hourly — uses standard labor rate</option>
                            <option value="PartsMarkup">Parts+Markup — cost per unit</option>
                        </select>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}>
                            <Input label={pricingType === 'PartsMarkup' ? 'Cost ($)' : 'Price ($)'} value={price} onChange={setPrice} type="number" placeholder="89.00" />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Input label="Unit (optional)" value={unit} onChange={setUnit} placeholder="hr, unit" />
                        </div>
                    </div>
                    <Input label="Description (optional)" value={description} onChange={setDescription} placeholder={`Shown to ${contractorLabelLower}/admin`} />
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Btn variant="outline" onClick={() => { setShowAdd(false); resetAddForm(); }}>Cancel</Btn>
                        <Btn onClick={addItem} disabled={saving} fullWidth>{saving ? 'Adding...' : 'Add item'}</Btn>
                    </div>
                </Card>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {items.map(item => editingId === item.id ? (
                    <Card key={item.id} style={{ borderLeft: `4px solid ${C.greenMid}` }}>
                        <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>✏️ Edit catalog item</h3>
                        <Input label="Name" value={editName} onChange={setEditName} />
                        <Input label="Category" value={editCategory} onChange={setEditCategory} />
                        <div style={{ display: 'flex', gap: 12 }}>
                            <div style={{ flex: 1 }}>
                                <Input label={item.pricingType === 'PartsMarkup' ? 'Cost ($)' : 'Price ($)'} value={editPrice} onChange={setEditPrice} type="number" />
                            </div>
                            <div style={{ flex: 1 }}>
                                <Input label="Unit (optional)" value={editUnit} onChange={setEditUnit} placeholder="hr, unit" />
                            </div>
                        </div>
                        <Input label="Description (optional)" value={editDescription} onChange={setEditDescription} />
                        <div style={{ fontSize: 11, color: C.textMuted, marginTop: -6, marginBottom: 12 }}>
                            Pricing type ({item.pricingType}) can't be changed here — deactivate this item and add a new one if it needs a different pricing type.
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Btn variant="outline" onClick={cancelEdit}>Cancel</Btn>
                            <Btn onClick={saveEdit} disabled={savingEdit} fullWidth>{savingEdit ? 'Saving...' : 'Save changes'}</Btn>
                        </div>
                    </Card>
                ) : (
                    <Card key={item.id} style={{ opacity: item.isActive ? 1 : 0.6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                                    <span style={{ fontWeight: 700, fontSize: 15 }}>{item.name}</span>
                                    {item.category && <Badge label={item.category} />}
                                    {!item.isActive && <span style={{ fontSize: 11, background: '#F1EFE8', color: '#5F5E5A', borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>Inactive</span>}
                                </div>
                                <div style={{ fontSize: 13, color: C.textSub }}>{pricingLabel(item)}</div>
                                {item.description && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, fontStyle: 'italic' }}>{item.description}</div>}
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                <Btn size="sm" variant="outline" onClick={() => startEdit(item)}>Edit</Btn>
                                <Btn size="sm" variant="outline" onClick={() => toggleActive(item)}>
                                    {item.isActive ? 'Deactivate' : 'Activate'}
                                </Btn>
                            </div>
                        </div>
                    </Card>
                ))}
                {items.length === 0 && <div style={{ color: C.textMuted, fontSize: 13 }}>No catalog items yet — add a diagnostic fee, labor rate item, or part above.</div>}
            </div>
        </div>
    );
}

// ── Supply & inventory tracking (TREK-81) ───────────────────────────────────────

function InventoryPanel() {
    const [items, setItems] = useState<InventoryItemDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [showAdd, setShowAdd] = useState(false);
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    const [qty, setQty] = useState('');
    const [threshold, setThreshold] = useState('');
    const [cost, setCost] = useState('');
    const [saving, setSaving] = useState(false);

    const [adjusting, setAdjusting] = useState<string | null>(null);
    const [adjustDelta, setAdjustDelta] = useState('');
    const [adjustReason, setAdjustReason] = useState('');

    const [historyFor, setHistoryFor] = useState<string | null>(null);
    const [history, setHistory] = useState<InventoryUsageDto[]>([]);

    const load = () => {
        setLoading(true); setError('');
        api.getInventory().then(setItems).catch(e => setError(e.message)).finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const resetAddForm = () => { setName(''); setCategory(''); setQty(''); setThreshold(''); setCost(''); };

    const addItem = async () => {
        if (!name.trim() || !qty || !threshold || !cost) { setError('Name, quantity, reorder threshold, and unit cost are required.'); return; }
        setSaving(true); setError(''); setSuccess('');
        try {
            await api.addInventoryItem(name.trim(), category.trim() || null, parseInt(qty, 10), parseInt(threshold, 10), parseFloat(cost));
            setSuccess(`${name} added.`);
            setShowAdd(false);
            resetAddForm();
            load();
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const submitAdjust = async (id: string) => {
        const delta = parseInt(adjustDelta, 10);
        if (isNaN(delta) || delta === 0 || !adjustReason.trim()) { setError('Enter a non-zero amount and a reason.'); return; }
        setError(''); setSuccess('');
        try {
            await api.adjustInventory(id, delta, adjustReason.trim());
            setSuccess('Inventory adjusted.');
            setAdjusting(null); setAdjustDelta(''); setAdjustReason('');
            load();
        } catch (e: any) { setError(e.message); }
    };

    const viewHistory = async (id: string) => {
        if (historyFor === id) { setHistoryFor(null); return; }
        try {
            const h = await api.getInventoryHistory(id);
            setHistory(h);
            setHistoryFor(id);
        } catch (e: any) { setError(e.message); }
    };

    if (loading) return <Spinner />;

    return (
        <div style={{ maxWidth: 720 }}>
            <Alert message={error} />
            <Alert message={success} type="success" />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: C.textSub }}>
                    {items.length} {items.length === 1 ? 'item' : 'items'}
                    {items.some(i => i.isLowStock) && (
                        <span style={{ marginLeft: 10, color: '#9B1C1C', fontWeight: 600 }}>
                            ⚠ {items.filter(i => i.isLowStock).length} below reorder threshold
                        </span>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Btn size="sm" variant="outline" onClick={() => api.downloadInventoryCsv().catch(e => setError(e.message))}>⬇ Export CSV</Btn>
                    <Btn size="sm" onClick={() => setShowAdd(true)}>+ Add item</Btn>
                </div>
            </div>

            {showAdd && (
                <Card style={{ marginBottom: 20, borderLeft: `4px solid ${C.greenMid}` }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>➕ New inventory item</h3>
                    <Input label="Name" value={name} onChange={setName} placeholder="e.g. Capacitor 45/5 MFD" />
                    <Input label="Category (optional)" value={category} onChange={setCategory} placeholder="e.g. Electrical, Refrigerant" />
                    <div style={{ display: 'flex', gap: 12 }}>
                        <div style={{ flex: 1 }}><Input label="Quantity on hand" value={qty} onChange={setQty} type="number" placeholder="20" /></div>
                        <div style={{ flex: 1 }}><Input label="Reorder threshold" value={threshold} onChange={setThreshold} type="number" placeholder="5" /></div>
                        <div style={{ flex: 1 }}><Input label="Unit cost ($)" value={cost} onChange={setCost} type="number" placeholder="12.50" /></div>
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                        <Btn variant="outline" onClick={() => { setShowAdd(false); resetAddForm(); }}>Cancel</Btn>
                        <Btn onClick={addItem} disabled={saving} fullWidth>{saving ? 'Adding...' : 'Add item'}</Btn>
                    </div>
                </Card>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {items.map(i => (
                    <Card key={i.id} style={{ borderLeft: i.isLowStock ? '4px solid #9B1C1C' : undefined }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontWeight: 700, fontSize: 15 }}>{i.name}</span>
                                    {i.category && <Badge label={i.category} />}
                                    {i.isLowStock && (
                                        <span style={{ fontSize: 11, background: '#FDECEA', color: '#9B1C1C', borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>
                                            ⚠ Reorder
                                        </span>
                                    )}
                                </div>
                                <div style={{ color: C.textSub, fontSize: 13, marginTop: 4 }}>
                                    <strong style={{ color: C.text }}>{i.quantityOnHand}</strong> on hand
                                    {' · '}reorder at {i.reorderThreshold}
                                    {' · '}${i.unitCost.toFixed(2)}/unit
                                </div>
                                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                    <Btn size="sm" variant="outline" onClick={() => { setAdjusting(adjusting === i.id ? null : i.id); setAdjustDelta(''); setAdjustReason(''); }}>
                                        {adjusting === i.id ? 'Cancel' : 'Adjust'}
                                    </Btn>
                                    <Btn size="sm" variant="outline" onClick={() => viewHistory(i.id)}>
                                        {historyFor === i.id ? 'Hide history' : 'History'}
                                    </Btn>
                                </div>
                                {adjusting === i.id && (
                                    <div style={{ marginTop: 10, padding: 10, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                                        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                                            <input value={adjustDelta} onChange={e => setAdjustDelta(e.target.value)} type="number" placeholder="+10 or -3"
                                                style={{ flex: 1, padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13 }} />
                                            <input value={adjustReason} onChange={e => setAdjustReason(e.target.value)} placeholder="Reason (e.g. restock, damaged)"
                                                style={{ flex: 2, padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13 }} />
                                        </div>
                                        <Btn size="sm" onClick={() => submitAdjust(i.id)}>Save adjustment</Btn>
                                    </div>
                                )}
                                {historyFor === i.id && (
                                    <div style={{ marginTop: 10, padding: 10, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                                        {history.length === 0 ? (
                                            <div style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>No usage history yet.</div>
                                        ) : history.map(h => (
                                            <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: `1px dashed ${C.borderLight}` }}>
                                                <span>{h.jobId ? 'Used on job' : h.reason}</span>
                                                <span style={{ fontWeight: 600, color: h.quantityChange < 0 ? '#9B1C1C' : C.green }}>
                                                    {h.quantityChange > 0 ? '+' : ''}{h.quantityChange} · {new Date(h.createdAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                ))}
                {items.length === 0 && <div style={{ color: C.textMuted, fontSize: 13 }}>No inventory items yet — add your first part or supply above.</div>}
            </div>
        </div>
    );
}

// ── Disputes panel ────────────────────────────────────────────────────────────

function DisputesPanel({ jobs, onResolved }: { jobs: JobListItem[]; onResolved: () => void }) {
    const branding = useBranding();
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
    const [adjustment, setAdjustment] = useState<any>(null);
    const [loadingAdj, setLoadingAdj] = useState(false);
    const [approving, setApproving] = useState(false);
    const [declining, setDeclining] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [chatMessages, setChatMessages] = useState<MessageItem[]>([]);
    const [loadingChat, setLoadingChat] = useState(false);

    const disputedJobs = jobs.filter(j => j.priceAdjustmentStatus === 'Disputed');
    // adjustedPrice/originalPrice are base price only — the amount actually captured either way
    // also carries forward the job's original service fee and tax unchanged (see
    // PriceAdjustmentService.CapturePaymentAsync / AdminDeclineAsync).
    const feeAndTax = adjustment ? (adjustment.serviceFeeAmount ?? 0) + (adjustment.taxAmount ?? 0) : 0;
    const newGrandTotal = adjustment ? (adjustment.adjustedPrice ?? 0) + feeAndTax : 0;
    const originalGrandTotal = adjustment ? (adjustment.originalPrice ?? 0) + feeAndTax : 0;

    const loadAdjustment = async (jobId: string) => {
        setSelectedJobId(jobId);
        setLoadingAdj(true);
        setError('');
        setAdjustment(null);
        setChatMessages([]);
        try {
            const adj = await api.getPriceAdjustment(jobId);
            setAdjustment(adj);
        } catch (e: any) { setError(e.message); }
        finally { setLoadingAdj(false); }

        setLoadingChat(true);
        try { setChatMessages(await api.getMessages(jobId)); }
        catch { /* chat is supplementary — a load failure here shouldn't block dispute review */ }
        finally { setLoadingChat(false); }
    };

    const approveAdjustment = async (jobId: string) => {
        if (!window.confirm('Approve the adjusted price and complete this job? Payment will be captured at the adjusted amount.')) return;
        setApproving(true); setError(''); setSuccess('');
        try {
            await api.adminApprovePriceAdjustment(jobId);
            setSuccess('Dispute resolved — job completed at adjusted price.');
            setSelectedJobId(null);
            setAdjustment(null);
            onResolved();
        } catch (e: any) { setError(e.message); }
        finally { setApproving(false); }
    };

    const declineAdjustment = async (jobId: string) => {
        if (!window.confirm(`Side with the customer and complete this job at the ORIGINAL price? The ${contractorLabel.toLowerCase()} will not receive the additional charge.`)) return;
        setDeclining(true); setError(''); setSuccess('');
        try {
            await api.adminDeclinePriceAdjustment(jobId);
            setSuccess('Dispute resolved — job completed at original price.');
            setSelectedJobId(null);
            setAdjustment(null);
            onResolved();
        } catch (e: any) { setError(e.message); }
        finally { setDeclining(false); }
    };

    return (
        <div>
            <Alert message={error} />
            <Alert message={success} type="success" />

            {disputedJobs.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 48 }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>No open disputes</div>
                    <div style={{ color: C.textSub, marginTop: 6 }}>
                        Price adjustment disputes will appear here when customers and {contractorLabel.toLowerCase()}s disagree on unit counts.
                    </div>
                </Card>
            ) : (
                <>
                    <div style={{ background: '#FFF7E6', border: '1px solid #EF9F27', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#854F0B' }}>
                        ⚠ {disputedJobs.length} dispute{disputedJobs.length > 1 ? 's' : ''} require your review. 
                        The {contractorLabel.toLowerCase()} and customer disagree on the unit count — review the details and approve the adjusted price to resolve.
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {disputedJobs.map(job => (
                            <Card key={job.id} style={{ borderLeft: `4px solid #EF9F27` }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                                            <Badge label={job.serviceType} />
                                            <span style={{ background: '#FFF7E6', color: '#854F0B', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
                                                ⚠ Disputed
                                            </span>
                                        </div>
                                        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>{job.pickupAddress}</div>
                                        <div style={{ fontSize: 13, color: C.textSub }}>
                                            Customer: <strong>{job.customerName}</strong>
                                            {job.contractorName && <> · {contractorLabel}: <strong>{job.contractorName}</strong></>}
                                        </div>
                                        <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>
                                            Original total: <strong>${job.totalPrice.toFixed(2)}</strong>
                                            {job.actualUnits && job.estimatedUnits && (
                                                <> · Est: <strong>{job.estimatedUnits}</strong> units → Actual: <strong style={{ color: '#C0392B' }}>{job.actualUnits}</strong> units</>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                                            {new Date(job.createdAt).toLocaleDateString()}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                        <Btn
                                            size="sm"
                                            variant="outline"
                                            onClick={() => selectedJobId === job.id ? setSelectedJobId(null) : loadAdjustment(job.id)}
                                        >
                                            {selectedJobId === job.id ? 'Hide details' : 'View details'}
                                        </Btn>
                                        <Btn
                                            size="sm"
                                            variant="outline"
                                            onClick={() => declineAdjustment(job.id)}
                                            disabled={declining}
                                        >
                                            {declining ? 'Processing...' : '✗ Side with customer'}
                                        </Btn>
                                        <Btn
                                            size="sm"
                                            onClick={() => approveAdjustment(job.id)}
                                            disabled={approving}
                                        >
                                            {approving ? 'Approving...' : '✓ Approve adjusted price'}
                                        </Btn>
                                    </div>
                                </div>

                                {/* Expanded adjustment details */}
                                {selectedJobId === job.id && (
                                    <div style={{ marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
                                        {loadingAdj ? (
                                            <Spinner />
                                        ) : adjustment ? (
                                            <div>
                                                {/* Unit comparison */}
                                                <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
                                                    <div style={{ flex: 1, background: C.greenBg, borderRadius: 10, padding: 16, textAlign: 'center', minWidth: 120 }}>
                                                        <div style={{ fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>AI Estimated</div>
                                                        <div style={{ fontSize: 36, fontWeight: 800, color: C.green }}>{adjustment.estimatedUnits}</div>
                                                        <div style={{ fontSize: 12, color: C.textSub }}>units</div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', fontSize: 24, color: C.textMuted }}>→</div>
                                                    <div style={{ flex: 1, background: '#FFF7E6', borderRadius: 10, padding: 16, textAlign: 'center', minWidth: 120 }}>
                                                        <div style={{ fontSize: 11, color: '#854F0B', textTransform: 'uppercase', letterSpacing: 0.5 }}>{contractorLabel} Counted</div>
                                                        <div style={{ fontSize: 36, fontWeight: 800, color: '#C0392B' }}>{adjustment.actualUnits}</div>
                                                        <div style={{ fontSize: 12, color: C.textSub }}>units</div>
                                                    </div>
                                                </div>

                                                {/* Price breakdown */}
                                                <div style={{ background: C.bg, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
                                                        <span style={{ color: C.textSub }}>Original price</span>
                                                        <span style={{ textDecoration: 'line-through', color: C.textMuted }}>${adjustment.originalPrice?.toFixed(2)}</span>
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 13 }}>
                                                        <span style={{ color: C.textSub }}>Additional charge</span>
                                                        <span style={{ color: '#C0392B', fontWeight: 600 }}>+${adjustment.difference?.toFixed(2)}</span>
                                                    </div>
                                                    {adjustment.serviceFeeAmount > 0 && (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 13 }}>
                                                            <span style={{ color: C.textSub }}>Service fee</span>
                                                            <span>${adjustment.serviceFeeAmount.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                    {adjustment.taxAmount > 0 && (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 13 }}>
                                                            <span style={{ color: C.textSub }}>Tax</span>
                                                            <span>${adjustment.taxAmount.toFixed(2)}</span>
                                                        </div>
                                                    )}
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: `1px solid ${C.border}`, paddingTop: 12, fontSize: 15, fontWeight: 700 }}>
                                                        <span>Adjusted total</span>
                                                        <span style={{ color: C.green }}>${newGrandTotal.toFixed(2)}</span>
                                                    </div>
                                                </div>

                                                {/* Dispute reason if any */}
                                                {adjustment.notes && (
                                                    <div style={{ background: '#FDECEA', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                                                        <div style={{ fontSize: 11, fontWeight: 600, color: '#9B1C1C', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                                                            Customer dispute reason
                                                        </div>
                                                        <div style={{ fontSize: 13, color: C.text }}>{adjustment.notes}</div>
                                                    </div>
                                                )}

                                                {/* Chat history — read-only, for auditing what was said between the customer
                                                    and the contractor before deciding the dispute. */}
                                                <div style={{ marginBottom: 16 }}>
                                                    <div style={{ fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                                                        Chat history
                                                    </div>
                                                    {loadingChat ? (
                                                        <Spinner />
                                                    ) : chatMessages.length === 0 ? (
                                                        <div style={{ fontSize: 13, color: C.textMuted, fontStyle: 'italic' }}>No messages were exchanged on this job.</div>
                                                    ) : (
                                                        <div style={{ background: C.bg, borderRadius: 8, padding: 12, maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                            {chatMessages.map(m => (
                                                                <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: m.senderRole === 'Customer' ? 'flex-start' : 'flex-end' }}>
                                                                    <div style={{
                                                                        maxWidth: '80%', borderRadius: 10, padding: '8px 12px', fontSize: 13,
                                                                        background: m.senderRole === 'Customer' ? '#fff' : C.greenBg,
                                                                        border: `1px solid ${C.border}`,
                                                                    }}>
                                                                        {m.text}
                                                                    </div>
                                                                    <div style={{ fontSize: 10, color: C.textMuted, marginTop: 2 }}>
                                                                        {m.senderRole === 'Customer' ? 'Customer' : contractorLabel} · {new Date(m.sentAt).toLocaleString()}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Admin action */}
                                                <div style={{ background: C.greenBg, borderRadius: 8, padding: 12, fontSize: 13, color: C.textSub, marginBottom: 16 }}>
                                                    {/* The {contractorLabel}'s actual payout depends on their commission/tier settings, not a
                                                        flat 80% — asserting that number here was a guess that could be wrong for any
                                                        contractor not on that exact rate. Payout is computed automatically at completion. */}
                                                    Approving will complete the job and capture payment at <strong>${newGrandTotal.toFixed(2)}</strong>.
                                                    The {contractorLabel.toLowerCase()}'s payout will be calculated automatically per their payout settings.
                                                </div>

                                                <div style={{ display: 'flex', gap: 10 }}>
                                                    <Btn
                                                        variant="outline"
                                                        onClick={() => declineAdjustment(job.id)}
                                                        disabled={declining}
                                                        fullWidth
                                                    >
                                                        {declining ? 'Processing...' : `✗ Side with customer — $${originalGrandTotal.toFixed(2)}`}
                                                    </Btn>
                                                    <Btn
                                                        onClick={() => approveAdjustment(job.id)}
                                                        disabled={approving}
                                                        fullWidth
                                                    >
                                                        {approving ? 'Processing...' : `✓ Approve — $${newGrandTotal.toFixed(2)}`}
                                                    </Btn>
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{ color: C.textMuted, fontSize: 13 }}>Could not load adjustment details.</div>
                                        )}
                                    </div>
                                )}
                            </Card>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

// ── Background Checks Panel ───────────────────────────────────────────────────

interface ContractorBgCheckItem {
    contractorId: string;
    name: string;
    email: string;
    backgroundCheckStatus: string;
    checkrCandidateId?: string;
    checkrReportId?: string;
    clearedAt?: string;
    expiresAt?: string;
    createdAt: string;
    notes?: string;
}

const bgStatusColor = (s: string) => {
    switch (s) {
        case 'Clear':         return { bg: '#E1F5EE', color: '#085041' };
        case 'CheckrPending': return { bg: '#EEF2FF', color: '#3730A3' };
        case 'Invited':       return { bg: '#EEF2FF', color: '#3730A3' };
        case 'Consider':      return { bg: '#FFF7E6', color: '#854F0B' };
        case 'Disputed':      return { bg: '#FFF7E6', color: '#854F0B' };
        case 'Suspended':     return { bg: '#FDECEA', color: '#9B1C1C' };
        default:              return { bg: '#F5F5F5', color: '#666' };
    }
};

const bgStatusIcon = (s: string) => {
    switch (s) {
        case 'Clear':         return '✓';
        case 'CheckrPending': return '⏳';
        case 'Invited':       return '📧';
        case 'Consider':      return '⚠';
        case 'Disputed':      return '⚖';
        case 'Suspended':     return '✗';
        default:              return '○';
    }
};

function BackgroundChecksPanel({ checks, loading, onRefresh }: {
    checks: ContractorBgCheckItem[];
    loading: boolean;
    onRefresh: () => void;
}) {
    const branding = useBranding();
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const contractorLabelPlural = branding.contractorNoun ? branding.contractorNoun + 's' : 'Contractors';
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [notes, setNotes] = useState<Record<string, string>>({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [filterStatus, setFilterStatus] = useState('All');

    const statuses = ['All', 'Pending', 'Invited', 'CheckrPending', 'Consider', 'Clear', 'Suspended', 'Disputed'];

    const filtered = filterStatus === 'All'
        ? checks
        : checks.filter(c => c.backgroundCheckStatus === filterStatus);

    const counts = {
        pending: checks.filter(c => c.backgroundCheckStatus === 'Pending').length,
        consider: checks.filter(c => c.backgroundCheckStatus === 'Consider').length,
        clear: checks.filter(c => c.backgroundCheckStatus === 'Clear').length,
        suspended: checks.filter(c => c.backgroundCheckStatus === 'Suspended').length,
    };

    const doAction = async (action: () => Promise<any>, contractorId: string) => {
        setActionLoading(contractorId);
        setError(''); setSuccess('');
        try {
            await action();
            setSuccess('Action completed successfully.');
            onRefresh();
        } catch (e: any) { setError(e.message); }
        finally { setActionLoading(null); }
    };

    const invite = (contractorId: string) =>
        doAction(() => api.adminInviteBackgroundCheck(contractorId), contractorId);

    const approve = (contractorId: string) =>
        doAction(() => api.adminApproveBackgroundCheck(contractorId, notes[contractorId] ?? ''), contractorId);

    const suspend = (contractorId: string) => {
        if (!window.confirm('This will send a pre-adverse action notice first (FCRA requirement). Continue?')) return;
        doAction(() => api.adminSuspendBackgroundCheck(contractorId, notes[contractorId] ?? ''), contractorId);
    };

    const rerun = (contractorId: string) => {
        if (!window.confirm(`Re-run background check? This will send a new Checkr invitation to the ${contractorLabel.toLowerCase()}.`)) return;
        doAction(() => api.adminRerunBackgroundCheck(contractorId), contractorId);
    };

    return (
        <div>
            <Alert message={error} />
            {success && (
                <div style={{ background: '#E1F5EE', border: `1px solid ${C.greenMid}`, borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: C.green }}>
                    ✓ {success}
                </div>
            )}

            {/* Summary stats */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
                {[
                    { label: 'Pending setup', value: counts.pending, color: C.textMuted },
                    { label: 'Needs review', value: counts.consider, color: '#854F0B' },
                    { label: 'Cleared', value: counts.clear, color: C.greenMid },
                    { label: 'Suspended', value: counts.suspended, color: C.coral },
                ].map(s => (
                    <div key={s.label} style={{ flex: 1, minWidth: 110, background: '#fff', borderRadius: 12, padding: 16, textAlign: 'center', border: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Filter pills */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {statuses.map(s => (
                    <button key={s} onClick={() => setFilterStatus(s)} style={{
                        padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                        border: `1px solid ${filterStatus === s ? C.greenMid : C.border}`,
                        background: filterStatus === s ? C.greenBg : '#fff',
                        color: filterStatus === s ? C.green : C.textSub,
                    }}>
                        {s} {s !== 'All' && `(${checks.filter(c => c.backgroundCheckStatus === s).length})`}
                    </button>
                ))}
                <Btn size="sm" variant="outline" onClick={onRefresh} style={{ marginLeft: 'auto' }}>↻ Refresh</Btn>
            </div>

            {loading ? <Spinner /> : filtered.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 48 }}>
                    <div style={{ fontSize: 40 }}>🛡</div>
                    <div style={{ fontSize: 16, fontWeight: 600, marginTop: 12 }}>No {contractorLabelPlural.toLowerCase()} in this filter</div>
                </Card>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {filtered.map(c => {
                        const statusStyle = bgStatusColor(c.backgroundCheckStatus);
                        const isLoading = actionLoading === c.contractorId;
                        const isConsider = c.backgroundCheckStatus === 'Consider';
                        const isPending = c.backgroundCheckStatus === 'Pending';
                        const isClear = c.backgroundCheckStatus === 'Clear';
                        const expiresSOon = c.expiresAt && new Date(c.expiresAt) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

                        return (
                            <Card key={c.contractorId} style={{ borderLeft: `4px solid ${statusStyle.color}` }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                            <div style={{ fontWeight: 700, fontSize: 15 }}>{c.name}</div>
                                            <span style={{ ...statusStyle, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
                                                {bgStatusIcon(c.backgroundCheckStatus)} {c.backgroundCheckStatus}
                                            </span>
                                            {expiresSOon && (
                                                <span style={{ background: '#FFF7E6', color: '#854F0B', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                                                    ⚠ Expiring soon
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 13, color: C.textSub }}>{c.email}</div>
                                        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                            <span>Joined: {new Date(c.createdAt).toLocaleDateString()}</span>
                                            {c.clearedAt && <span>Cleared: {new Date(c.clearedAt).toLocaleDateString()}</span>}
                                            {c.expiresAt && <span>Expires: {new Date(c.expiresAt).toLocaleDateString()}</span>}
                                            {c.checkrReportId && <span style={{ fontFamily: 'monospace' }}>Report: {c.checkrReportId.slice(0, 12)}…</span>}
                                        </div>
                                        {c.notes && (
                                            <div style={{ marginTop: 8, fontSize: 12, color: C.textSub, background: C.bg, borderRadius: 6, padding: '6px 10px' }}>
                                                📝 {c.notes}
                                            </div>
                                        )}
                                    </div>

                                    {/* Action buttons */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', minWidth: 160 }}>
                                        {isPending && (
                                            <Btn size="sm" onClick={() => invite(c.contractorId)} disabled={isLoading}>
                                                {isLoading ? 'Sending...' : '📧 Send invitation'}
                                            </Btn>
                                        )}
                                        {isConsider && (
                                            <>
                                                <Btn size="sm" onClick={() => approve(c.contractorId)} disabled={isLoading}>
                                                    {isLoading ? '...' : '✓ Approve'}
                                                </Btn>
                                                <Btn size="sm" variant="outline" onClick={() => suspend(c.contractorId)} disabled={isLoading}>
                                                    {isLoading ? '...' : '✗ Decline'}
                                                </Btn>
                                            </>
                                        )}
                                        {(isClear || expiresSOon) && (
                                            <Btn size="sm" variant="outline" onClick={() => rerun(c.contractorId)} disabled={isLoading}>
                                                {isLoading ? '...' : '↻ Re-run check'}
                                            </Btn>
                                        )}
                                        {c.backgroundCheckStatus === 'Suspended' && (
                                            <Btn size="sm" onClick={() => approve(c.contractorId)} disabled={isLoading}>
                                                {isLoading ? '...' : '↩ Reinstate'}
                                            </Btn>
                                        )}
                                    </div>
                                </div>

                                {/* Notes input for Consider actions */}
                                {isConsider && (
                                    <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                                        <label style={{ fontSize: 12, color: C.textSub, fontWeight: 500 }}>Admin notes (optional)</label>
                                        <input
                                            type="text"
                                            value={notes[c.contractorId] ?? ''}
                                            onChange={e => setNotes(prev => ({ ...prev, [c.contractorId]: e.target.value }))}
                                            placeholder="Reason for approval or decline..."
                                            style={{ width: '100%', marginTop: 6, padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' as const }}
                                        />
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── Documents Panel ───────────────────────────────────────────────────────────

interface ContractorDocumentItem {
    contractorId: string;
    name: string;
    email: string;
    driversLicenseStatus: string;
    driversLicenseExpiry?: string;
    driversLicensePhotoUrl?: string;
    driversLicenseNumber?: string;
    driversLicenseRejectionReason?: string;
    insuranceStatus: string;
    insuranceExpiry?: string;
    insurancePhotoUrl?: string;
    insuranceProvider?: string;
    insurancePolicyNumber?: string;
    insuranceRejectionReason?: string;
    vehicleStatus: string;
    vehicleMake?: string;
    vehicleModel?: string;
    vehicleYear?: number;
    vehicleColor?: string;
    vehiclePlate?: string;
    vehiclePhotoUrl?: string;
    vehicleRejectionReason?: string;
    onboardingStatus: string;
    createdAt: string;
}

const getDocImageUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    // Legacy relative path — prefix with API base
    return `${API_BASE}${url}`;
};

const docStatusStyle = (s: string) => {
    switch (s) {
        case 'Approved':   return { bg: C.greenBg, color: C.green, icon: '✓' };
        case 'Submitted':  return { bg: '#EEF2FF', color: '#3730A3', icon: '⏳' };
        case 'Rejected':   return { bg: '#FDECEA', color: '#9B1C1C', icon: '✗' };
        case 'Expired':    return { bg: '#FFF7E6', color: '#854F0B', icon: '⚠' };
        default:           return { bg: C.bg, color: C.textMuted, icon: '○' };
    }
};

function DocBadge({ status }: { status: string }) {
    const s = docStatusStyle(status);
    return (
        <span style={{ background: s.bg, color: s.color, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
            {s.icon} {status}
        </span>
    );
}

function DocumentsPanel({ documents, loading, expiringDocs, onRefresh }: {
    documents: ContractorDocumentItem[];
    loading: boolean;
    expiringDocs: ContractorDocumentItem[];
    onRefresh: () => void;
}) {
    const branding = useBranding();
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const contractorLabelPlural = branding.contractorNoun ? branding.contractorNoun + 's' : 'Contractors';
    const [filterStatus, setFilterStatus] = useState('All');
    const [expanded, setExpanded] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectTarget, setRejectTarget] = useState<{ id: string; type: string } | null>(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const statuses = ['All', 'Incomplete', 'UnderReview', 'Approved', 'Suspended'];

    const filtered = filterStatus === 'All'
        ? documents
        : documents.filter(d => d.onboardingStatus === filterStatus);

    const pendingCount = documents.filter(d =>
        d.driversLicenseStatus === 'Submitted' ||
        d.insuranceStatus === 'Submitted' ||
        d.vehicleStatus === 'Submitted'
    ).length;

    const doAction = async (action: () => Promise<any>, key: string) => {
        setActionLoading(key);
        setError(''); setSuccess('');
        try {
            await action();
            setSuccess('Action completed.');
            onRefresh();
        } catch (e: any) { setError(e.message); }
        finally { setActionLoading(null); }
    };

    return (
        <div>
            <Alert message={error} />
            {success && <div style={{ background: C.greenBg, border: `1px solid ${C.greenMid}`, borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: C.green }}>✓ {success}</div>}

            {/* Summary stats */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
                {[
                    { label: 'Pending review', value: pendingCount, color: '#3730A3', bg: '#EEF2FF' },
                    { label: 'Fully approved', value: documents.filter(d => d.onboardingStatus === 'Approved').length, color: C.greenMid, bg: C.greenBg },
                    { label: 'Incomplete', value: documents.filter(d => d.onboardingStatus === 'Incomplete').length, color: C.textMuted, bg: C.bg },
                    { label: 'Expiring soon', value: expiringDocs.length, color: '#854F0B', bg: '#FFF7E6' },
                ].map(s => (
                    <div key={s.label} style={{ flex: 1, minWidth: 110, background: s.bg, borderRadius: 12, padding: 16, textAlign: 'center', border: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Filter pills */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                {statuses.map(s => (
                    <button key={s} onClick={() => setFilterStatus(s)} style={{
                        padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                        border: `1px solid ${filterStatus === s ? C.greenMid : C.border}`,
                        background: filterStatus === s ? C.greenBg : '#fff',
                        color: filterStatus === s ? C.green : C.textSub,
                    }}>
                        {s}
                    </button>
                ))}
                <Btn size="sm" variant="outline" onClick={onRefresh} style={{ marginLeft: 'auto' }}>↻ Refresh</Btn>
            </div>

            {/* Reject modal */}
            {rejectTarget && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 400, maxWidth: '90vw' }}>
                        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>
                            {['Approved', 'Expired'].includes(
                                rejectTarget.type === 'License' ? (filtered.find((d: any) => d.contractorId === rejectTarget.id)?.driversLicenseStatus || '') :
                                rejectTarget.type === 'Insurance' ? (filtered.find((d: any) => d.contractorId === rejectTarget.id)?.insuranceStatus || '') :
                                (filtered.find((d: any) => d.contractorId === rejectTarget.id)?.vehicleStatus || '')
                            ) ? `Request ${rejectTarget.type} resubmission` : `Reject ${rejectTarget.type}`}
                        </div>
                        <label style={{ fontSize: 13, color: C.textSub, fontWeight: 500 }}>Reason (shown to {contractorLabel.toLowerCase()})</label>
                        <input type="text" value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                            placeholder="e.g. Photo is blurry, document is expired..."
                            style={{ width: '100%', marginTop: 6, marginBottom: 16, padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' as const }} />
                        <div style={{ display: 'flex', gap: 10 }}>
                            <Btn variant="outline" onClick={() => { setRejectTarget(null); setRejectReason(''); }} fullWidth>Cancel</Btn>
                            <Btn onClick={async () => {
                                const { id, type } = rejectTarget;
                                const fn = type === 'License' ? () => api.rejectLicense(id, rejectReason)
                                    : type === 'Insurance' ? () => api.rejectInsurance(id, rejectReason)
                                    : () => api.rejectVehicle(id, rejectReason);
                                await doAction(fn, `${id}-${type}-reject`);
                                setRejectTarget(null); setRejectReason('');
                            }} fullWidth>Reject</Btn>
                        </div>
                    </div>
                </div>
            )}

            {loading ? <Spinner /> : filtered.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 48 }}>
                    <div style={{ fontSize: 40 }}>📋</div>
                    <div style={{ fontSize: 16, fontWeight: 600, marginTop: 12 }}>No {contractorLabelPlural.toLowerCase()} in this filter</div>
                </Card>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {filtered.map(d => {
                        const isOpen = expanded === d.contractorId;
                        const licDays = d.driversLicenseExpiry ? Math.ceil((new Date(d.driversLicenseExpiry).getTime() - Date.now()) / 86400000) : null;
                        const insDays = d.insuranceExpiry ? Math.ceil((new Date(d.insuranceExpiry).getTime() - Date.now()) / 86400000) : null;
                        const onboardStyle = d.onboardingStatus === 'Approved' ? C.green
                            : d.onboardingStatus === 'UnderReview' ? '#3730A3'
                            : d.onboardingStatus === 'Suspended' ? C.coral : C.textMuted;

                        return (
                            <Card key={d.contractorId} style={{ borderLeft: `4px solid ${onboardStyle}` }}>
                                {/* Header row */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                                            <span style={{ fontWeight: 700, fontSize: 15 }}>{d.name}</span>
                                            <span style={{ fontSize: 11, background: d.onboardingStatus === 'Approved' ? C.greenBg : d.onboardingStatus === 'UnderReview' ? '#EEF2FF' : C.bg, color: onboardStyle, borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>
                                                {d.onboardingStatus}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: 13, color: C.textSub, marginBottom: 6 }}>{d.email}</div>
                                        {/* Document status row */}
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <span style={{ fontSize: 11, color: C.textMuted }}>🪪</span>
                                                <DocBadge status={d.driversLicenseStatus} />
                                                {licDays !== null && licDays <= 30 && <span style={{ fontSize: 11, color: licDays <= 7 ? C.coral : '#854F0B', fontWeight: 600 }}>{licDays}d</span>}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <span style={{ fontSize: 11, color: C.textMuted }}>📄</span>
                                                <DocBadge status={d.insuranceStatus} />
                                                {insDays !== null && insDays <= 30 && <span style={{ fontSize: 11, color: insDays <= 7 ? C.coral : '#854F0B', fontWeight: 600 }}>{insDays}d</span>}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <span style={{ fontSize: 11, color: C.textMuted }}>🚗</span>
                                                <DocBadge status={d.vehicleStatus} />
                                            </div>
                                        </div>
                                    </div>
                                    <Btn size="sm" variant="outline" onClick={() => setExpanded(isOpen ? null : d.contractorId)}>
                                        {isOpen ? 'Collapse' : 'Review documents'}
                                    </Btn>
                                </div>

                                {/* Expanded document review */}
                                {isOpen && (
                                    <div style={{ marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 20 }}>

                                        {/* Driver's License */}
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                🪪 Driver's License <DocBadge status={d.driversLicenseStatus} />
                                            </div>
                                            {d.driversLicensePhotoUrl && (
                                                <img src={getDocImageUrl(d.driversLicensePhotoUrl)} alt="License" style={{ maxHeight: 160, borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 8 }} />
                                            )}
                                            <div style={{ fontSize: 12, color: C.textSub, marginBottom: 8 }}>
                                                {d.driversLicenseNumber && <div>License #: <strong>{d.driversLicenseNumber}</strong></div>}
                                                {d.driversLicenseExpiry && <div>Expires: <strong style={{ color: licDays !== null && licDays <= 30 ? C.coral : C.text }}>{new Date(d.driversLicenseExpiry).toLocaleDateString()}{licDays !== null && licDays <= 30 ? ` (${licDays} days)` : ''}</strong></div>}
                                                {d.driversLicenseRejectionReason && <div style={{ color: C.coral, marginTop: 4 }}>Reason: {d.driversLicenseRejectionReason}</div>}
                                            </div>
                                            {d.driversLicenseStatus === 'Submitted' && (
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <Btn size="sm" onClick={() => doAction(() => api.approveLicense(d.contractorId), `${d.contractorId}-lic-approve`)} disabled={actionLoading !== null}>✓ Approve</Btn>
                                                    <Btn size="sm" variant="outline" onClick={() => setRejectTarget({ id: d.contractorId, type: 'License' })}>✗ Reject</Btn>
                                                </div>
                                            )}
                                            {(d.driversLicenseStatus === 'Approved' || d.driversLicenseStatus === 'Expired') && (
                                                <Btn size="sm" variant="outline" onClick={() => setRejectTarget({ id: d.contractorId, type: 'License' })}>
                                                    ↩ Request resubmission
                                                </Btn>
                                            )}
                                        </div>

                                        {/* Insurance */}
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                📄 Proof of Insurance <DocBadge status={d.insuranceStatus} />
                                            </div>
                                            {d.insurancePhotoUrl && (
                                                <img src={getDocImageUrl(d.insurancePhotoUrl)} alt="Insurance" style={{ maxHeight: 160, borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 8 }} />
                                            )}
                                            <div style={{ fontSize: 12, color: C.textSub, marginBottom: 8 }}>
                                                {d.insuranceProvider && <div>Provider: <strong>{d.insuranceProvider}</strong></div>}
                                                {d.insurancePolicyNumber && <div>Policy #: <strong>{d.insurancePolicyNumber}</strong></div>}
                                                {d.insuranceExpiry && <div>Expires: <strong style={{ color: insDays !== null && insDays <= 30 ? C.coral : C.text }}>{new Date(d.insuranceExpiry).toLocaleDateString()}{insDays !== null && insDays <= 30 ? ` (${insDays} days)` : ''}</strong></div>}
                                                {d.insuranceRejectionReason && <div style={{ color: C.coral, marginTop: 4 }}>Reason: {d.insuranceRejectionReason}</div>}
                                            </div>
                                            {d.insuranceStatus === 'Submitted' && (
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <Btn size="sm" onClick={() => doAction(() => api.approveInsurance(d.contractorId), `${d.contractorId}-ins-approve`)} disabled={actionLoading !== null}>✓ Approve</Btn>
                                                    <Btn size="sm" variant="outline" onClick={() => setRejectTarget({ id: d.contractorId, type: 'Insurance' })}>✗ Reject</Btn>
                                                </div>
                                            )}
                                            {(d.insuranceStatus === 'Approved' || d.insuranceStatus === 'Expired') && (
                                                <Btn size="sm" variant="outline" onClick={() => setRejectTarget({ id: d.contractorId, type: 'Insurance' })}>
                                                    ↩ Request resubmission
                                                </Btn>
                                            )}
                                        </div>

                                        {/* Vehicle */}
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                🚗 Vehicle Information <DocBadge status={d.vehicleStatus} />
                                            </div>
                                            {d.vehiclePhotoUrl && (
                                                <img src={getDocImageUrl(d.vehiclePhotoUrl)} alt="Vehicle" style={{ maxHeight: 160, borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 8 }} />
                                            )}
                                            <div style={{ fontSize: 12, color: C.textSub, marginBottom: 8 }}>
                                                {d.vehicleYear && d.vehicleMake && <div>Vehicle: <strong>{d.vehicleYear} {d.vehicleMake} {d.vehicleModel}</strong></div>}
                                                {d.vehicleColor && <div>Color: <strong>{d.vehicleColor}</strong></div>}
                                                {d.vehiclePlate && <div>Plate: <strong>{d.vehiclePlate}</strong></div>}
                                                {d.vehicleRejectionReason && <div style={{ color: C.coral, marginTop: 4 }}>Reason: {d.vehicleRejectionReason}</div>}
                                            </div>
                                            {d.vehicleStatus === 'Submitted' && (
                                                <div style={{ display: 'flex', gap: 8 }}>
                                                    <Btn size="sm" onClick={() => doAction(() => api.approveVehicle(d.contractorId), `${d.contractorId}-veh-approve`)} disabled={actionLoading !== null}>✓ Approve</Btn>
                                                    <Btn size="sm" variant="outline" onClick={() => setRejectTarget({ id: d.contractorId, type: 'Vehicle' })}>✗ Reject</Btn>
                                                </div>
                                            )}
                                            {(d.vehicleStatus === 'Approved' || d.vehicleStatus === 'Expired') && (
                                                <Btn size="sm" variant="outline" onClick={() => setRejectTarget({ id: d.contractorId, type: 'Vehicle' })}>
                                                    ↩ Request resubmission
                                                </Btn>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── Dumpster Admin Panel ──────────────────────────────────────────────────────

interface VendorFormErrors {
    companyName?: string;
    contactName?: string;
    email?: string;
    password?: string;
    serviceAreaAddress?: string;
    serviceAreaRadiusMiles?: string;
}

function DumpsterAdminPanel({ orders, vendors, loading, onRefresh }: {
    orders: any[]; vendors: any[]; loading: boolean; onRefresh: () => void;
}) {
    const [filter, setFilter] = useState('All');
    const [showAddVendor, setShowAddVendor] = useState(false);
    const [showAddSize, setShowAddSize] = useState<string | null>(null);
    const [vendorForm, setVendorForm] = useState({ companyName: '', contactName: '', email: '', password: '', phoneNumber: '', serviceAreaAddress: '', serviceAreaLat: '34.7304', serviceAreaLng: '-86.5861', serviceAreaRadiusMiles: '25' });
    const [vendorFormErrors, setVendorFormErrors] = useState<VendorFormErrors>({});
    const [sizeForm, setSizeForm] = useState({ yards: '10', description: '', deliveryPrice: '', dailyRate: '', maxRentalDays: '14' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Edit vendor
    const [editVendorId, setEditVendorId] = useState<string | null>(null);
    const [editVendorForm, setEditVendorForm] = useState({ companyName: '', contactName: '', phoneNumber: '', serviceAreaAddress: '', serviceAreaLat: '', serviceAreaLng: '', serviceAreaRadiusMiles: '' });
    const [editVendorErrors, setEditVendorErrors] = useState<Omit<VendorFormErrors, 'email' | 'password'>>({});
    const [editSaving, setEditSaving] = useState(false);

    // Edit vendor payout rate
    const [rateEditId, setRateEditId] = useState<string | null>(null);
    const [rateForm, setRateForm] = useState({ rateType: 'PercentageSplit', rateAmount: '' });
    const [rateSaving, setRateSaving] = useState(false);

    // Vendor docs
    const [vendorDocsMap, setVendorDocsMap] = useState<Record<string, VendorDocumentItem>>({});
    const [expandedVendorDocs, setExpandedVendorDocs] = useState<string | null>(null);
    const [docActionLoading, setDocActionLoading] = useState<string | null>(null);
    const [vendorDocRejectTarget, setVendorDocRejectTarget] = useState<{ vendorId: string; docType: string } | null>(null);
    const [vendorDocRejectReason, setVendorDocRejectReason] = useState('');
    const [docSuccess, setDocSuccess] = useState('');

    const orderStatuses = ['All', 'Pending', 'VendorAccepted', 'Delivered', 'PickupRequested', 'Completed', 'Cancelled'];
    const filtered = filter === 'All' ? orders : orders.filter(o => o.status === filter);

    const statusColor = (s: string) => {
        switch (s) {
            case 'Completed': return C.green;
            case 'Delivered': return C.greenMid;
            case 'VendorAccepted': case 'DeliveryScheduled': return '#3730A3';
            case 'PickupRequested': return '#854F0B';
            case 'Cancelled': return C.coral;
            default: return C.textMuted;
        }
    };

    const onboardBadgeStyle = (s: string) => {
        switch (s) {
            case 'Approved':    return { bg: C.greenBg, color: C.green };
            case 'UnderReview': return { bg: '#EEF2FF', color: '#3730A3' };
            case 'Suspended':   return { bg: '#FDECEA', color: '#9B1C1C' };
            default:            return { bg: C.bg, color: C.textMuted };
        }
    };

    // Load vendor documents once on mount / when vendors change
    useEffect(() => {
        api.getAllVendorDocuments().then((docs: VendorDocumentItem[]) => {
            const map: Record<string, VendorDocumentItem> = {};
            docs.forEach(d => { map[d.vendorId] = d; });
            setVendorDocsMap(map);
        }).catch(() => {});
    }, [vendors]);

    const validateVendorForm = (): VendorFormErrors => {
        const e: VendorFormErrors = {};
        if (!vendorForm.companyName.trim()) e.companyName = 'Company name is required';
        if (!vendorForm.contactName.trim()) e.contactName = 'Contact name is required';
        if (!vendorForm.email.trim()) e.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vendorForm.email)) e.email = 'Enter a valid email address';
        if (!vendorForm.password.trim()) e.password = 'Password is required';
        else if (vendorForm.password.length < 8) e.password = 'Password must be at least 8 characters';
        if (!vendorForm.serviceAreaAddress.trim()) e.serviceAreaAddress = 'Select a service area address from autocomplete';
        const r = parseFloat(vendorForm.serviceAreaRadiusMiles);
        if (!vendorForm.serviceAreaRadiusMiles || isNaN(r) || r <= 0) e.serviceAreaRadiusMiles = 'Enter a valid radius greater than 0';
        return e;
    };

    const validateEditForm = (): Omit<VendorFormErrors, 'email' | 'password'> => {
        const e: Omit<VendorFormErrors, 'email' | 'password'> = {};
        if (!editVendorForm.companyName.trim()) e.companyName = 'Company name is required';
        if (!editVendorForm.contactName.trim()) e.contactName = 'Contact name is required';
        if (!editVendorForm.serviceAreaAddress.trim()) e.serviceAreaAddress = 'Service area address is required';
        const r = parseFloat(editVendorForm.serviceAreaRadiusMiles);
        if (!editVendorForm.serviceAreaRadiusMiles || isNaN(r) || r <= 0) e.serviceAreaRadiusMiles = 'Enter a valid radius greater than 0';
        return e;
    };

    const addSize = async (vendorId: string) => {
        setSaving(true); setError('');
        try {
            await api.addDumpsterSize(vendorId, {
                yards: parseInt(sizeForm.yards),
                description: sizeForm.description,
                deliveryPrice: parseFloat(sizeForm.deliveryPrice),
                dailyRate: parseFloat(sizeForm.dailyRate),
                maxRentalDays: parseInt(sizeForm.maxRentalDays),
            });
            setSuccess('Size added!');
            setShowAddSize(null);
            setSizeForm({ yards: '10', description: '', deliveryPrice: '', dailyRate: '', maxRentalDays: '14' });
            onRefresh();
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const addVendor = async () => {
        const errs = validateVendorForm();
        if (Object.keys(errs).length > 0) { setVendorFormErrors(errs); return; }
        setSaving(true); setError(''); setVendorFormErrors({});
        try {
            await api.registerDumpsterVendor({
                ...vendorForm,
                serviceAreaLat: parseFloat(vendorForm.serviceAreaLat),
                serviceAreaLng: parseFloat(vendorForm.serviceAreaLng),
                serviceAreaRadiusMiles: parseFloat(vendorForm.serviceAreaRadiusMiles),
            });
            setSuccess('Vendor registered!');
            setShowAddVendor(false);
            setVendorForm({ companyName: '', contactName: '', email: '', password: '', phoneNumber: '', serviceAreaAddress: '', serviceAreaLat: '34.7304', serviceAreaLng: '-86.5861', serviceAreaRadiusMiles: '25' });
            onRefresh();
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const startEditVendor = (v: any) => {
        setEditVendorId(v.id);
        setEditVendorForm({
            companyName: v.companyName,
            contactName: v.contactName,
            phoneNumber: v.phoneNumber ?? '',
            serviceAreaAddress: v.serviceAreaAddress ?? '',
            serviceAreaLat: String(v.serviceAreaLat),
            serviceAreaLng: String(v.serviceAreaLng),
            serviceAreaRadiusMiles: String(v.serviceAreaRadiusMiles),
        });
        setEditVendorErrors({});
        setShowAddVendor(false);
    };

    const saveEditVendor = async (vendorId: string) => {
        const errs = validateEditForm();
        if (Object.keys(errs).length > 0) { setEditVendorErrors(errs); return; }
        setEditSaving(true); setError('');
        try {
            await api.updateDumpsterVendor(vendorId, {
                companyName: editVendorForm.companyName,
                contactName: editVendorForm.contactName,
                phoneNumber: editVendorForm.phoneNumber || undefined,
                serviceAreaAddress: editVendorForm.serviceAreaAddress || undefined,
                serviceAreaLat: parseFloat(editVendorForm.serviceAreaLat),
                serviceAreaLng: parseFloat(editVendorForm.serviceAreaLng),
                serviceAreaRadiusMiles: parseFloat(editVendorForm.serviceAreaRadiusMiles),
            });
            setSuccess('Vendor updated!');
            setEditVendorId(null);
            onRefresh();
        } catch (e: any) { setError(e.message); }
        finally { setEditSaving(false); }
    };

    const startEditRate = (v: any) => {
        setRateEditId(v.id);
        setRateForm({
            rateType: v.vendorRateType ?? 'PercentageSplit',
            rateAmount: v.vendorRateAmount != null ? String(v.vendorRateAmount) : '',
        });
    };

    const saveVendorRate = async (vendorId: string) => {
        setRateSaving(true); setError('');
        try {
            const amount = rateForm.rateAmount.trim() ? parseFloat(rateForm.rateAmount) : undefined;
            await api.adminUpdateVendorRate(vendorId, rateForm.rateType, amount);
            setSuccess('Payout rate updated!');
            setRateEditId(null);
            onRefresh();
        } catch (e: any) { setError(e.message); }
        finally { setRateSaving(false); }
    };

    const reviewDoc = async (vendorId: string, docType: string, approved: boolean, reason?: string) => {
        const key = `${vendorId}-${docType}`;
        setDocActionLoading(key);
        setDocSuccess(''); setError('');
        try {
            const updated = await api.reviewVendorDocument(vendorId, docType, approved, reason);
            setVendorDocsMap(prev => ({ ...prev, [vendorId]: updated }));
            setDocSuccess(approved ? `${docType} approved!` : `${docType} rejected.`);
            onRefresh();
        } catch (e: any) { setError(e.message); }
        finally { setDocActionLoading(null); }
    };

    const pendingDocVendorCount = Object.values(vendorDocsMap).filter(d =>
        d.businessLicenseStatus === 'Submitted' ||
        d.liabilityInsuranceStatus === 'Submitted' ||
        d.equipmentDocStatus === 'Submitted'
    ).length;

    return (
        <div>
            <Alert message={error} />
            {success && <div style={{ background: C.greenBg, border: `1px solid ${C.greenMid}`, borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: C.green }}>✓ {success}</div>}

            {/* Stats */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
                {[
                    { label: 'Total orders', value: orders.length, color: C.text, bg: C.bg },
                    { label: 'Active', value: orders.filter(o => !['Completed', 'Cancelled'].includes(o.status)).length, color: C.greenMid, bg: C.greenBg },
                    { label: 'Pending review', value: orders.filter(o => o.status === 'Pending').length, color: '#3730A3', bg: '#EEF2FF' },
                    { label: 'Revenue', value: `$${orders.filter(o => o.status === 'Completed').reduce((s: number, o: any) => s + o.totalCost, 0).toFixed(2)}`, color: C.greenMid, bg: C.greenBg },
                ].map(s => (
                    <div key={s.label} style={{ flex: 1, minWidth: 120, background: s.bg, borderRadius: 12, padding: 16, textAlign: 'center', border: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</div>
                        <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Pending vendor docs alert */}
            {pendingDocVendorCount > 0 && (
                <div style={{ background: '#EEF2FF', border: '1px solid #6366F1', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#3730A3', fontWeight: 500 }}>
                    ⏳ {pendingDocVendorCount} vendor{pendingDocVendorCount > 1 ? 's have' : ' has'} documents pending review — expand a vendor card below to review.
                </div>
            )}

            {/* Vendors section */}
            <Card style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>🚛 Vendors ({vendors.length})</h3>
                    <Btn size="sm" onClick={() => { setShowAddVendor(!showAddVendor); setEditVendorId(null); }}>+ Add Vendor</Btn>
                </div>

                {/* Add vendor form */}
                {showAddVendor && (
                    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16, marginBottom: 16 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            <div>
                                <Input label="Company name *" value={vendorForm.companyName} onChange={v => setVendorForm(f => ({ ...f, companyName: v }))} />
                                {vendorFormErrors.companyName && <div style={{ color: C.coral, fontSize: 12, marginTop: -8, marginBottom: 8 }}>{vendorFormErrors.companyName}</div>}
                            </div>
                            <div>
                                <Input label="Contact name *" value={vendorForm.contactName} onChange={v => setVendorForm(f => ({ ...f, contactName: v }))} />
                                {vendorFormErrors.contactName && <div style={{ color: C.coral, fontSize: 12, marginTop: -8, marginBottom: 8 }}>{vendorFormErrors.contactName}</div>}
                            </div>
                            <div>
                                <Input label="Email *" value={vendorForm.email} onChange={v => setVendorForm(f => ({ ...f, email: v }))} type="email" />
                                {vendorFormErrors.email && <div style={{ color: C.coral, fontSize: 12, marginTop: -8, marginBottom: 8 }}>{vendorFormErrors.email}</div>}
                            </div>
                            <div>
                                <Input label="Password *" value={vendorForm.password} onChange={v => setVendorForm(f => ({ ...f, password: v }))} type="password" />
                                {vendorFormErrors.password && <div style={{ color: C.coral, fontSize: 12, marginTop: -8, marginBottom: 8 }}>{vendorFormErrors.password}</div>}
                            </div>
                            <Input label="Phone (optional)" value={vendorForm.phoneNumber} onChange={v => setVendorForm(f => ({ ...f, phoneNumber: v }))} />
                            <div>
                                <Input label="Service radius (miles) *" value={vendorForm.serviceAreaRadiusMiles} onChange={v => setVendorForm(f => ({ ...f, serviceAreaRadiusMiles: v }))} type="number" />
                                {vendorFormErrors.serviceAreaRadiusMiles && <div style={{ color: C.coral, fontSize: 12, marginTop: -8, marginBottom: 8 }}>{vendorFormErrors.serviceAreaRadiusMiles}</div>}
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <AddressAutocomplete
                                    label="Service area center address *"
                                    value={vendorForm.serviceAreaAddress}
                                    onChange={({ address, lat, lng }) => setVendorForm(f => ({ ...f, serviceAreaAddress: address, serviceAreaLat: String(lat), serviceAreaLng: String(lng) }))}
                                    placeholder="Enter a city or address"
                                />
                                {vendorFormErrors.serviceAreaAddress && <div style={{ color: C.coral, fontSize: 12, marginTop: 2 }}>{vendorFormErrors.serviceAreaAddress}</div>}
                                {vendorForm.serviceAreaAddress && !vendorFormErrors.serviceAreaAddress && (
                                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{vendorForm.serviceAreaLat}, {vendorForm.serviceAreaLng}</div>
                                )}
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                            <Btn variant="outline" onClick={() => { setShowAddVendor(false); setVendorFormErrors({}); }}>Cancel</Btn>
                            <Btn onClick={addVendor} disabled={saving}>{saving ? 'Saving...' : 'Register vendor'}</Btn>
                        </div>
                    </div>
                )}

                {/* Vendor reject modal */}
                {vendorDocRejectTarget && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 420, maxWidth: '90vw' }}>
                            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>
                                Reject {vendorDocRejectTarget.docType === 'BusinessLicense' ? 'Business License' : vendorDocRejectTarget.docType === 'LiabilityInsurance' ? 'Liability Insurance' : 'Equipment/Vehicle Proof'}
                            </div>
                            <label style={{ fontSize: 13, color: C.textSub, fontWeight: 500 }}>Reason (shown to vendor)</label>
                            <input type="text" value={vendorDocRejectReason} onChange={e => setVendorDocRejectReason(e.target.value)}
                                placeholder="e.g. Photo is blurry, document is expired..."
                                style={{ width: '100%', marginTop: 6, marginBottom: 16, padding: '10px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, boxSizing: 'border-box' as const }} />
                            <div style={{ display: 'flex', gap: 10 }}>
                                <Btn variant="outline" onClick={() => { setVendorDocRejectTarget(null); setVendorDocRejectReason(''); }} fullWidth>Cancel</Btn>
                                <Btn onClick={async () => {
                                    const { vendorId, docType } = vendorDocRejectTarget;
                                    await reviewDoc(vendorId, docType, false, vendorDocRejectReason);
                                    setVendorDocRejectTarget(null); setVendorDocRejectReason('');
                                }} fullWidth>Reject</Btn>
                            </div>
                        </div>
                    </div>
                )}

                {vendors.length === 0 ? (
                    <div style={{ color: C.textMuted, fontSize: 13, padding: '12px 0' }}>No vendors registered yet.</div>
                ) : vendors.map((v: any) => {
                    const docs = vendorDocsMap[v.id];
                    const isEditing = editVendorId === v.id;
                    const isDocsOpen = expandedVendorDocs === v.id;
                    const obStyle = onboardBadgeStyle(v.onboardingStatus ?? 'Incomplete');
                    const hasPendingDocs = docs && (
                        docs.businessLicenseStatus === 'Submitted' ||
                        docs.liabilityInsuranceStatus === 'Submitted' ||
                        docs.equipmentDocStatus === 'Submitted'
                    );

                    return (
                        <div key={v.id} style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginTop: 14 }}>
                            {/* Vendor header row */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                                        <span style={{ fontWeight: 700, fontSize: 14 }}>{v.companyName}</span>
                                        <span style={{ ...obStyle, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
                                            {v.onboardingStatus ?? 'Incomplete'}
                                        </span>
                                        {hasPendingDocs && (
                                            <span style={{ background: '#EEF2FF', color: '#3730A3', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>
                                                ⏳ Docs pending
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 12, color: C.textSub }}>
                                        {v.contactName} · {v.email} · {v.serviceAreaRadiusMiles}mi radius
                                    </div>
                                    {v.serviceAreaAddress && (
                                        <div style={{ fontSize: 12, color: C.textSub }}>📍 {v.serviceAreaAddress}</div>
                                    )}
                                    {/* Doc status badges */}
                                    {docs && (
                                        <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                                            {[
                                                { label: '🏢', name: 'License', status: docs.businessLicenseStatus },
                                                { label: '🛡', name: 'Insurance', status: docs.liabilityInsuranceStatus },
                                                { label: '🚚', name: 'Equipment', status: docs.equipmentDocStatus },
                                            ].map(d => {
                                                const s = docStatusStyle(d.status);
                                                return (
                                                    <span key={d.name} style={{ background: s.bg, color: s.color, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                                                        {d.label} {d.name}: {s.icon} {d.status}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                    <Btn size="sm" variant="outline" onClick={() => isEditing ? setEditVendorId(null) : startEditVendor(v)}>
                                        {isEditing ? 'Cancel edit' : '✏ Edit'}
                                    </Btn>
                                    {docs && (
                                        <Btn size="sm" variant={hasPendingDocs ? undefined : 'outline'}
                                            onClick={() => setExpandedVendorDocs(isDocsOpen ? null : v.id)}>
                                            {isDocsOpen ? 'Hide docs' : hasPendingDocs ? '📄 Review docs !' : '📄 View docs'}
                                        </Btn>
                                    )}
                                    <Btn size="sm" variant="outline" onClick={() => setShowAddSize(showAddSize === v.id ? null : v.id)}>+ Size</Btn>
                                </div>
                            </div>

                            {/* Edit vendor form */}
                            {isEditing && (
                                <div style={{ background: C.bg, borderRadius: 10, padding: 16, marginBottom: 12 }}>
                                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12 }}>Edit vendor info</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                        <div>
                                            <Input label="Company name *" value={editVendorForm.companyName} onChange={val => setEditVendorForm(f => ({ ...f, companyName: val }))} />
                                            {editVendorErrors.companyName && <div style={{ color: C.coral, fontSize: 12, marginTop: -8, marginBottom: 8 }}>{editVendorErrors.companyName}</div>}
                                        </div>
                                        <div>
                                            <Input label="Contact name *" value={editVendorForm.contactName} onChange={val => setEditVendorForm(f => ({ ...f, contactName: val }))} />
                                            {editVendorErrors.contactName && <div style={{ color: C.coral, fontSize: 12, marginTop: -8, marginBottom: 8 }}>{editVendorErrors.contactName}</div>}
                                        </div>
                                        <Input label="Phone (optional)" value={editVendorForm.phoneNumber} onChange={val => setEditVendorForm(f => ({ ...f, phoneNumber: val }))} />
                                        <div>
                                            <Input label="Service radius (miles) *" value={editVendorForm.serviceAreaRadiusMiles} onChange={val => setEditVendorForm(f => ({ ...f, serviceAreaRadiusMiles: val }))} type="number" />
                                            {editVendorErrors.serviceAreaRadiusMiles && <div style={{ color: C.coral, fontSize: 12, marginTop: -8, marginBottom: 8 }}>{editVendorErrors.serviceAreaRadiusMiles}</div>}
                                        </div>
                                        <div style={{ gridColumn: '1 / -1' }}>
                                            <AddressAutocomplete
                                                label="Service area center address *"
                                                value={editVendorForm.serviceAreaAddress}
                                                onChange={({ address, lat, lng }) => setEditVendorForm(f => ({ ...f, serviceAreaAddress: address, serviceAreaLat: String(lat), serviceAreaLng: String(lng) }))}
                                                placeholder="Enter a city or address"
                                            />
                                            {editVendorErrors.serviceAreaAddress && <div style={{ color: C.coral, fontSize: 12, marginTop: 2 }}>{editVendorErrors.serviceAreaAddress}</div>}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                        <Btn variant="outline" size="sm" onClick={() => setEditVendorId(null)}>Cancel</Btn>
                                        <Btn size="sm" onClick={() => saveEditVendor(v.id)} disabled={editSaving}>
                                            {editSaving ? 'Saving...' : 'Save changes'}
                                        </Btn>
                                    </div>
                                </div>
                            )}

                            {/* Vendor doc review panel */}
                            {isDocsOpen && docs && (
                                <div style={{ background: C.bg, borderRadius: 10, padding: 14, marginBottom: 12 }}>
                                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10 }}>Document Review — {v.companyName}</div>
                                    {docSuccess && <div style={{ background: C.greenBg, color: C.green, borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 10, fontWeight: 600 }}>✓ {docSuccess}</div>}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        {[
                                            { type: 'BusinessLicense', label: '🏢 Business License', status: docs.businessLicenseStatus, photoUrl: docs.businessLicensePhotoUrl, reason: docs.businessLicenseRejectionReason },
                                            { type: 'LiabilityInsurance', label: '🛡 Liability Insurance', status: docs.liabilityInsuranceStatus, photoUrl: docs.liabilityInsurancePhotoUrl, reason: docs.liabilityInsuranceRejectionReason },
                                            { type: 'EquipmentDoc', label: '🚚 Equipment / Vehicle Proof', status: docs.equipmentDocStatus, photoUrl: docs.equipmentDocPhotoUrl, reason: docs.equipmentDocRejectionReason },
                                        ].map(doc => {
                                            const s = docStatusStyle(doc.status);
                                            const isLoading = docActionLoading === `${docs.vendorId}-${doc.type}`;
                                            return (
                                                <div key={doc.type} style={{ background: '#fff', borderRadius: 8, padding: 12, borderLeft: `4px solid ${s.color}` }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                                <span style={{ fontWeight: 600, fontSize: 13 }}>{doc.label}</span>
                                                                <DocBadge status={doc.status} />
                                                            </div>
                                                            {doc.photoUrl && (
                                                                <img src={getDocImageUrl(doc.photoUrl)} alt={doc.label} style={{ maxHeight: 130, borderRadius: 6, border: `1px solid ${C.border}`, marginBottom: 6, display: 'block' }} />
                                                            )}
                                                            {!doc.photoUrl && doc.status === 'Pending' && (
                                                                <div style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>Not yet submitted by vendor</div>
                                                            )}
                                                            {doc.reason && (
                                                                <div style={{ fontSize: 12, color: '#9B1C1C', marginTop: 4 }}>Rejection reason: {doc.reason}</div>
                                                            )}
                                                        </div>
                                                        {doc.status === 'Submitted' && (
                                                            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                                                <Btn size="sm" onClick={() => reviewDoc(docs.vendorId, doc.type, true)} disabled={isLoading}>
                                                                    {isLoading ? '...' : '✓ Approve'}
                                                                </Btn>
                                                                <Btn size="sm" variant="outline" onClick={() => setVendorDocRejectTarget({ vendorId: docs.vendorId, docType: doc.type })} disabled={isLoading}>
                                                                    ✗ Reject
                                                                </Btn>
                                                            </div>
                                                        )}
                                                        {doc.status === 'Approved' && (
                                                            <div style={{ fontSize: 12, color: C.green, fontWeight: 600, padding: '4px 0' }}>✓ Approved</div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Payout rate */}
                            {(() => {
                                const isEditingRate = rateEditId === v.id;
                                const rateLabel = v.vendorRateType === 'PerJob'
                                    ? v.vendorRateAmount != null ? `$${Number(v.vendorRateAmount).toFixed(2)} per order` : 'Flat rate (not set)'
                                    : v.vendorRateAmount != null ? `${v.vendorRateAmount}% split` : '80% split (default)';
                                return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: `1px solid ${C.border}`, marginBottom: 8, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 12, color: C.textSub, fontWeight: 500 }}>Payout rate:</span>
                                        {!isEditingRate ? (
                                            <>
                                                <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{rateLabel}</span>
                                                <Btn size="sm" variant="outline" onClick={() => { startEditRate(v); setEditVendorId(null); setExpandedVendorDocs(null); }}>
                                                    ✏ Edit rate
                                                </Btn>
                                            </>
                                        ) : (
                                            <>
                                                <select value={rateForm.rateType} onChange={e => setRateForm(f => ({ ...f, rateType: e.target.value }))}
                                                    style={{ padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}>
                                                    <option value="PercentageSplit">% Split</option>
                                                    <option value="PerJob">Flat per order</option>
                                                </select>
                                                <input
                                                    type="number"
                                                    value={rateForm.rateAmount}
                                                    onChange={e => setRateForm(f => ({ ...f, rateAmount: e.target.value }))}
                                                    placeholder={rateForm.rateType === 'PercentageSplit' ? '80 (leave blank for default)' : 'e.g. 150.00'}
                                                    style={{ width: 180, padding: '5px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12 }}
                                                />
                                                <Btn size="sm" onClick={() => saveVendorRate(v.id)} disabled={rateSaving}>
                                                    {rateSaving ? 'Saving…' : 'Save'}
                                                </Btn>
                                                <Btn size="sm" variant="outline" onClick={() => setRateEditId(null)}>Cancel</Btn>
                                            </>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Add size form */}
                            {showAddSize === v.id && (
                                <div style={{ background: C.bg, borderRadius: 10, padding: 14, marginBottom: 12 }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                                        <div>
                                            <label style={{ fontSize: 12, color: C.textSub, fontWeight: 500 }}>Size (yards)</label>
                                            <select value={sizeForm.yards} onChange={e => setSizeForm(f => ({ ...f, yards: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: `1px solid ${C.border}`, borderRadius: 6, marginTop: 4, fontSize: 13 }}>
                                                {[10, 20, 30, 40].map(y => <option key={y} value={y}>{y}yd</option>)}
                                            </select>
                                        </div>
                                        <Input label="Delivery price ($)" value={sizeForm.deliveryPrice} onChange={val => setSizeForm(f => ({ ...f, deliveryPrice: val }))} type="number" />
                                        <Input label="Daily rate ($)" value={sizeForm.dailyRate} onChange={val => setSizeForm(f => ({ ...f, dailyRate: val }))} type="number" />
                                    </div>
                                    <Input label="Description" value={sizeForm.description} onChange={val => setSizeForm(f => ({ ...f, description: val }))} placeholder="e.g. Great for medium home cleanouts" />
                                    <Input label="Max rental days" value={sizeForm.maxRentalDays} onChange={val => setSizeForm(f => ({ ...f, maxRentalDays: val }))} type="number" />
                                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                        <Btn variant="outline" size="sm" onClick={() => setShowAddSize(null)}>Cancel</Btn>
                                        <Btn size="sm" onClick={() => addSize(v.id)} disabled={saving}>{saving ? '...' : 'Add size'}</Btn>
                                    </div>
                                </div>
                            )}

                            {/* Sizes */}
                            {v.sizes.length === 0 ? (
                                <div style={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>No sizes added yet.</div>
                            ) : (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                                    {v.sizes.map((s: any) => (
                                        <div key={s.id} style={{ background: s.isAvailable ? C.greenBg : C.bg, border: `1px solid ${s.isAvailable ? C.greenMid : C.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
                                            <div style={{ fontWeight: 700, color: C.green }}>{s.yards}yd</div>
                                            <div style={{ color: C.textSub }}>${s.deliveryPrice} delivery + ${s.dailyRate}/day</div>
                                            <div style={{ color: C.textMuted }}>Max {s.maxRentalDays}d</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </Card>

            {/* Orders list */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {orderStatuses.map(s => (
                        <button key={s} onClick={() => setFilter(s)} style={{ padding: '5px 12px', borderRadius: 20, fontSize: 12, border: `1px solid ${filter === s ? C.greenMid : C.border}`, background: filter === s ? C.greenBg : '#fff', color: filter === s ? C.green : C.textSub, cursor: 'pointer', fontWeight: filter === s ? 600 : 400 }}>
                            {s}
                        </button>
                    ))}
                </div>
                <Btn size="sm" variant="outline" onClick={onRefresh}>↻ Refresh</Btn>
            </div>

            {loading ? <Spinner /> : filtered.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 48 }}>
                    <div style={{ fontSize: 40 }}>🚛</div>
                    <div style={{ fontSize: 16, fontWeight: 600, marginTop: 12 }}>No orders yet</div>
                </Card>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {filtered.map((order: any) => (
                        <Card key={order.id} style={{ borderLeft: `4px solid ${statusColor(order.status)}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                                        <span style={{ fontWeight: 700 }}>{order.dumpsterYards}yd · {order.vendorName}</span>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: statusColor(order.status), background: `${statusColor(order.status)}18`, padding: '3px 10px', borderRadius: 20 }}>{order.status}</span>
                                    </div>
                                    <div style={{ fontSize: 13, color: C.textSub }}>👤 {order.customerName}</div>
                                    <div style={{ fontSize: 13, color: C.textSub }}>📍 {order.deliveryAddress}</div>
                                    <div style={{ fontSize: 13, color: C.textSub }}>📅 {new Date(order.requestedDeliveryDate).toLocaleDateString()} · {order.rentalDays}d rental</div>
                                    {order.loadingRequested && (
                                        <div style={{ fontSize: 13, color: C.textSub }}>💪 {order.loadingRunnersRequested} runner{order.loadingRunnersRequested > 1 ? 's' : ''} requested</div>
                                    )}
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontWeight: 800, fontSize: 18, color: C.greenMid }}>${order.totalCost.toFixed(2)}</div>
                                    <div style={{ fontSize: 11, color: C.textMuted }}>
                                        ${order.deliveryPrice.toFixed(2)} + ${order.totalRentalCost.toFixed(2)}
                                        {order.loadingCost > 0 ? ` + $${order.loadingCost.toFixed(2)}` : ''}
                                        {order.overageCost > 0 ? ` + $${order.overageCost.toFixed(2)} overage` : ''}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Service Area Coverage ───────────────────────────────────────────────────────
// Tenant-wide allow-list-with-exceptions, enforced for both regular job bookings
// (JobService.CreateJobAsync) and product preorders. No rules at all means
// unrestricted; add State/County rules to cover broadly, Exclude rules to carve out
// specific zips within them, and Include-Zip rules to add specific addresses outside
// the normal coverage.

function ServiceAreaPanel() {
    const mapAndLicensesEnabled = useFeature('serviceAreaMapAndLicenses');
    const [rules, setRules] = useState<ServiceAreaRuleResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [type, setType] = useState<'State' | 'County' | 'Zip'>('State');
    const [mode, setMode] = useState<'Include' | 'Exclude'>('Include');
    const [state, setState] = useState('');
    const [county, setCounty] = useState('');
    const [zip, setZip] = useState('');
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState('');

    const load = () => {
        setLoading(true);
        api.getServiceAreaRules().then(setRules).catch(e => setError(e.message)).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const addRule = async () => {
        setError('');
        if (!state.trim()) { setError('State is required.'); return; }
        if (type === 'County' && !county.trim()) { setError('County is required for a County rule.'); return; }
        if (type === 'Zip' && !zip.trim()) { setError('Zip is required for a Zip rule.'); return; }
        setAdding(true);
        try {
            await api.addServiceAreaRule(type, mode, state, type === 'County' ? county : undefined, type === 'Zip' ? zip : undefined);
            setState(''); setCounty(''); setZip('');
            load();
        } catch (e: any) { setError(e.message); }
        finally { setAdding(false); }
    };

    const removeRule = async (id: string) => {
        try { await api.removeServiceAreaRule(id); load(); } catch (e: any) { setError(e.message); }
    };

    return (
        <>
            <Card style={{ marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>📍 Service area</h3>
            <p style={{ color: C.textSub, fontSize: 13, margin: '0 0 16px' }}>
                Controls which addresses can book a pickup or preorder a product. No rules = no restriction.
            </p>
            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
                <div style={{ width: 110 }}>
                    <Select label="Type" value={type} onChange={v => setType(v as any)}
                        options={[{ value: 'State', label: 'State' }, { value: 'County', label: 'County' }, { value: 'Zip', label: 'Zip' }]} />
                </div>
                <div style={{ width: 190 }}>
                    <Select label="Mode" value={mode} onChange={v => setMode(v as any)}
                        options={[{ value: 'Include', label: 'Include (expand coverage)' }, { value: 'Exclude', label: 'Exclude (limit coverage)' }]} />
                </div>
                <div style={{ width: 80 }}>
                    <Input label="State" value={state} onChange={v => setState(v.toUpperCase())} placeholder="AL" />
                </div>
                {type === 'County' && (
                    <div style={{ width: 160 }}>
                        <Input label="County" value={county} onChange={setCounty} placeholder="Madison County" />
                    </div>
                )}
                {type === 'Zip' && (
                    <div style={{ width: 110 }}>
                        <Input label="Zip" value={zip} onChange={setZip} placeholder="35801" />
                    </div>
                )}
                <Btn size="sm" onClick={addRule} disabled={adding} style={{ marginBottom: 14 }}>
                    {adding ? 'Adding…' : '+ Add rule'}
                </Btn>
            </div>

            {loading ? (
                <div style={{ fontSize: 13, color: C.textSub }}>Loading…</div>
            ) : rules.length === 0 ? (
                <div style={{ fontSize: 13, color: C.textSub }}>No rules — every address is currently allowed.</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {rules.map(r => (
                        <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: C.bg, borderRadius: 8 }}>
                            <div style={{ fontSize: 13 }}>
                                <span style={{
                                    display: 'inline-block', fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '2px 8px', marginRight: 8,
                                    background: r.mode === 'Include' ? C.greenBg : '#FDECEA', color: r.mode === 'Include' ? C.green : '#9B1C1C',
                                }}>{r.mode}</span>
                                <strong>{r.type}</strong>: {
                                    r.type === 'Zip' ? r.zip
                                        : r.type === 'County' ? `${r.county}, ${r.state}`
                                        : r.type === 'Radius' ? `${r.radiusMiles}mi around ${r.centerAddress || `${r.centerLat}, ${r.centerLng}`}`
                                        : r.state
                                }
                            </div>
                            <button onClick={() => removeRule(r.id)} style={{ background: 'transparent', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: 12 }}>Remove</button>
                        </div>
                    ))}
                </div>
            )}
            </Card>
            {mapAndLicensesEnabled && <ServiceAreaMapPanel onRuleAdded={load} />}
            {mapAndLicensesEnabled && <BusinessLicensePanel />}
        </>
    );
}

// ── Business License Compliance ─────────────────────────────────────────────────
// Standalone jurisdiction-tagged (State/County/City) compliance records, looked up by
// whatever jurisdiction an inspector names — deliberately not tied to a specific
// ServiceAreaRule. Behind the same feature flag as the map panel above.

function BusinessLicensePanel() {
    const [licenses, setLicenses] = useState<BusinessLicenseResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');

    const [state, setState] = useState('');
    const [county, setCounty] = useState('');
    const [city, setCity] = useState('');
    const [licenseNumber, setLicenseNumber] = useState('');
    const [issuedDate, setIssuedDate] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [file, setFile] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    const load = () => {
        setLoading(true);
        api.getBusinessLicenses().then(setLicenses).catch(e => setError(e.message)).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const handleFile = (f: File) => {
        const reader = new FileReader();
        reader.onload = e => setFile(e.target?.result as string);
        reader.readAsDataURL(f);
    };

    const upload = async () => {
        setError('');
        if (!file) { setError('Choose a photo of the license first.'); return; }
        setUploading(true);
        try {
            await api.uploadBusinessLicense({
                state: state.trim() || undefined, county: county.trim() || undefined, city: city.trim() || undefined,
                licenseNumber: licenseNumber.trim() || undefined,
                photoBase64: file, issuedDate: issuedDate || undefined, expiryDate: expiryDate || undefined,
            });
            setState(''); setCounty(''); setCity(''); setLicenseNumber(''); setIssuedDate(''); setExpiryDate('');
            setFile(null);
            if (fileRef.current) fileRef.current.value = '';
            load();
        } catch (e: any) { setError(e.message); }
        finally { setUploading(false); }
    };

    const review = async (id: string, approved: boolean) => {
        try { await api.reviewBusinessLicense(id, approved, approved ? undefined : 'Rejected by admin'); load(); }
        catch (e: any) { setError(e.message); }
    };

    const remove = async (id: string) => {
        try { await api.removeBusinessLicense(id); load(); } catch (e: any) { setError(e.message); }
    };

    const filtered = licenses.filter(l => {
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return (l.county?.toLowerCase().includes(q)) || (l.city?.toLowerCase().includes(q)) || (l.state?.toLowerCase().includes(q));
    });

    return (
        <Card style={{ marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>📄 Business licenses</h3>
            <p style={{ color: C.textSub, fontSize: 13, margin: '0 0 16px' }}>
                Compliance documents proving BoxRunner is licensed to operate in a jurisdiction — looked up by city or county for audits.
            </p>
            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
                <div style={{ width: 80 }}>
                    <Input label="State" value={state} onChange={v => setState(v.toUpperCase())} placeholder="AL" />
                </div>
                <div style={{ width: 160 }}>
                    <Input label="County" value={county} onChange={setCounty} placeholder="Madison County" />
                </div>
                <div style={{ width: 140 }}>
                    <Input label="City" value={city} onChange={setCity} placeholder="Huntsville" />
                </div>
                <div style={{ width: 160 }}>
                    <Input label="License #" value={licenseNumber} onChange={setLicenseNumber} placeholder="Optional" />
                </div>
                <div style={{ width: 150 }}>
                    <Input label="Issued" value={issuedDate} onChange={setIssuedDate} type="date" />
                </div>
                <div style={{ width: 150 }}>
                    <Input label="Expires" value={expiryDate} onChange={setExpiryDate} type="date" />
                </div>
                <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>Photo</label>
                    <input ref={fileRef} type="file" accept="image/*" onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} />
                </div>
                <Btn size="sm" onClick={upload} disabled={uploading} style={{ marginBottom: 14 }}>
                    {uploading ? 'Uploading…' : '+ Upload license'}
                </Btn>
            </div>

            <div style={{ marginBottom: 12 }}>
                <Input label="Search by city or county" value={search} onChange={setSearch} placeholder="Type to filter…" />
            </div>

            {loading ? (
                <div style={{ fontSize: 13, color: C.textSub }}>Loading…</div>
            ) : filtered.length === 0 ? (
                <div style={{ fontSize: 13, color: C.textSub }}>No business licenses on file{search ? ' matching that search' : ''}.</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {filtered.map(l => (
                        <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: C.bg, borderRadius: 8, flexWrap: 'wrap', gap: 8 }}>
                            <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Badge label={l.status} />
                                <a href={l.documentUrl} target="_blank" rel="noreferrer" style={{ color: C.greenMid }}>
                                    {[l.city, l.county, l.state].filter(Boolean).join(', ') || 'Untagged'}
                                </a>
                                {l.licenseNumber && <span style={{ color: C.textSub }}>· #{l.licenseNumber}</span>}
                                {l.expiryDate && <span style={{ color: C.textSub }}>· expires {new Date(l.expiryDate).toLocaleDateString()}</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 10 }}>
                                {(l.status === 'Submitted' || l.status === 'Pending') && (
                                    <>
                                        <button onClick={() => review(l.id, true)} style={{ background: 'transparent', border: 'none', color: C.green, cursor: 'pointer', fontSize: 12 }}>Approve</button>
                                        <button onClick={() => review(l.id, false)} style={{ background: 'transparent', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: 12 }}>Reject</button>
                                    </>
                                )}
                                <button onClick={() => remove(l.id)} style={{ background: 'transparent', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: 12 }}>Remove</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}

// ── Product Preorders Admin ─────────────────────────────────────────────────────

function ProductPreorderAdminPanel({ preorders, loading, onRefresh }: {
    preorders: ProductPreorderResponse[]; loading: boolean; onRefresh: () => void;
}) {
    const [filter, setFilter] = useState('All');
    const [updating, setUpdating] = useState<string | null>(null);
    const [error, setError] = useState('');

    const filtered = filter === 'All' ? preorders : preorders.filter(p => p.fulfillmentStatus === filter);

    const markFulfilled = async (id: string) => {
        setUpdating(id); setError('');
        try {
            await api.updateProductPreorderFulfillment(id, 'Fulfilled');
            onRefresh();
        } catch (e: any) { setError(e.message); }
        finally { setUpdating(null); }
    };

    const statusColor = (s: string) => {
        switch (s) {
            case 'Fulfilled': return C.green;
            case 'Cancelled': return C.coral;
            default: return '#854F0B';
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Preorders</h2>
                <div style={{ display: 'flex', gap: 8 }}>
                    {['All', 'Pending', 'Fulfilled', 'Cancelled'].map(s => (
                        <button key={s} onClick={() => setFilter(s)}
                            style={{
                                padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                border: `1px solid ${filter === s ? C.greenMid : C.border}`,
                                background: filter === s ? C.greenBg : '#fff',
                                color: filter === s ? C.green : C.textSub,
                            }}>{s}</button>
                    ))}
                </div>
            </div>
            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            {loading ? (
                <div style={{ textAlign: 'center', color: C.textSub, padding: 40 }}>Loading…</div>
            ) : filtered.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 40 }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📦</div>
                    <div style={{ color: C.textSub }}>No preorders{filter !== 'All' ? ` with status "${filter}"` : ''} yet.</div>
                </Card>
            ) : (
                <Card style={{ padding: 0, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                                {['Item', 'Contact', 'Delivery address', 'Qty', 'Total', 'Payment', 'Fulfillment', 'Date', ''].map(h => (
                                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(p => (
                                <tr key={p.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                                    <td style={{ padding: '12px 16px', fontWeight: 600 }}>{p.serviceTypeName}</td>
                                    <td style={{ padding: '12px 16px' }}>
                                        <div>{p.contactName}</div>
                                        <div style={{ fontSize: 11, color: C.textMuted }}>{p.contactEmail}{p.contactPhone ? ` · ${p.contactPhone}` : ''}</div>
                                    </td>
                                    <td style={{ padding: '12px 16px', color: C.textSub }}>{p.deliveryAddress}</td>
                                    <td style={{ padding: '12px 16px' }}>{p.quantity}</td>
                                    <td style={{ padding: '12px 16px', fontWeight: 700 }}>${p.totalPrice.toFixed(2)}</td>
                                    <td style={{ padding: '12px 16px' }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: p.paymentStatus === 'Captured' ? C.green : C.textMuted }}>{p.paymentStatus}</span>
                                    </td>
                                    <td style={{ padding: '12px 16px' }}>
                                        <span style={{ borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600, background: `${statusColor(p.fulfillmentStatus)}20`, color: statusColor(p.fulfillmentStatus) }}>
                                            {p.fulfillmentStatus}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px 16px', color: C.textSub }}>{new Date(p.createdAt).toLocaleDateString()}</td>
                                    <td style={{ padding: '12px 16px' }}>
                                        {p.fulfillmentStatus === 'Pending' && (
                                            <Btn size="sm" onClick={() => markFulfilled(p.id)} disabled={updating === p.id}>
                                                {updating === p.id ? '…' : 'Mark fulfilled'}
                                            </Btn>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}
        </div>
    );
}

// ── Subscription Plans Admin ──────────────────────────────────────────────────

const billingPeriodSuffix = (interval?: string) => {
    switch (interval) {
        case 'Quarterly':  return '/quarter';
        case 'SemiAnnual': return '/6 mo';
        case 'Annual':     return '/year';
        default:           return '/mo';
    }
};

function SubscriptionPlansAdmin() {
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState<SubscriptionPlan | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const blank = { name: '', description: '', monthlyPrice: 0, billingInterval: 'Monthly', includedPickups: '', serviceLabel: '', discountPercent: 0, priorityScheduling: false, sortOrder: 0 };
    const [form, setForm] = useState<typeof blank>(blank);

    const load = async () => {
        setLoading(true);
        try { setPlans(await api.getSubscriptionPlans(true)); } catch { } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openNew = () => { setForm(blank); setEditing(null); setShowForm(true); setError(''); };
    const openEdit = (p: SubscriptionPlan) => {
        setForm({
            name: p.name, description: p.description ?? '', monthlyPrice: p.monthlyPrice,
            billingInterval: p.billingInterval ?? 'Monthly',
            includedPickups: p.includedPickups?.toString() ?? '',
            serviceLabel: p.serviceLabel ?? '',
            discountPercent: p.discountPercent,
            priorityScheduling: p.priorityScheduling, sortOrder: p.sortOrder,
        });
        setEditing(p); setShowForm(true); setError('');
    };

    const save = async () => {
        setSaving(true); setError('');
        try {
            const payload = {
                name: form.name,
                description: form.description || undefined,
                monthlyPrice: Number(form.monthlyPrice),
                billingInterval: form.billingInterval,
                includedPickups: form.includedPickups ? Number(form.includedPickups) : undefined,
                serviceLabel: form.serviceLabel || undefined,
                discountPercent: Number(form.discountPercent),
                priorityScheduling: form.priorityScheduling,
                sortOrder: Number(form.sortOrder),
            };
            if (editing) {
                await api.adminUpdateSubscriptionPlan(editing.id, payload);
            } else {
                await api.adminCreateSubscriptionPlan(payload);
            }
            setShowForm(false);
            load();
        } catch (e: any) { setError(e.message); } finally { setSaving(false); }
    };

    const deactivatePlan = async (id: string) => {
        if (!window.confirm('Deactivate this plan? Existing subscribers keep access until their period ends. No new subscriptions can be created for this plan.')) return;
        try {
            await api.adminDeleteSubscriptionPlan(id);
            setPlans(prev => prev.map(p => p.id === id ? { ...p, isActive: false } : p));
        } catch (e: any) { alert(e.message); }
    };

    const reactivatePlan = async (id: string) => {
        try {
            await api.adminUpdateSubscriptionPlan(id, { isActive: true });
            setPlans(prev => prev.map(p => p.id === id ? { ...p, isActive: true } : p));
        } catch (e: any) { alert(e.message); }
    };

    const f = (key: keyof typeof blank, val: any) => setForm(prev => ({ ...prev, [key]: val }));

    if (loading) return <Spinner />;

    const activePlans = plans.filter(p => p.isActive !== false);
    const inactivePlans = plans.filter(p => p.isActive === false);

    return (
        <div style={{ maxWidth: 800 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Subscription Plans</h2>
                    <p style={{ margin: '4px 0 0', color: C.textSub, fontSize: 14 }}>Plans are synced to Stripe when saved.</p>
                </div>
                <Btn onClick={openNew}>+ New plan</Btn>
            </div>

            {showForm && (
                <Card style={{ marginBottom: 24, border: `1px solid ${C.green}` }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>{editing ? 'Edit plan' : 'New plan'}</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <Input label="Name" value={form.name} onChange={v => f('name', v)} />
                        <Input label="Price ($)" value={String(form.monthlyPrice)} onChange={v => f('monthlyPrice', v)} />
                        <div>
                            <div style={{ fontSize: 12, color: C.textSub, marginBottom: 4 }}>Billing period</div>
                            <select value={form.billingInterval} onChange={e => f('billingInterval', e.target.value)}
                                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.text, fontSize: 14 }}>
                                <option value="Monthly">Monthly</option>
                                <option value="Quarterly">Quarterly (every 3 months)</option>
                                <option value="SemiAnnual">Semi-Annual (every 6 months)</option>
                                <option value="Annual">Annual (once a year)</option>
                            </select>
                        </div>
                        <Input label="Service label (e.g. pickups, visits, tune-ups)" value={form.serviceLabel} onChange={v => f('serviceLabel', v)} />
                        <Input label="Included count (blank = unlimited)" value={form.includedPickups} onChange={v => f('includedPickups', v)} />
                        <Input label="Discount (%)" value={String(form.discountPercent)} onChange={v => f('discountPercent', v)} />
                        <Input label="Sort order" value={String(form.sortOrder)} onChange={v => f('sortOrder', v)} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 20 }}>
                            <input type="checkbox" id="prio" checked={form.priorityScheduling} onChange={e => f('priorityScheduling', e.target.checked)} style={{ width: 16, height: 16, accentColor: C.green }} />
                            <label htmlFor="prio" style={{ fontSize: 14, cursor: 'pointer' }}>Priority scheduling</label>
                        </div>
                    </div>
                    <div style={{ marginTop: 12 }}>
                        <Input label="Description" value={form.description} onChange={v => f('description', v)} />
                    </div>
                    {error && <div style={{ marginTop: 12 }}><Alert message={error} /></div>}
                    <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save plan'}</Btn>
                        <Btn variant="outline" onClick={() => setShowForm(false)}>Cancel</Btn>
                    </div>
                </Card>
            )}

            {plans.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 48, color: C.textSub }}>
                    No plans yet. Click <strong>+ New plan</strong> to create one.
                </Card>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {activePlans.map(p => (
                        <Card key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4 }}>
                                    <span style={{ fontWeight: 700, fontSize: 16 }}>{p.name}</span>
                                    <span style={{ fontWeight: 700, color: C.green }}>${p.monthlyPrice}{billingPeriodSuffix(p.billingInterval)}</span>
                                </div>
                                <div style={{ fontSize: 13, color: C.textSub }}>
                                    {p.includedPickups ? `${p.includedPickups} ${p.serviceLabel}` : `Unlimited ${p.serviceLabel}`}
                                    {p.discountPercent > 0 && ` · ${p.discountPercent}% discount`}
                                    {p.priorityScheduling && ' · Priority scheduling'}
                                </div>
                                {p.description && <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>{p.description}</div>}
                                {p.stripePriceId && <div style={{ fontSize: 11, color: C.textSub, marginTop: 4, fontFamily: 'monospace' }}>{p.stripePriceId}</div>}
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                <Btn variant="outline" size="sm" onClick={() => openEdit(p)}>Edit</Btn>
                                <Btn variant="danger" size="sm" onClick={() => deactivatePlan(p.id)}>Deactivate</Btn>
                            </div>
                        </Card>
                    ))}

                    {inactivePlans.length > 0 && (
                        <>
                            <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, textTransform: 'uppercase', letterSpacing: 1, marginTop: 8 }}>Deactivated</div>
                            {inactivePlans.map(p => (
                                <Card key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', opacity: 0.6 }}>
                                    <div>
                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4 }}>
                                            <span style={{ fontWeight: 700, fontSize: 16, textDecoration: 'line-through' }}>{p.name}</span>
                                            <span style={{ fontWeight: 700 }}>${p.monthlyPrice}{billingPeriodSuffix(p.billingInterval)}</span>
                                        </div>
                                        <div style={{ fontSize: 13, color: C.textSub }}>
                                            {p.includedPickups ? `${p.includedPickups} ${p.serviceLabel}` : `Unlimited ${p.serviceLabel}`}
                                            {p.discountPercent > 0 && ` · ${p.discountPercent}% discount`}
                                        </div>
                                    </div>
                                    <Btn variant="outline" size="sm" onClick={() => reactivatePlan(p.id)}>Reactivate</Btn>
                                </Card>
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Customers Panel ────────────────────────────────────────────────────────────

function CustomersPanel() {
    const [search, setSearch] = useState('');
    const [customers, setCustomers] = useState<AdminCustomerListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<AdminCustomerDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [editName, setEditName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [saving, setSaving] = useState(false);
    const [cancelConfirm, setCancelConfirm] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const t = setTimeout(() => fetchCustomers(), 300);
        return () => clearTimeout(t);
    }, [search]);

    const fetchCustomers = async () => {
        setLoading(true);
        try { setCustomers(await api.adminGetCustomers(search || undefined)); }
        catch { setCustomers([]); }
        finally { setLoading(false); }
    };

    const openDetail = async (id: string) => {
        setDetailLoading(true);
        setSelected(null);
        setError(''); setSuccess(''); setCancelConfirm(false);
        try {
            const d = await api.adminGetCustomer(id);
            setSelected(d);
            setEditName(d.name);
            setEditEmail(d.email);
            setEditPhone(d.phoneNumber ?? '');
        } catch { setError('Failed to load customer details.'); }
        finally { setDetailLoading(false); }
    };

    const closeModal = () => { setSelected(null); setError(''); setSuccess(''); setCancelConfirm(false); };

    const saveChanges = async () => {
        if (!selected) return;
        setSaving(true); setError(''); setSuccess('');
        try {
            await api.adminUpdateCustomer(selected.id, {
                name: editName || undefined,
                email: editEmail || undefined,
                phoneNumber: editPhone || undefined,
            });
            setSuccess('Saved.');
            setCustomers(prev => prev.map(c =>
                c.id === selected.id
                    ? { ...c, name: editName || c.name, email: editEmail || c.email, phoneNumber: editPhone || c.phoneNumber }
                    : c
            ));
            setSelected(prev => prev ? { ...prev, name: editName, email: editEmail, phoneNumber: editPhone } : prev);
        } catch (e: any) { setError(e.message ?? 'Save failed.'); }
        finally { setSaving(false); }
    };

    const cancelSubscription = async () => {
        if (!selected) return;
        setSaving(true); setError(''); setSuccess('');
        try {
            await api.adminCancelCustomerSubscription(selected.id);
            setSuccess('Subscription cancelled.');
            setSelected(prev => prev ? { ...prev, activeSubscription: undefined } : prev);
            setCustomers(prev => prev.map(c =>
                c.id === selected.id
                    ? { ...c, activePlan: undefined, subscriptionStatus: undefined, monthlyPrice: undefined }
                    : c
            ));
            setCancelConfirm(false);
        } catch (e: any) { setError(e.message ?? 'Cancel failed.'); }
        finally { setSaving(false); }
    };

    const totalSubscribers = customers.filter(c => c.subscriptionStatus === 'Active').length;
    const mrr = customers.reduce((sum, c) => sum + (c.subscriptionStatus === 'Active' ? (c.monthlyPrice ?? 0) : 0), 0);

    return (
        <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                <StatCard label="Total Customers" value={customers.length} />
                <StatCard label="Active Subscribers" value={totalSubscribers} />
                <StatCard label="MRR" value={`$${mrr.toFixed(2)}`} />
            </div>

            <input
                placeholder="Search by name or email…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                    maxWidth: 360, width: '100%', marginBottom: 16, padding: '10px 12px',
                    border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 14,
                    color: C.text, background: C.card, outline: 'none',
                }}
            />

            {loading && <Spinner />}

            {!loading && customers.length === 0 && (
                <Card><div style={{ color: C.textSub, textAlign: 'center', padding: 24 }}>No customers found.</div></Card>
            )}

            {!loading && customers.map(c => (
                <div
                    key={c.id}
                    style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                        cursor: 'pointer', marginBottom: 8, background: C.card, borderRadius: 10,
                        padding: '14px 16px', border: `1px solid ${C.border}`,
                    }}
                    onClick={() => openDetail(c.id)}
                >
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 700 }}>{c.name}</span>
                            {c.subscriptionStatus === 'Active' && c.activePlan && (
                                <Badge label={c.activePlan} />
                            )}
                            {c.subscriptionStatus && c.subscriptionStatus !== 'Active' && (
                                <Badge label={c.subscriptionStatus} />
                            )}
                        </div>
                        <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>{c.email}</div>
                        {c.phoneNumber && <div style={{ fontSize: 12, color: C.textSub }}>{c.phoneNumber}</div>}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 16 }}>
                        <div style={{ fontSize: 13, color: C.textSub }}>{c.totalJobs} job{c.totalJobs !== 1 ? 's' : ''}</div>
                        {c.subscriptionStatus === 'Active' && c.monthlyPrice != null && (
                            <div style={{ fontSize: 12, color: C.textSub }}>${c.monthlyPrice}/mo</div>
                        )}
                        <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>
                            Joined {new Date(c.createdAt).toLocaleDateString()}
                        </div>
                    </div>
                </div>
            ))}

            {(detailLoading || selected) && (
                <div
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        zIndex: 1000, padding: 16,
                    }}
                    onClick={e => { if (e.target === e.currentTarget) closeModal(); }}
                >
                    <div style={{
                        background: C.card, borderRadius: 12, padding: 28,
                        width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto',
                        boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
                    }}>
                        {detailLoading && <div style={{ textAlign: 'center', padding: 32 }}><Spinner /></div>}

                        {selected && (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                                    <span style={{ fontSize: 18, fontWeight: 700 }}>Customer Details</span>
                                    <Btn variant="outline" size="sm" onClick={closeModal}>✕</Btn>
                                </div>

                                <Alert message={error} />
                                <Alert message={success} type="success" />

                                <Input label="Name" value={editName} onChange={setEditName} />
                                <Input label="Email" value={editEmail} onChange={setEditEmail} />
                                <Input label="Phone" value={editPhone} onChange={setEditPhone} placeholder="(optional)" />

                                <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
                                    <Btn variant="primary" size="sm" onClick={saveChanges} disabled={saving}>
                                        {saving ? 'Saving…' : 'Save Changes'}
                                    </Btn>
                                    <Btn variant="outline" size="sm" onClick={closeModal}>Dismiss</Btn>
                                </div>

                                <div style={{ fontSize: 13, color: C.textSub, marginBottom: 12 }}>
                                    Member since {new Date(selected.createdAt).toLocaleDateString()} · {selected.totalJobs} total job{selected.totalJobs !== 1 ? 's' : ''}
                                    {selected.defaultAddress && (
                                        <div style={{ marginTop: 4 }}>Default address: {selected.defaultAddress}</div>
                                    )}
                                </div>

                                <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
                                    <div style={{ fontWeight: 600, marginBottom: 10 }}>Subscription</div>
                                    {selected.activeSubscription ? (
                                        <>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13, marginBottom: 16 }}>
                                                <div><span style={{ color: C.textSub }}>Plan: </span>{selected.activeSubscription.planName}</div>
                                                <div><span style={{ color: C.textSub }}>Price: </span>${selected.activeSubscription.monthlyPrice}/mo</div>
                                                <div>
                                                    <span style={{ color: C.textSub }}>Pickups: </span>
                                                    {selected.activeSubscription.pickupsUsed}
                                                    {selected.activeSubscription.includedPickups != null ? ` / ${selected.activeSubscription.includedPickups}` : ''}
                                                </div>
                                                <div><span style={{ color: C.textSub }}>Renews: </span>{new Date(selected.activeSubscription.currentPeriodEnd).toLocaleDateString()}</div>
                                                {selected.activeSubscription.discountPercent > 0 && (
                                                    <div><span style={{ color: C.textSub }}>Discount: </span>{selected.activeSubscription.discountPercent}%</div>
                                                )}
                                                {selected.activeSubscription.priorityScheduling && (
                                                    <div style={{ color: '#0F6E56', fontWeight: 600 }}>Priority scheduling</div>
                                                )}
                                            </div>

                                            {cancelConfirm ? (
                                                <div style={{ background: 'rgba(220,53,69,0.08)', borderRadius: 8, padding: 12, border: '1px solid rgba(220,53,69,0.25)' }}>
                                                    <div style={{ fontSize: 13, marginBottom: 10 }}>Cancel this subscription immediately? This cannot be undone.</div>
                                                    <div style={{ display: 'flex', gap: 8 }}>
                                                        <Btn variant="danger" size="sm" onClick={cancelSubscription} disabled={saving}>
                                                            {saving ? 'Cancelling…' : 'Yes, Cancel Now'}
                                                        </Btn>
                                                        <Btn variant="outline" size="sm" onClick={() => setCancelConfirm(false)}>Keep Active</Btn>
                                                    </div>
                                                </div>
                                            ) : (
                                                <Btn variant="danger" size="sm" onClick={() => setCancelConfirm(true)}>
                                                    Cancel Subscription
                                                </Btn>
                                            )}
                                        </>
                                    ) : (
                                        <div style={{ color: C.textSub, fontSize: 13 }}>No active subscription.</div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Ads Panel ─────────────────────────────────────────────────────────────────

function AdsPanel() {
    const [ads, setAds] = useState<AdItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState<AdItem | null>(null);
    const [isNew, setIsNew] = useState(false);

    const blank = { title: '', subtitle: '', imageData: '', buttonText: '', buttonUrl: '', isActive: true, sortOrder: 0 };
    const [form, setForm] = useState<Omit<AdItem, 'id'>>(blank);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState<string | null>(null);
    const fileRef = React.useRef<HTMLInputElement>(null);

    const load = async () => {
        setLoading(true);
        try { setAds(await api.adminGetAds()); }
        catch (e: any) { setError(e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const openNew = () => { setForm(blank); setEditing(null); setIsNew(true); };
    const openEdit = (ad: AdItem) => {
        setForm({ title: ad.title, subtitle: ad.subtitle ?? '', imageData: ad.imageData ?? '', buttonText: ad.buttonText, buttonUrl: ad.buttonUrl ?? '', isActive: ad.isActive, sortOrder: ad.sortOrder });
        setEditing(ad); setIsNew(false);
    };
    const closeForm = () => { setEditing(null); setIsNew(false); };

    const handleImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = ev => setForm(f => ({ ...f, imageData: ev.target?.result as string }));
        reader.readAsDataURL(file);
    };

    const save = async () => {
        if (!form.title.trim() || !form.buttonText.trim()) { setError('Title and button text are required.'); return; }
        setSaving(true); setError('');
        try {
            if (isNew) await api.adminCreateAd(form);
            else if (editing) await api.adminUpdateAd(editing.id, form);
            await load(); closeForm();
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const del = async (id: string) => {
        if (!window.confirm('Delete this ad?')) return;
        setDeleting(id);
        try { await api.adminDeleteAd(id); await load(); }
        catch (e: any) { setError(e.message); }
        finally { setDeleting(null); }
    };

    const formOpen = isNew || !!editing;

    if (loading) return <Spinner />;

    return (
        <div style={{ maxWidth: 900 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Ad Management</h2>
                    <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>Create and manage promotional cards shown to customers</div>
                </div>
                {!formOpen && <Btn onClick={openNew}>+ New Ad</Btn>}
            </div>
            <Alert message={error} />

            {formOpen && (
                <Card style={{ marginBottom: 28 }}>
                    <h3 style={{ margin: '0 0 20px', fontSize: 16, fontWeight: 700 }}>{isNew ? 'Create Ad' : 'Edit Ad'}</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Input label="Title *" value={form.title} onChange={v => setForm(f => ({ ...f, title: v }))} />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Input label="Subtitle (optional)" value={form.subtitle ?? ''} onChange={v => setForm(f => ({ ...f, subtitle: v }))} />
                        </div>
                        <div>
                            <Input
                                label={`Button text * (${form.buttonText.length}/28 chars)`}
                                value={form.buttonText}
                                onChange={v => setForm(f => ({ ...f, buttonText: v.slice(0, 28) }))}
                            />
                        </div>
                        <div>
                            <Input label="Button URL (link on click)" value={form.buttonUrl ?? ''} onChange={v => setForm(f => ({ ...f, buttonUrl: v }))} />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ fontSize: 13, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 6 }}>Ad Image</label>
                            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                <div>
                                    <input type="file" accept="image/*" ref={fileRef} style={{ display: 'none' }} onChange={handleImageFile} />
                                    <Btn variant="outline" onClick={() => fileRef.current?.click()}>Upload image</Btn>
                                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>or paste URL below</div>
                                    <input
                                        type="text"
                                        placeholder="https://... or leave blank for gradient"
                                        value={form.imageData?.startsWith('data:') ? '(uploaded file)' : (form.imageData ?? '')}
                                        onChange={e => setForm(f => ({ ...f, imageData: e.target.value }))}
                                        style={{ marginTop: 8, width: 280, padding: '7px 10px', borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.bg }}
                                    />
                                </div>
                                <div style={{ border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden', width: 200, flexShrink: 0 }}>
                                    {form.imageData ? (
                                        <img src={form.imageData} alt="preview" style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
                                    ) : (
                                        <div style={{ width: '100%', height: 100, background: 'linear-gradient(135deg, #1baf7a, #2a78d6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <span style={{ fontSize: 30 }}>📦</span>
                                        </div>
                                    )}
                                    <div style={{ padding: '10px 12px' }}>
                                        <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{form.title || 'Ad title'}</div>
                                        {form.subtitle && <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>{form.subtitle}</div>}
                                        <div style={{ marginTop: 8, background: '#1baf7a', color: '#fff', borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 700, textAlign: 'center' }}>
                                            {(form.buttonText || 'Button text').slice(0, 28)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <label style={{ fontSize: 13, fontWeight: 500, color: C.textSub }}>Sort order</label>
                            <input
                                type="number"
                                value={form.sortOrder}
                                onChange={e => setForm(f => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))}
                                style={{ width: 70, padding: '7px 10px', borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.bg }}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: C.textSub }}>
                                <input
                                    type="checkbox"
                                    checked={form.isActive}
                                    onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                                    style={{ width: 16, height: 16, accentColor: C.greenMid }}
                                />
                                Active (visible to customers)
                            </label>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                        <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : isNew ? 'Create ad' : 'Save changes'}</Btn>
                        <Btn variant="outline" onClick={closeForm}>Cancel</Btn>
                    </div>
                </Card>
            )}

            {ads.length === 0 && !formOpen ? (
                <Card style={{ textAlign: 'center', padding: 48, color: C.textMuted }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>📣</div>
                    <div style={{ fontWeight: 600, color: C.textSub, fontSize: 16 }}>No ads yet</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>Create your first ad to show offers to customers in the booking flow.</div>
                </Card>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {ads.map(ad => (
                        <Card key={ad.id} style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '14px 18px', opacity: ad.isActive ? 1 : 0.55 }}>
                            <div style={{ width: 64, height: 50, borderRadius: 8, overflow: 'hidden', flexShrink: 0 }}>
                                {ad.imageData ? (
                                    <img src={ad.imageData} alt={ad.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #1baf7a, #2a78d6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📦</div>
                                )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{ad.title}</span>
                                    <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 99, background: ad.isActive ? C.greenBg : C.bg, color: ad.isActive ? C.greenMid : C.textMuted, fontWeight: 600 }}>
                                        {ad.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                {ad.subtitle && <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>{ad.subtitle}</div>}
                                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
                                    Button: <strong>{ad.buttonText}</strong>
                                    {ad.buttonUrl && <> &middot; <a href={ad.buttonUrl} target="_blank" rel="noopener noreferrer" style={{ color: C.greenMid }}>{ad.buttonUrl.slice(0, 40)}{ad.buttonUrl.length > 40 ? '...' : ''}</a></>}
                                    {' '}&middot; Order: {ad.sortOrder}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                <Btn variant="outline" onClick={() => openEdit(ad)}>Edit</Btn>
                                <Btn variant="danger" onClick={() => del(ad.id)} disabled={deleting === ad.id}>
                                    {deleting === ad.id ? '...' : 'Delete'}
                                </Btn>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}


// ── QuickBooks Panel ──────────────────────────────────────────────────────────

type QboTab = 'connection' | 'mapping' | 'errors';

function QuickBooksPanel() {
    const [tab, setTab] = useState<QboTab>('connection');
    const [status, setStatus] = useState<QboConnectionStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [working, setWorking] = useState(false);
    const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');

    const loadStatus = async () => {
        setLoading(true);
        try { setStatus(await api.qboStatus()); }
        catch (e: any) { setErrorMsg(e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        // Handle return from Intuit OAuth redirect
        const params = new URLSearchParams(window.location.search);
        const qbo = params.get('qbo');
        if (qbo === 'connected') {
            setSuccessMsg('QuickBooks connected successfully!');
            window.history.replaceState({}, '', window.location.pathname);
        } else if (qbo === 'error') {
            setErrorMsg(params.get('msg') || 'QuickBooks connection failed.');
            window.history.replaceState({}, '', window.location.pathname);
        }
        loadStatus();
    }, []);

    const handleConnect = async () => {
        setWorking(true); setErrorMsg(''); setSuccessMsg('');
        try {
            const { url } = await api.qboGetConnectUrl(environment);
            // Full-page navigation — Intuit redirects back to the backend callback
            // which exchanges the code and redirects back here with ?qbo=connected
            window.location.href = url;
        } catch (e: any) { setErrorMsg(e.message); setWorking(false); }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Disconnect QuickBooks? Existing synced records will remain in QuickBooks but no new syncs will occur.')) return;
        setWorking(true); setErrorMsg('');
        try { await api.qboDisconnect(); await loadStatus(); }
        catch (e: any) { setErrorMsg(e.message); }
        finally { setWorking(false); }
    };

    const tabs: { key: QboTab; label: string }[] = [
        { key: 'connection', label: 'Connection' },
        { key: 'mapping', label: 'Account Mapping' },
        { key: 'errors', label: 'Sync Errors' },
    ];

    return (
        <div style={{ maxWidth: 900 }}>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>QuickBooks Online</h2>
                <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>Automated accounting sync for revenue, customers, and expenses</div>
            </div>
            <Alert message={errorMsg} />
            {successMsg && (
                <div style={{ background: C.greenBg, color: C.greenMid, borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
                    ✓ {successMsg}
                </div>
            )}

            {/* Tab bar */}
            <div style={{ display: 'flex', gap: 2, marginBottom: 24, borderBottom: `2px solid ${C.border}` }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} style={{
                        padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
                        fontSize: 13, fontWeight: 600,
                        color: tab === t.key ? C.greenMid : C.textSub,
                        borderBottom: tab === t.key ? `2px solid ${C.greenMid}` : '2px solid transparent',
                        marginBottom: -2,
                    }}>{t.label}</button>
                ))}
            </div>

            {loading ? <Spinner /> : (
                <>
                    {tab === 'connection' && (
                        <QboConnectionTab
                            status={status}
                            working={working}
                            environment={environment}
                            onEnvironmentChange={setEnvironment}
                            onConnect={handleConnect}
                            onDisconnect={handleDisconnect}
                            onRefresh={loadStatus}
                        />
                    )}
                    {tab === 'mapping' && (
                        <QboMappingTab isConnected={!!status?.isConnected} />
                    )}
                    {tab === 'errors' && (
                        <QboErrorsTab isConnected={!!status?.isConnected} />
                    )}
                </>
            )}
        </div>
    );
}

function QboConnectionTab({
    status,
    working,
    environment,
    onEnvironmentChange,
    onConnect,
    onDisconnect,
    onRefresh,
}: {
    status: QboConnectionStatus | null;
    working: boolean;
    environment: 'sandbox' | 'production';
    onEnvironmentChange: (env: 'sandbox' | 'production') => void;
    onConnect: () => void;
    onDisconnect: () => void;
    onRefresh: () => void;
}) {
    const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

    const isExpiringSoon = status?.refreshTokenExpiresAt
        ? new Date(status.refreshTokenExpiresAt) < new Date(Date.now() + 7 * 86400000)
        : false;

    return (
        <div>
            <Card style={{ padding: 28 }}>
                {status?.isConnected ? (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: '50%', background: C.greenBg,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
                            }}>✓</div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 16, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    Connected
                                    <span style={{
                                        fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                                        background: status.environment === 'production' ? '#FEF3C7' : C.greenBg,
                                        color: status.environment === 'production' ? '#92400E' : C.greenMid,
                                    }}>{status.environment === 'production' ? 'PRODUCTION' : 'SANDBOX'}</span>
                                </div>
                                <div style={{ fontSize: 13, color: C.textSub }}>{status.companyName}</div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                            {[
                                { label: 'Realm ID', value: status.realmId },
                                { label: 'Connected', value: fmtDate(status.connectedAt) },
                                { label: 'Access token expires', value: fmtDate(status.tokenExpiresAt) },
                                { label: 'Refresh token expires', value: fmtDate(status.refreshTokenExpiresAt) },
                            ].map(({ label, value }) => (
                                <div key={label} style={{ background: C.bg, borderRadius: 8, padding: '10px 14px' }}>
                                    <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 500, marginBottom: 2 }}>{label}</div>
                                    <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{value}</div>
                                </div>
                            ))}
                        </div>

                        {isExpiringSoon && (
                            <div style={{
                                background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
                                padding: '10px 14px', fontSize: 13, color: '#92400e', marginBottom: 20,
                            }}>
                                ⚠ Refresh token expiring soon — reconnect to avoid sync interruption.
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: 10 }}>
                            <Btn variant="outline" onClick={onRefresh}>Refresh status</Btn>
                            <Btn variant="danger" onClick={onDisconnect} disabled={working}>
                                {working ? 'Disconnecting...' : 'Disconnect QuickBooks'}
                            </Btn>
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
                        <div style={{ fontWeight: 700, fontSize: 18, color: C.text, marginBottom: 8 }}>Connect QuickBooks Online</div>
                        <div style={{ fontSize: 14, color: C.textSub, maxWidth: 480, margin: '0 auto 24px' }}>
                            Link your QuickBooks Online account to automatically sync job payments, customer records, and expense entries — no manual bookkeeping required.
                        </div>
                        <div style={{ display: 'inline-flex', gap: 6, marginBottom: 16, background: C.bg, borderRadius: 8, padding: 4 }}>
                            {(['sandbox', 'production'] as const).map(env => (
                                <button key={env} type="button" onClick={() => onEnvironmentChange(env)}
                                    style={{
                                        padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                        fontSize: 13, fontWeight: 600, textTransform: 'capitalize',
                                        background: environment === env ? '#fff' : 'transparent',
                                        color: environment === env ? C.text : C.textMuted,
                                        boxShadow: environment === env ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                                    }}>{env}</button>
                            ))}
                        </div>
                        <div>
                            <Btn onClick={onConnect} disabled={working}>
                                {working ? 'Opening authorization...' : `Connect QuickBooks (${environment})`}
                            </Btn>
                        </div>
                        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 12 }}>
                            You'll be redirected to Intuit to authorize access. Only accounting data is synced.
                        </div>
                    </div>
                )}
            </Card>

            {status?.isConnected && (
                <Card style={{ marginTop: 16, padding: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 12 }}>What syncs automatically</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        {[
                            { icon: '💳', label: 'Job payments', desc: 'Invoice + Payment on every captured payment' },
                            { icon: '👤', label: 'Customer records', desc: 'QB Customer created on first booking' },
                            { icon: '📋', label: 'Service items', desc: 'Service types mapped to QB Products' },
                            { icon: '🔄', label: 'Idempotent sync', desc: 'Safe to retry — no duplicates created' },
                        ].map(({ icon, label, desc }) => (
                            <div key={label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                <div style={{ fontSize: 20, flexShrink: 0 }}>{icon}</div>
                                <div>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{label}</div>
                                    <div style={{ fontSize: 12, color: C.textSub }}>{desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}
        </div>
    );
}

function QboMappingTab({ isConnected }: { isConnected: boolean }) {
    const branding = useBranding();
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const [accounts, setAccounts] = useState<QboAccount[]>([]);
    const [items, setItems] = useState<QboItem[]>([]);
    const [mappings, setMappings] = useState<QboAccountMapping[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    const CATEGORIES = [
        { key: 'Revenue', label: 'Revenue account (job payments)' },
        { key: 'AccountsReceivable', label: 'Accounts Receivable account' },
        { key: 'PlatformFee', label: 'Platform Fee income account' },
        { key: 'ContractorPayout', label: `${contractorLabel} Payout expense account` },
        { key: 'BackgroundCheck', label: 'Background Check expense account' },
    ];

    // Was a hardcoded ['Cardboard','Furniture','YardDebris','General'] list — the backend has
    // always matched on the tenant's real PricingRule.ServiceTypeName (see
    // QuickBooksService.GetServiceItemMappingAsync: `$"Service:{serviceTypeName}"`), so a tenant
    // with renamed or custom service types (or LoadRunner, which this list used to omit entirely)
    // had no mapping row to fill in at all. Sourced from the tenant's actual active pricing rules
    // instead, once loaded below.
    const [serviceCategories, setServiceCategories] = useState<{ key: string; label: string }[]>([]);
    const allCategories = [...CATEGORIES, ...serviceCategories];

    useEffect(() => {
        if (!isConnected) { setLoading(false); return; }
        Promise.all([
            api.qboGetAccounts(),
            api.qboGetItems(),
            api.qboGetMappings(),
            api.getPricing(),
        ]).then(([accts, its, maps, rules]) => {
            setAccounts(accts);
            setItems(its);
            const svcCategories = rules
                .filter(r => r.isActive)
                .map(r => ({ key: `Service:${r.serviceTypeName}`, label: `Service item — ${r.serviceTypeName}` }));
            setServiceCategories(svcCategories);
            const categories = [...CATEGORIES, ...svcCategories];
            // Ensure all categories have an entry
            const mapByKey: Record<string, QboAccountMapping> = {};
            maps.forEach(m => { mapByKey[m.category] = m; });
            setMappings(categories.map(c => mapByKey[c.key] ?? {
                category: c.key, qboAccountId: '', qboAccountName: '', qboItemId: undefined, qboItemName: undefined,
            }));
        }).catch(e => setErrorMsg(e.message))
          .finally(() => setLoading(false));
    }, [isConnected]);

    const updateMapping = (category: string, field: keyof QboAccountMapping, value: string) => {
        setMappings(prev => prev.map(m => {
            if (m.category !== category) return m;
            if (field === 'qboAccountId') {
                const acct = accounts.find(a => a.id === value);
                return { ...m, qboAccountId: value, qboAccountName: acct?.name ?? '' };
            }
            if (field === 'qboItemId') {
                const item = items.find(i => i.id === value);
                return { ...m, qboItemId: value, qboItemName: item?.name ?? '' };
            }
            return { ...m, [field]: value };
        }));
    };

    const save = async () => {
        setSaving(true); setMsg(''); setErrorMsg('');
        try {
            await api.qboSaveMappings(mappings.filter(m => m.qboAccountId || m.qboItemId));
            setMsg('Mappings saved successfully.');
        } catch (e: any) { setErrorMsg(e.message); }
        finally { setSaving(false); }
    };

    if (!isConnected) {
        return (
            <Card style={{ textAlign: 'center', padding: 40, color: C.textMuted }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🔗</div>
                <div style={{ fontWeight: 600, color: C.textSub }}>Connect QuickBooks first</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>Account mapping requires an active QB connection.</div>
            </Card>
        );
    }

    if (loading) return <Spinner />;

    return (
        <div>
            <Alert message={errorMsg} />
            {msg && <div style={{ background: C.greenBg, color: C.greenMid, borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{msg}</div>}
            <Card style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: C.textSub, marginBottom: 20 }}>
                    Map ServiceTrek categories to your QuickBooks chart of accounts. Sync is blocked for unmapped required categories.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {allCategories.map(cat => {
                        const m = mappings.find(x => x.category === cat.key);
                        const isServiceItem = cat.key.startsWith('Service:');
                        return (
                            <div key={cat.key}>
                                <label style={{ fontSize: 12, fontWeight: 600, color: C.textSub, display: 'block', marginBottom: 6 }}>
                                    {cat.label}
                                </label>
                                <div style={{ display: 'grid', gridTemplateColumns: isServiceItem ? '1fr 1fr' : '1fr', gap: 10 }}>
                                    {isServiceItem ? (
                                        <select
                                            value={m?.qboItemId ?? ''}
                                            onChange={e => updateMapping(cat.key, 'qboItemId', e.target.value)}
                                            style={{ padding: '8px 10px', borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.bg }}
                                        >
                                            <option value="">— Select QB Item —</option>
                                            {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                                        </select>
                                    ) : (
                                        <select
                                            value={m?.qboAccountId ?? ''}
                                            onChange={e => updateMapping(cat.key, 'qboAccountId', e.target.value)}
                                            style={{ padding: '8px 10px', borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.bg }}
                                        >
                                            <option value="">— Select QB Account —</option>
                                            {accounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.accountType})</option>)}
                                        </select>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div style={{ marginTop: 20 }}>
                    <Btn onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save mappings'}</Btn>
                </div>
            </Card>
        </div>
    );
}

function QboErrorsTab({ isConnected }: { isConnected: boolean }) {
    const [errors, setErrors] = useState<QboSyncError[]>([]);
    const [loading, setLoading] = useState(true);
    const [retrying, setRetrying] = useState<string | null>(null);
    const [retryingAll, setRetryingAll] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const load = async () => {
        setLoading(true);
        try { setErrors(await api.qboGetSyncErrors()); }
        catch (e: any) { setErrorMsg(e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => { if (isConnected) load(); else setLoading(false); }, [isConnected]);

    const retry = async (id: string) => {
        setRetrying(id);
        try { await api.qboRetryError(id); await load(); }
        catch (e: any) { setErrorMsg(e.message); }
        finally { setRetrying(null); }
    };

    const retryAll = async () => {
        setRetryingAll(true);
        try { await api.qboRetryAll(); await load(); }
        catch (e: any) { setErrorMsg(e.message); }
        finally { setRetryingAll(false); }
    };

    if (!isConnected) {
        return (
            <Card style={{ textAlign: 'center', padding: 40, color: C.textMuted }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🔗</div>
                <div style={{ fontWeight: 600, color: C.textSub }}>Connect QuickBooks first</div>
            </Card>
        );
    }

    if (loading) return <Spinner />;

    return (
        <div>
            <Alert message={errorMsg} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 14, color: C.textSub }}>
                    {errors.length === 0 ? 'No unresolved sync errors' : `${errors.length} unresolved error${errors.length !== 1 ? 's' : ''}`}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Btn variant="outline" onClick={load}>Refresh</Btn>
                    {errors.length > 0 && (
                        <Btn onClick={retryAll} disabled={retryingAll}>{retryingAll ? 'Retrying...' : 'Retry all'}</Btn>
                    )}
                </div>
            </div>

            {errors.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 48, color: C.textMuted }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                    <div style={{ fontWeight: 600, color: C.textSub, fontSize: 16 }}>All syncs healthy</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>No failed syncs in the last 30 days.</div>
                </Card>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {errors.map(err => (
                        <Card key={err.id} style={{ padding: '14px 18px', borderLeft: `4px solid #ef4444` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                        <span style={{
                                            fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 99,
                                            background: '#fef2f2', color: '#ef4444',
                                        }}>{err.entityType}</span>
                                        <span style={{ fontSize: 12, color: C.textMuted }}>
                                            ID: {err.entityId.slice(0, 8)}...
                                        </span>
                                        {err.retryCount > 0 && (
                                            <span style={{ fontSize: 12, color: C.textMuted }}>
                                                · {err.retryCount} retr{err.retryCount === 1 ? 'y' : 'ies'}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 13, color: C.text, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                        {err.errorMessage}
                                    </div>
                                    <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
                                        Failed: {new Date(err.failedAt).toLocaleString()}
                                    </div>
                                </div>
                                <Btn
                                    variant="outline"
                                    onClick={() => retry(err.id)}
                                    disabled={retrying === err.id}
                                    style={{ flexShrink: 0 }}
                                >
                                    {retrying === err.id ? '...' : 'Retry'}
                                </Btn>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Salesforce CRM integration (first entry in the "CRM" App Marketplace category) ───────
// Mirrors QuickBooksPanel's shape (connection/mapping/errors tabs), but the mapping tab hits
// the generic CrmFieldMapping endpoints (provider="Salesforce") instead of QBO's account-based
// ones, so a future second CRM provider reuses the same mapping UI shape with a different
// provider value rather than a rewrite.

type SfdcTab = 'connection' | 'mapping' | 'errors';

const SFDC_ENTITY_FIELDS: Record<string, { defaultTarget: string; sourceFields: string[] }> = {
    Prospect: { defaultTarget: 'Lead', sourceFields: ['Name', 'Email', 'Phone', 'BillingAddress', 'BillingCity', 'BillingState', 'BillingPostalCode', 'BillingCountry'] },
    Job: { defaultTarget: 'Opportunity', sourceFields: ['Name', 'Amount', 'StageName', 'CloseDate'] },
};

function SalesforcePanel() {
    const [tab, setTab] = useState<SfdcTab>('connection');
    const [status, setStatus] = useState<SalesforceConnectionStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [working, setWorking] = useState(false);
    const [environment, setEnvironment] = useState<'sandbox' | 'production'>('sandbox');

    const loadStatus = async () => {
        setLoading(true);
        try { setStatus(await api.sfdcStatus()); }
        catch (e: any) { setErrorMsg(e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sfdc = params.get('sfdc');
        if (sfdc === 'connected') {
            setSuccessMsg('Salesforce connected successfully!');
            window.history.replaceState({}, '', window.location.pathname);
        } else if (sfdc === 'error') {
            setErrorMsg(params.get('msg') || 'Salesforce connection failed.');
            window.history.replaceState({}, '', window.location.pathname);
        }
        loadStatus();
    }, []);

    const handleConnect = async () => {
        setWorking(true); setErrorMsg(''); setSuccessMsg('');
        try {
            const { url } = await api.sfdcGetConnectUrl(environment);
            window.location.href = url;
        } catch (e: any) { setErrorMsg(e.message); setWorking(false); }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Disconnect Salesforce? Existing synced records will remain in Salesforce but no new syncs will occur.')) return;
        setWorking(true); setErrorMsg('');
        try { await api.sfdcDisconnect(); await loadStatus(); }
        catch (e: any) { setErrorMsg(e.message); }
        finally { setWorking(false); }
    };

    const tabs: { key: SfdcTab; label: string }[] = [
        { key: 'connection', label: 'Connection' },
        { key: 'mapping', label: 'Field Mapping' },
        { key: 'errors', label: 'Sync Errors' },
    ];

    return (
        <div style={{ maxWidth: 900 }}>
            <div style={{ marginBottom: 24 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.text }}>Salesforce</h2>
                <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>Two-way CRM sync for prospects and jobs</div>
            </div>
            <Alert message={errorMsg} />
            {successMsg && (
                <div style={{ background: C.greenBg, color: C.greenMid, borderRadius: 8, padding: '10px 14px', fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
                    ✓ {successMsg}
                </div>
            )}

            <div style={{ display: 'flex', gap: 2, marginBottom: 24, borderBottom: `2px solid ${C.border}` }}>
                {tabs.map(t => (
                    <button key={t.key} onClick={() => setTab(t.key)} style={{
                        padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
                        fontSize: 13, fontWeight: 600,
                        color: tab === t.key ? C.greenMid : C.textSub,
                        borderBottom: tab === t.key ? `2px solid ${C.greenMid}` : '2px solid transparent',
                        marginBottom: -2,
                    }}>{t.label}</button>
                ))}
            </div>

            {loading ? <Spinner /> : (
                <>
                    {tab === 'connection' && (
                        <SfdcConnectionTab status={status} working={working} environment={environment} onEnvironmentChange={setEnvironment} onConnect={handleConnect} onDisconnect={handleDisconnect} onRefresh={loadStatus} />
                    )}
                    {tab === 'mapping' && (
                        <SfdcMappingTab isConnected={!!status?.isConnected} />
                    )}
                    {tab === 'errors' && (
                        <SfdcErrorsTab isConnected={!!status?.isConnected} />
                    )}
                </>
            )}
        </div>
    );
}

function SfdcConnectionTab({
    status, working, environment, onEnvironmentChange, onConnect, onDisconnect, onRefresh,
}: {
    status: SalesforceConnectionStatus | null;
    working: boolean;
    environment: 'sandbox' | 'production';
    onEnvironmentChange: (env: 'sandbox' | 'production') => void;
    onConnect: () => void;
    onDisconnect: () => void;
    onRefresh: () => void;
}) {
    const [copied, setCopied] = useState(false);
    const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

    const copyWebhook = () => {
        if (!status?.webhookUrl) return;
        navigator.clipboard.writeText(status.webhookUrl).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div>
            <Card style={{ padding: 28 }}>
                {status?.isConnected ? (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: '50%', background: C.greenBg,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
                            }}>✓</div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 16, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    Connected
                                    <span style={{
                                        fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                                        background: status.environment === 'production' ? '#FEF3C7' : C.greenBg,
                                        color: status.environment === 'production' ? '#92400E' : C.greenMid,
                                    }}>{status.environment === 'production' ? 'PRODUCTION' : 'SANDBOX'}</span>
                                </div>
                                <div style={{ fontSize: 13, color: C.textSub }}>{status.organizationName || status.instanceUrl}</div>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                            {[
                                { label: 'Instance URL', value: status.instanceUrl },
                                { label: 'Connected', value: fmtDate(status.connectedAt) },
                                { label: 'Access token expires', value: fmtDate(status.tokenExpiresAt) },
                            ].map(({ label, value }) => (
                                <div key={label} style={{ background: C.bg, borderRadius: 8, padding: '10px 14px' }}>
                                    <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 500, marginBottom: 2 }}>{label}</div>
                                    <div style={{ fontSize: 13, color: C.text, fontWeight: 600, wordBreak: 'break-all' }}>{value}</div>
                                </div>
                            ))}
                        </div>

                        <div style={{ display: 'flex', gap: 10 }}>
                            <Btn variant="outline" onClick={onRefresh}>Refresh status</Btn>
                            <Btn variant="danger" onClick={onDisconnect} disabled={working}>
                                {working ? 'Disconnecting...' : 'Disconnect Salesforce'}
                            </Btn>
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div style={{ fontSize: 48, marginBottom: 16 }}>☁️</div>
                        <div style={{ fontWeight: 700, fontSize: 18, color: C.text, marginBottom: 8 }}>Connect Salesforce</div>
                        <div style={{ fontSize: 14, color: C.textSub, maxWidth: 480, margin: '0 auto 24px' }}>
                            Link your Salesforce org to push prospects and jobs as Leads and Opportunities, and pull changes made in Salesforce back into ServiceTrek.
                        </div>
                        <div style={{ display: 'inline-flex', gap: 6, marginBottom: 16, background: C.bg, borderRadius: 8, padding: 4 }}>
                            {(['sandbox', 'production'] as const).map(env => (
                                <button key={env} type="button" onClick={() => onEnvironmentChange(env)}
                                    style={{
                                        padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                        fontSize: 13, fontWeight: 600, textTransform: 'capitalize',
                                        background: environment === env ? '#fff' : 'transparent',
                                        color: environment === env ? C.text : C.textMuted,
                                        boxShadow: environment === env ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                                    }}>{env}</button>
                            ))}
                        </div>
                        <div>
                            <Btn onClick={onConnect} disabled={working}>
                                {working ? 'Opening authorization...' : `Connect Salesforce (${environment})`}
                            </Btn>
                        </div>
                        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 12 }}>
                            You'll be redirected to Salesforce to authorize access.
                        </div>
                    </div>
                )}
            </Card>

            {status?.isConnected && status.webhookUrl && (
                <Card style={{ marginTop: 16, padding: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 6 }}>Enable changes flowing back from Salesforce</div>
                    <div style={{ fontSize: 13, color: C.textSub, marginBottom: 12 }}>
                        In your Salesforce org, configure a Flow or Outbound Message to POST to this URL whenever a Lead or Opportunity changes. This is what lets edits made in Salesforce update the matching ServiceTrek record.
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <input readOnly value={status.webhookUrl} style={{
                            flex: 1, padding: '8px 10px', borderRadius: 7, border: `1px solid ${C.border}`,
                            fontSize: 12, fontFamily: 'monospace', color: C.text, background: C.bg,
                        }} onFocus={e => e.target.select()} />
                        <Btn variant="outline" onClick={copyWebhook}>{copied ? 'Copied!' : 'Copy'}</Btn>
                    </div>
                </Card>
            )}
        </div>
    );
}

function SfdcMappingTab({ isConnected }: { isConnected: boolean }) {
    const [mappingsByEntity, setMappingsByEntity] = useState<Record<string, CrmFieldMapping>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState<string | null>(null);
    const [msg, setMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');

    const entityTypes = Object.keys(SFDC_ENTITY_FIELDS);

    useEffect(() => {
        if (!isConnected) { setLoading(false); return; }
        api.crmGetMappings('Salesforce').then(maps => {
            const byEntity: Record<string, CrmFieldMapping> = {};
            maps.forEach(m => { byEntity[m.entityType] = m; });
            setMappingsByEntity(byEntity);
        }).catch(e => setErrorMsg(e.message))
          .finally(() => setLoading(false));
    }, [isConnected]);

    const getMapping = (entityType: string): CrmFieldMapping => mappingsByEntity[entityType] ?? {
        id: '', provider: 'Salesforce', entityType,
        targetObject: SFDC_ENTITY_FIELDS[entityType].defaultTarget,
        fieldMappings: {}, updatedAt: '',
    };

    const updateTargetObject = (entityType: string, targetObject: string) => {
        setMappingsByEntity(prev => ({ ...prev, [entityType]: { ...getMapping(entityType), targetObject } }));
    };

    const updateFieldMapping = (entityType: string, sourceField: string, targetField: string) => {
        setMappingsByEntity(prev => {
            const current = getMapping(entityType);
            const fieldMappings = { ...current.fieldMappings };
            if (targetField.trim()) fieldMappings[sourceField] = targetField.trim();
            else delete fieldMappings[sourceField];
            return { ...prev, [entityType]: { ...current, fieldMappings } };
        });
    };

    const save = async (entityType: string) => {
        setSaving(entityType); setMsg(''); setErrorMsg('');
        try {
            const m = getMapping(entityType);
            const saved = await api.crmSaveMapping({
                provider: 'Salesforce', entityType,
                targetObject: m.targetObject, fieldMappings: m.fieldMappings,
            });
            setMappingsByEntity(prev => ({ ...prev, [entityType]: saved }));
            setMsg(`${entityType} mapping saved.`);
        } catch (e: any) { setErrorMsg(e.message); }
        finally { setSaving(null); }
    };

    if (!isConnected) {
        return (
            <Card style={{ textAlign: 'center', padding: 40, color: C.textMuted }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🔗</div>
                <div style={{ fontWeight: 600, color: C.textSub }}>Connect Salesforce first</div>
                <div style={{ fontSize: 13, marginTop: 4 }}>Field mapping requires an active Salesforce connection.</div>
            </Card>
        );
    }

    if (loading) return <Spinner />;

    return (
        <div>
            <Alert message={errorMsg} />
            {msg && <div style={{ background: C.greenBg, color: C.greenMid, borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>{msg}</div>}
            <div style={{ fontSize: 13, color: C.textSub, marginBottom: 16 }}>
                Nothing syncs for an entity until it has a saved mapping. Leave a field's Salesforce column blank to leave it out of the sync.
            </div>
            {entityTypes.map(entityType => {
                const m = getMapping(entityType);
                return (
                    <Card key={entityType} style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{entityType}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <label style={{ fontSize: 12, color: C.textSub }}>Salesforce object</label>
                                <input
                                    value={m.targetObject}
                                    onChange={e => updateTargetObject(entityType, e.target.value)}
                                    placeholder={SFDC_ENTITY_FIELDS[entityType].defaultTarget}
                                    style={{ padding: '6px 10px', borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.bg, width: 140 }}
                                />
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {SFDC_ENTITY_FIELDS[entityType].sourceFields.map(field => (
                                <div key={field} style={{ display: 'grid', gridTemplateColumns: '1fr 20px 1fr', alignItems: 'center', gap: 10 }}>
                                    <div style={{ fontSize: 13, color: C.text, fontFamily: 'monospace' }}>{field}</div>
                                    <div style={{ textAlign: 'center', color: C.textMuted }}>→</div>
                                    <input
                                        value={m.fieldMappings[field] ?? ''}
                                        onChange={e => updateFieldMapping(entityType, field, e.target.value)}
                                        placeholder="Salesforce field API name"
                                        style={{ padding: '7px 10px', borderRadius: 7, border: `1px solid ${C.border}`, fontSize: 13, color: C.text, background: C.bg }}
                                    />
                                </div>
                            ))}
                        </div>
                        <div style={{ marginTop: 16 }}>
                            <Btn onClick={() => save(entityType)} disabled={saving === entityType}>
                                {saving === entityType ? 'Saving...' : `Save ${entityType} mapping`}
                            </Btn>
                        </div>
                    </Card>
                );
            })}
        </div>
    );
}

function SfdcErrorsTab({ isConnected }: { isConnected: boolean }) {
    const [errors, setErrors] = useState<CrmSyncError[]>([]);
    const [loading, setLoading] = useState(true);
    const [retrying, setRetrying] = useState<string | null>(null);
    const [retryingAll, setRetryingAll] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const load = async () => {
        setLoading(true);
        try { setErrors(await api.sfdcGetSyncErrors()); }
        catch (e: any) { setErrorMsg(e.message); }
        finally { setLoading(false); }
    };

    useEffect(() => { if (isConnected) load(); else setLoading(false); }, [isConnected]);

    const retry = async (id: string) => {
        setRetrying(id);
        try { await api.sfdcRetryError(id); await load(); }
        catch (e: any) { setErrorMsg(e.message); }
        finally { setRetrying(null); }
    };

    const retryAll = async () => {
        setRetryingAll(true);
        try { await api.sfdcRetryAll(); await load(); }
        catch (e: any) { setErrorMsg(e.message); }
        finally { setRetryingAll(false); }
    };

    if (!isConnected) {
        return (
            <Card style={{ textAlign: 'center', padding: 40, color: C.textMuted }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>🔗</div>
                <div style={{ fontWeight: 600, color: C.textSub }}>Connect Salesforce first</div>
            </Card>
        );
    }

    if (loading) return <Spinner />;

    return (
        <div>
            <Alert message={errorMsg} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 14, color: C.textSub }}>
                    {errors.length === 0 ? 'No unresolved sync errors' : `${errors.length} unresolved error${errors.length !== 1 ? 's' : ''}`}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <Btn variant="outline" onClick={load}>Refresh</Btn>
                    {errors.length > 0 && (
                        <Btn onClick={retryAll} disabled={retryingAll}>{retryingAll ? 'Retrying...' : 'Retry all'}</Btn>
                    )}
                </div>
            </div>

            {errors.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 48, color: C.textMuted }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                    <div style={{ fontWeight: 600, color: C.textSub, fontSize: 16 }}>All syncs healthy</div>
                    <div style={{ fontSize: 13, marginTop: 4 }}>No failed syncs in the last 30 days.</div>
                </Card>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {errors.map(err => (
                        <Card key={err.id} style={{ padding: '14px 18px', borderLeft: `4px solid #ef4444` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                        <span style={{
                                            fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 99,
                                            background: '#fef2f2', color: '#ef4444',
                                        }}>{err.entityType}</span>
                                        <span style={{ fontSize: 12, color: C.textMuted }}>
                                            ID: {err.entityId.slice(0, 8)}...
                                        </span>
                                        {err.retryCount > 0 && (
                                            <span style={{ fontSize: 12, color: C.textMuted }}>
                                                · {err.retryCount} retr{err.retryCount === 1 ? 'y' : 'ies'}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 13, color: C.text, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                        {err.errorMessage}
                                    </div>
                                    <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
                                        Failed: {new Date(err.failedAt).toLocaleString()}
                                    </div>
                                </div>
                                <Btn
                                    variant="outline"
                                    onClick={() => retry(err.id)}
                                    disabled={retrying === err.id}
                                    style={{ flexShrink: 0 }}
                                >
                                    {retrying === err.id ? '...' : 'Retry'}
                                </Btn>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── TREK-67: Unified Dispatch & Calendar Panel ────────────────────────────────

type CalendarMode = 'month' | 'week' | 'day';

// A customer name/address like =HYPERLINK("http://evil","x") is stored as plain text but
// executes as a live formula the moment an admin opens the CSV export in Excel or Sheets — both
// treat a leading =, +, -, or @ as a formula prefix regardless of quoting. Prefixing with a
// single quote forces the cell to render as literal text in both, neutralizing it.
function csvFormulaSafe(value: string): string {
    return /^[=+\-@]/.test(value) ? `'${value}` : value;
}

function fmtJobDuration(startedAt?: string, completedAt?: string): string {
    if (!startedAt || !completedAt) return '—';
    const mins = Math.round((new Date(completedAt).getTime() - new Date(startedAt).getTime()) / 60000);
    if (mins < 60) return `${mins}m`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

const STATUS_COLORS: Record<string, string> = {
    Pending: '#6366F1', Accepted: '#3B82F6', EnRoute: '#F59E0B',
    Arrived: '#F97316', InProgress: '#8B5CF6', Completed: C.greenMid, Cancelled: '#9CA3AF',
};

// ── Estimates (TREK-78 follow-up) — admin can view/build/send an estimate for an ──
// active job, same as the contractor's JobPricingBuilder in ContractorPortal.tsx.

function JobDetailEstimateSection({ jobId }: { jobId: string }) {
    const [catalog, setCatalog] = useState<ServiceCatalogItemDto[]>([]);
    const [lineItems, setLineItems] = useState<JobLineItemDto[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [estimate, setEstimate] = useState<EstimateDto | null>(null);
    const [sendingEstimate, setSendingEstimate] = useState(false);

    const [showAdd, setShowAdd] = useState(false);
    const [selectedCatalogId, setSelectedCatalogId] = useState('');
    const [customDescription, setCustomDescription] = useState('');
    const [customPrice, setCustomPrice] = useState('');
    const [quantity, setQuantity] = useState('1');
    const [afterHours, setAfterHours] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true); setError('');
        Promise.all([api.getServiceCatalog(), api.getJobLineItems(jobId), api.getEstimate(jobId)])
            .then(([items, summary, est]) => {
                setCatalog(items);
                setLineItems(summary.lineItems);
                setTotal(summary.total);
                setEstimate(est);
            })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [jobId]);

    const sendEstimate = async () => {
        setSendingEstimate(true); setError('');
        try { await api.sendEstimate(jobId); load(); }
        catch (e: any) { setError(e.message); }
        finally { setSendingEstimate(false); }
    };

    const addItem = async () => {
        setSaving(true); setError('');
        try {
            const qty = parseFloat(quantity);
            if (isNaN(qty) || qty <= 0) { setError('Enter a valid quantity.'); return; }
            if (selectedCatalogId) {
                await api.addJobLineItem(jobId, selectedCatalogId, null, qty, null, afterHours);
            } else {
                if (!customDescription.trim() || !customPrice) { setError('Description and price are required for a custom part.'); return; }
                await api.addJobLineItem(jobId, null, customDescription.trim(), qty, parseFloat(customPrice), afterHours);
            }
            setShowAdd(false);
            setSelectedCatalogId(''); setCustomDescription(''); setCustomPrice(''); setQuantity('1'); setAfterHours(false);
            load();
        } catch (e: any) { setError(e.message); }
        finally { setSaving(false); }
    };

    const removeItem = async (lineItemId: string) => {
        setError('');
        try { await api.removeJobLineItem(jobId, lineItemId); load(); }
        catch (e: any) { setError(e.message); }
    };

    if (loading) return null;

    const locked = estimate?.status === 'Pending';

    return (
        <div style={{ marginTop: 8, padding: 10, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                Estimate
            </div>
            <Alert message={error} />

            {estimate?.status === 'Pending' && (
                <div style={{ background: '#FAEEDA', color: '#854F0B', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontWeight: 600, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>⏳ #E-{estimate.number} sent — awaiting customer approval</span>
                    <a href="#" onClick={e => { e.preventDefault(); api.downloadEstimatePdf(jobId, estimate.number).catch(err => setError(err.message)); }} style={{ color: '#854F0B' }}>PDF ↗</a>
                </div>
            )}
            {estimate?.status === 'ChangesRequested' && (
                <div style={{ background: '#FDECEA', color: '#9B1C1C', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                    ⚠ Customer requested changes{estimate.customerNote ? `: "${estimate.customerNote}"` : ''}
                </div>
            )}
            {estimate?.status === 'Approved' && (
                <div style={{ background: C.greenBg, color: C.green, borderRadius: 6, padding: '8px 10px', fontSize: 12, fontWeight: 600, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>✓ #E-{estimate.number} approved</span>
                    <a href="#" onClick={e => { e.preventDefault(); api.downloadEstimatePdf(jobId, estimate.number).catch(err => setError(err.message)); }} style={{ color: C.green }}>PDF ↗</a>
                </div>
            )}

            {lineItems.map(li => (
                <div key={li.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px dashed ${C.borderLight}`, fontSize: 13 }}>
                    <span>{li.description}{li.quantity !== 1 ? ` × ${li.quantity}` : ''}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 600 }}>${li.lineTotal.toFixed(2)}</span>
                        {!locked && (
                            <button onClick={() => removeItem(li.id)} title="Remove"
                                style={{ background: 'none', border: 'none', color: '#C0392B', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
                        )}
                    </div>
                </div>
            ))}
            {lineItems.length === 0 && <div style={{ fontSize: 12, color: C.textMuted, padding: '6px 0' }}>No items yet.</div>}

            {!locked && showAdd ? (
                <div style={{ marginTop: 10, padding: 10, background: '#fff', borderRadius: 6, border: `1px solid ${C.border}` }}>
                    <div style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 11, fontWeight: 500, color: C.textSub, display: 'block', marginBottom: 4 }}>Catalog item</label>
                        <select value={selectedCatalogId} onChange={e => setSelectedCatalogId(e.target.value)}
                            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13 }}>
                            <option value="">— Custom part (not in catalog) —</option>
                            {catalog.map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.name} · {c.pricingType === 'Hourly' ? 'hourly' : `$${c.price.toFixed(2)}`}
                                </option>
                            ))}
                        </select>
                    </div>
                    {!selectedCatalogId && (
                        <>
                            <div style={{ marginBottom: 8 }}>
                                <Input label="Description" value={customDescription} onChange={setCustomDescription} placeholder="e.g. Capacitor" />
                            </div>
                            <div style={{ marginBottom: 8 }}>
                                <Input label="Unit price ($)" value={customPrice} onChange={setCustomPrice} type="number" placeholder="48.00" />
                            </div>
                        </>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        <div style={{ flex: 1 }}>
                            <Input label={selectedCatalogId && catalog.find(c => c.id === selectedCatalogId)?.pricingType === 'Hourly' ? 'Hours' : 'Quantity'} value={quantity} onChange={setQuantity} type="number" />
                        </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, cursor: 'pointer' }}>
                        <input type="checkbox" checked={afterHours} onChange={e => setAfterHours(e.target.checked)} style={{ accentColor: C.greenMid }} />
                        <span style={{ fontSize: 12, color: C.text }}>After-hours / emergency rate</span>
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Btn size="sm" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Btn>
                        <Btn size="sm" onClick={addItem} disabled={saving}>{saving ? 'Adding...' : 'Add'}</Btn>
                    </div>
                </div>
            ) : !locked ? (
                <Btn size="sm" variant="outline" onClick={() => setShowAdd(true)} style={{ width: '100%', marginTop: 8 }}>+ Add Item / Part</Btn>
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`, fontWeight: 700, fontSize: 14 }}>
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
            </div>

            {!locked && estimate?.status !== 'Approved' && lineItems.length > 0 && (
                <Btn onClick={sendEstimate} disabled={sendingEstimate} style={{ width: '100%', marginTop: 10 }}>
                    {sendingEstimate ? 'Sending...' : 'Send Estimate to Customer'}
                </Btn>
            )}
        </div>
    );
}

// ── Digital work order & signature (TREK-88) ────────────────────────────────────

function JobDetailWorkOrderSection({ jobId }: { jobId: string }) {
    const [workOrder, setWorkOrder] = useState<WorkOrderDto | null>(null);

    useEffect(() => { api.getWorkOrder(jobId).then(setWorkOrder).catch(() => {}); }, [jobId]);

    if (!workOrder) return null;

    return (
        <div style={{ marginTop: 8, padding: '8px 10px', background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 12, color: C.textSub, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>🖊 Work order: <strong style={{ color: workOrder.status === 'Signed' ? C.green : '#854F0B' }}>{workOrder.status}</strong></span>
                {workOrder.status === 'Signed' && workOrder.signedPdfUrl && (
                    <a href={workOrder.signedPdfUrl} target="_blank" rel="noreferrer" style={{ color: C.greenMid, fontSize: 12 }}>View PDF ↗</a>
                )}
            </div>
        </div>
    );
}

function DispatchCalendarPanel({ contractors, pricing = [] }: { contractors: ContractorListItem[]; pricing?: PricingRule[] }) {
    const branding = useBranding();
    const contractorLabel = branding.contractorNoun || 'Contractor';
    const contractorLabelPlural = branding.contractorNoun ? branding.contractorNoun + 's' : 'Contractors';
    const certTrackingEnabled = useFeature('contractorCertTracking');
    const digitalWorkOrdersEnabled = useFeature('digitalWorkOrders');
    const serviceCatalogEnabled = useFeature('serviceCatalogPricing');
    const bgCheckRequired = useFeature('backgroundCheckRequired');
    const [mode, setMode] = useState<CalendarMode>('month');
    const [cursor, setCursor] = useState(() => new Date());
    const [jobs, setJobs] = useState<CalendarJob[]>([]);
    const [unassigned, setUnassigned] = useState<CalendarJob[]>([]);
    const [assigned, setAssigned] = useState<Record<string, CalendarJob[]>>({});
    const [contractorAvails, setContractorAvails] = useState<ContractorAvailabilityItem[]>([]);
    const [contractorFilter, setContractorFilter] = useState('');
    const [loading, setLoading] = useState(true);
    const [panelError, setPanelError] = useState('');
    const [dragJobId, setDragJobId] = useState<string | null>(null);
    const [dropHighlight, setDropHighlight] = useState<string | null>(null);
    const [pickerTarget, setPickerTarget] = useState<{ jobId: string; date: string } | null>(null);
    const [selectedJob, setSelectedJob] = useState<CalendarJob | null>(null);
    const [cancellingJob, setCancellingJob] = useState<CalendarJob | null>(null);
    const [rescheduleTarget, setRescheduleTarget] = useState<CalendarJob | null>(null);
    const [rescheduleDate, setRescheduleDate] = useState('');
    const [rescheduleTime, setRescheduleTime] = useState('');
    const [rescheduleSaving, setRescheduleSaving] = useState(false);
    const [rescheduleError, setRescheduleError] = useState('');
    const [routeMap, setRouteMap] = useState<Record<string, any>>({});
    const [payoutOverrideInput, setPayoutOverrideInput] = useState('');
    const [payoutOverrideSaving, setPayoutOverrideSaving] = useState(false);
    const [payoutOverrideError, setPayoutOverrideError] = useState('');
    const hubRef = useRef<any>(null);

    const fmtDate = (d: Date) => d.toISOString().split('T')[0];

    const { start, end } = React.useMemo(() => {
        if (mode === 'month') {
            const s = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
            const e = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
            return { start: s, end: e };
        } else if (mode === 'week') {
            const dow = cursor.getDay();
            const s = new Date(cursor); s.setDate(cursor.getDate() - dow);
            const e = new Date(s); e.setDate(s.getDate() + 6);
            return { start: s, end: e };
        } else {
            return { start: new Date(cursor), end: new Date(cursor) };
        }
    }, [mode, cursor]);

    const dayViewDate = fmtDate(cursor);

    const loadAll = React.useCallback(async () => {
        setLoading(true);
        try {
            const [j, u, sa, avails] = await Promise.all([
                api.getCalendarJobs(fmtDate(start), fmtDate(end)),
                api.getUnassignedJobs(),
                api.getSelfAssignedJobs(),
                api.getContractorAvailabilities(),
            ]);
            setJobs(j);
            setUnassigned(u);
            setContractorAvails(avails);
            const byContractor: Record<string, CalendarJob[]> = {};
            j.forEach(job => {
                if (job.contractorId) {
                    if (!byContractor[job.contractorId]) byContractor[job.contractorId] = [];
                    byContractor[job.contractorId].push(job);
                }
            });
            sa.forEach(job => {
                if (!job.contractorId) return;
                if (!byContractor[job.contractorId]) byContractor[job.contractorId] = [];
                if (!byContractor[job.contractorId].some(e => e.id === job.id))
                    byContractor[job.contractorId].push({ ...job, selfAssigned: true } as any);
            });
            setAssigned(byContractor);
        } catch (e: any) { setPanelError(e.message); }
        finally { setLoading(false); }
    }, [start, end]);

    useEffect(() => { loadAll(); }, [loadAll]);

    // Join admin SignalR pool for real-time board refresh
    useEffect(() => {
        const token = localStorage.getItem('br_token');
        if (!token) return;
        const hub = new signalR.HubConnectionBuilder()
            .withUrl(`${API_BASE}/hubs/tracking?access_token=${token}`, { withCredentials: false })
            .withAutomaticReconnect()
            .build();
        hub.on('JobUpdated', () => loadAll());
        hub.on('JobRescheduled', () => loadAll());
        hub.start()
            .then(() => hub.invoke('JoinAdminPool').catch(() => {}))
            .catch(() => {});
        hubRef.current = hub;
        return () => { hub.stop(); };
    }, [loadAll]);

    // Idle-gated — same reasoning as the contractor-status hub above: this dispatch board is
    // realistically left open on a screen all day, so it needs a real inactivity signal, not just
    // "still mounted," to ever let the serverless DB reach a clean pause window.
    useIdleDisconnect({
        onIdle: () => { hubRef.current?.stop().catch(() => {}); },
        onActive: () => {
            const hub = hubRef.current;
            if (hub && hub.state === signalR.HubConnectionState.Disconnected) {
                hub.start().then(() => hub.invoke('JoinAdminPool').catch(() => {})).catch(() => {});
            }
        },
    });

    // Availability helpers
    const isAvailableOnDate = (contractorId: string, ymd: string): boolean => {
        const avail = contractorAvails.find(a => a.contractorId === contractorId);
        if (!avail) return true;
        if (avail.blockedDates.includes(ymd)) return false;
        if (avail.weeklySlots.length === 0) return true;
        const dayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date(ymd + 'T12:00:00').getDay()];
        return avail.weeklySlots.some(s => s.dayOfWeek === dayName);
    };

    const isDateBlockedForFilter = (ymd: string): boolean =>
        !!contractorFilter && !isAvailableOnDate(contractorFilter, ymd);

    const jobsByDate = React.useMemo(() => {
        const map: Record<string, CalendarJob[]> = {};
        const filtered = contractorFilter ? jobs.filter(j => j.contractorId === contractorFilter) : jobs;
        filtered.forEach(j => {
            if (!j.scheduledDate) return;
            const key = j.scheduledDate.split('T')[0];
            if (!map[key]) map[key] = [];
            map[key].push(j);
        });
        return map;
    }, [jobs, contractorFilter]);

    // Drop on calendar date (month/week views)
    const handleDropOnDate = async (ymd: string) => {
        if (!dragJobId) return;
        const jobId = dragJobId;
        setDragJobId(null); setDropHighlight(null);
        const job = unassigned.find(j => j.id === jobId) ?? jobs.find(j => j.id === jobId);
        if (!job) return;
        if (job.contractorId) {
            try { await api.rescheduleJob(jobId, ymd, undefined); await loadAll(); }
            catch (e: any) { setPanelError(e.message); }
        } else {
            setPickerTarget({ jobId, date: ymd });
        }
    };

    // Drop on contractor column (day view)
    const handleDropOnContractor = async (contractorId: string) => {
        if (!dragJobId) return;
        const c = contractors.find(x => x.id === contractorId);
        if (!c || (bgCheckRequired && c.backgroundCheckStatus !== 'Clear') || !c.docsApproved) {
            setPanelError(`${contractorLabel} is not eligible: background check or documents not approved.`);
            setDragJobId(null);
            return;
        }
        try {
            await api.assignJob(dragJobId, contractorId, dayViewDate);
            setDragJobId(null);
            await loadAll();
        } catch (e: any) { setPanelError(e.message); }
    };

    const handleUnassign = async (jobId: string) => {
        try { await api.unassignJob(jobId); await loadAll(); }
        catch (e: any) { setPanelError(e.message); }
    };

    const handleCancelJob = async (jobId: string, reason: string) => {
        try { await api.cancelJob(jobId, reason); setCancellingJob(null); setSelectedJob(null); await loadAll(); }
        catch (e: any) { setPanelError(e.message); }
    };

    const handlePickContractor = async (contractorId: string) => {
        if (!pickerTarget) return;
        try {
            await api.assignJob(pickerTarget.jobId, contractorId, pickerTarget.date);
            setPickerTarget(null);
            await loadAll();
        } catch (e: any) { setPanelError(e.message); }
    };

    const openReschedule = (j: CalendarJob) => {
        setRescheduleTarget(j);
        setRescheduleDate(j.scheduledDate ? j.scheduledDate.slice(0, 10) : j.requestedDate ? j.requestedDate.slice(0, 10) : '');
        setRescheduleTime(j.scheduledTimeSlot ?? '');
        setRescheduleError('');
    };

    const saveReschedule = async () => {
        if (!rescheduleTarget || !rescheduleDate) { setRescheduleError('Please select a date.'); return; }
        setRescheduleSaving(true); setRescheduleError('');
        try {
            await api.rescheduleJob(rescheduleTarget.id, rescheduleDate, rescheduleTime || undefined);
            setRescheduleTarget(null);
            await loadAll();
        } catch (e: any) { setRescheduleError(e.message || 'Failed to save.'); }
        finally { setRescheduleSaving(false); }
    };

    const loadRoute = async (contractorId: string) => {
        try {
            const r = await api.getOptimizedRoute(contractorId, dayViewDate);
            setRouteMap(prev => ({ ...prev, [contractorId]: r }));
        } catch { /* ignore */ }
    };

    const applyRoute = async (contractorId: string) => {
        const r = routeMap[contractorId];
        if (!r) return;
        try {
            await api.applyRoute(contractorId, dayViewDate, r.stops.map((s: any) => s.jobId));
            await loadAll();
        } catch (e: any) { setPanelError(e.message); }
    };

    const navigateCal = (dir: -1 | 1) => {
        const d = new Date(cursor);
        if (mode === 'month') d.setMonth(d.getMonth() + dir);
        else if (mode === 'week') d.setDate(d.getDate() + dir * 7);
        else d.setDate(d.getDate() + dir);
        setCursor(d);
    };

    const calTitle = mode === 'month'
        ? cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        : mode === 'week'
        ? `Week of ${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
        : cursor.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

    if (loading) return <Spinner />;

    return (
        <>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
            {/* Main calendar panel */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button onClick={() => navigateCal(-1)} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>‹</button>
                        <span style={{ fontWeight: 700, fontSize: 16, minWidth: 180, textAlign: 'center' }}>{calTitle}</span>
                        <button onClick={() => navigateCal(1)} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>›</button>
                        <Btn size="sm" variant="outline" onClick={() => setCursor(new Date())}>Today</Btn>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select value={contractorFilter} onChange={e => setContractorFilter(e.target.value)}
                            style={{ padding: '5px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, background: C.card, color: C.text }}>
                            <option value="">All {contractorLabelPlural.toLowerCase()}</option>
                            {contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        {(['month', 'week', 'day'] as CalendarMode[]).map(m => (
                            <button key={m} onClick={() => setMode(m)} style={{
                                padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                                background: mode === m ? C.greenMid : 'transparent',
                                color: mode === m ? '#fff' : C.textSub,
                                border: `1px solid ${mode === m ? C.greenMid : C.border}`,
                            }}>{m.charAt(0).toUpperCase() + m.slice(1)}</button>
                        ))}
                        <Btn size="sm" variant="outline" onClick={loadAll}>↻</Btn>
                    </div>
                </div>

                {panelError && <Alert message={panelError} />}

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                    {Object.entries(STATUS_COLORS).map(([s, clr]) => (
                        <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                            <div style={{ width: 8, height: 8, borderRadius: 2, background: clr }} />
                            <span style={{ color: C.textSub }}>{s}</span>
                        </div>
                    ))}
                    {dragJobId && <span style={{ fontSize: 11, color: C.greenMid, fontWeight: 700 }}>Drop on a date to schedule ↗</span>}
                </div>

                {mode === 'month' && (
                    <DispatchMonthGrid
                        year={start.getFullYear()} month={start.getMonth()}
                        jobsByDate={jobsByDate} dragJobId={dragJobId} dropHighlight={dropHighlight}
                        isDateBlocked={isDateBlockedForFilter}
                        onDropDate={handleDropOnDate} onDragOver={setDropHighlight}
                        onSelectJob={setSelectedJob}
                    />
                )}
                {mode === 'week' && (
                    <DispatchWeekGrid
                        startDate={start} jobsByDate={jobsByDate} dragJobId={dragJobId} dropHighlight={dropHighlight}
                        isDateBlocked={isDateBlockedForFilter}
                        onDropDate={handleDropOnDate} onDragOver={setDropHighlight}
                        onSelectJob={setSelectedJob}
                    />
                )}
                {mode === 'day' && (
                    <DispatchDayColumns
                        date={dayViewDate} contractors={contractors} assigned={assigned}
                        dragJobId={dragJobId} isAvailableOnDate={isAvailableOnDate} routeMap={routeMap}
                        onDragStart={setDragJobId} onDragEnd={() => setDragJobId(null)}
                        onDropContractor={handleDropOnContractor} onUnassign={handleUnassign}
                        onReschedule={openReschedule} onLoadRoute={loadRoute} onApplyRoute={applyRoute}
                    />
                )}
            </div>

            {/* Right sidebar: unassigned queue */}
            <div style={{ width: 250, flexShrink: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#6366F1', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    Unassigned ({unassigned.length})
                </div>
                <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 8 }}>Drag onto a date to schedule</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 600, overflowY: 'auto' }}>
                    {unassigned.length === 0 && (
                        <div style={{ color: C.textMuted, fontSize: 13, textAlign: 'center', padding: 24 }}>All jobs assigned</div>
                    )}
                    {unassigned.map(j => (
                        <div key={j.id}
                            draggable
                            onDragStart={() => setDragJobId(j.id)}
                            onDragEnd={() => { setDragJobId(null); setDropHighlight(null); }}
                            onClick={() => setSelectedJob(j)}
                            style={{
                                cursor: 'grab', userSelect: 'none',
                                background: dragJobId === j.id ? '#EEF2FF' : C.card,
                                border: `1px solid ${dragJobId === j.id ? '#6366F1' : C.border}`,
                                borderLeft: '4px solid #6366F1',
                                borderRadius: 8, padding: '8px 10px',
                                opacity: dragJobId && dragJobId !== j.id ? 0.5 : 1,
                                transition: 'all 0.1s',
                            }}>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{j.serviceType}</div>
                            <div style={{ fontSize: 11, color: C.textSub }}>{j.pickupAddress.split(',')[0]}</div>
                            {j.requestedDate && (
                                <div style={{ fontSize: 10, color: '#D97706', marginTop: 2 }}>
                                    📋 {new Date(j.requestedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </div>
                            )}
                            <div style={{ fontSize: 12, fontWeight: 700, color: C.greenMid }}>${j.totalPrice.toFixed(2)}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>

        {/* Contractor picker modal */}
        {pickerTarget && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: C.card, borderRadius: 12, padding: 24, width: 380, maxWidth: '92vw', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Assign {contractorLabel}</div>
                    <div style={{ fontSize: 12, color: C.textSub, marginBottom: 16 }}>
                        Scheduling for {new Date(pickerTarget.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </div>
                    {(() => {
                        const targetJob = [...jobs, ...unassigned].find(j => j.id === pickerTarget.jobId);
                        const dispatchRequiredSkills = certTrackingEnabled && targetJob
                            ? (pricing.find(r => (r.serviceTypeName || r.serviceType) === targetJob.serviceType)?.requiredSkills ?? [])
                            : [];
                        return (
                            <>
                                {dispatchRequiredSkills.length > 0 && (
                                    <div style={{ fontSize: 12, color: C.textSub, marginBottom: 10 }}>
                                        🔧 Requires: {dispatchRequiredSkills.join(', ')}
                                    </div>
                                )}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {contractors.map(c => {
                                        const avail = isAvailableOnDate(c.id, pickerTarget.date);
                                        const bgOk = !bgCheckRequired || c.backgroundCheckStatus === 'Clear';
                                        const contractorSkillNames = (c.skills ?? []).map(s => s.name.toLowerCase());
                                        const missingSkills = dispatchRequiredSkills.filter(s => !contractorSkillNames.includes(s.toLowerCase()));
                                        const hasSkills = missingSkills.length === 0;
                                        const eligible = avail && bgOk && c.docsApproved && hasSkills;
                                        const ineligibleReason = !bgOk ? 'BG check not cleared' : !c.docsApproved ? 'Docs not approved' : !hasSkills ? `Missing: ${missingSkills.join(', ')}` : 'Unavailable';
                                        return (
                                <button key={c.id}
                                    onClick={() => eligible && handlePickContractor(c.id)}
                                    style={{
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        padding: '10px 14px', borderRadius: 8, cursor: eligible ? 'pointer' : 'not-allowed',
                                        background: eligible ? C.greenBg : C.bg,
                                        border: `1px solid ${eligible ? C.greenMid : C.border}`,
                                        opacity: eligible ? 1 : 0.45, textAlign: 'left', width: '100%',
                                    }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{c.name}</div>
                                        <div style={{ fontSize: 11, color: C.textSub }}>{assigned[c.id]?.length ?? 0} jobs today</div>
                                    </div>
                                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: eligible ? C.green : '#9CA3AF', color: '#fff' }}>
                                        {eligible ? 'Available' : ineligibleReason}
                                    </span>
                                </button>
                                        );
                                    })}
                                </div>
                            </>
                        );
                    })()}
                    <button onClick={() => setPickerTarget(null)} style={{ marginTop: 16, width: '100%', padding: '9px 0', borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', fontSize: 13, color: C.textSub }}>
                        Cancel
                    </button>
                </div>
            </div>
        )}

        {/* Job detail modal */}
        {selectedJob && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                onClick={() => setSelectedJob(null)}>
                <div style={{ width: 360, maxWidth: '90vw', background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 }}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{selectedJob.serviceType}</div>
                        <button onClick={() => setSelectedJob(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: C.textMuted }}>×</button>
                    </div>
                    <div style={{ fontSize: 13, color: C.textSub, marginBottom: 8 }}>{selectedJob.pickupAddress}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                        <span style={{ background: STATUS_COLORS[selectedJob.status] || '#999', color: '#fff', borderRadius: 12, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{selectedJob.status}</span>
                        <span style={{ fontSize: 12, color: C.textSub }}>{selectedJob.estimatedUnits} units</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: C.greenMid }}>${selectedJob.totalPrice.toFixed(2)}</span>
                    </div>
                    {selectedJob.contractorName && <div style={{ fontSize: 12, color: C.textSub, marginBottom: 4 }}>{contractorLabel}: {selectedJob.contractorName}</div>}
                    {selectedJob.scheduledDate && <div style={{ fontSize: 12, color: C.textSub, marginBottom: 4 }}>Scheduled: {new Date(selectedJob.scheduledDate).toLocaleDateString()}</div>}
                    {selectedJob.scheduledTimeSlot && <div style={{ fontSize: 12, color: C.textSub, marginBottom: 4 }}>Slot: {selectedJob.scheduledTimeSlot}</div>}
                    {digitalWorkOrdersEnabled && <JobDetailWorkOrderSection jobId={selectedJob.id} />}
                    {serviceCatalogEnabled && !['Completed', 'Cancelled'].includes(selectedJob.status) && <JobDetailEstimateSection jobId={selectedJob.id} />}

                    {/* Payout override */}
                    <div style={{ marginTop: 14, padding: 12, background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.textSub, marginBottom: 8 }}>
                            Contractor Payout Override
                            {selectedJob.contractorPayoutOverride != null && (
                                <span style={{ marginLeft: 8, fontWeight: 400, color: C.green }}>currently ${selectedJob.contractorPayoutOverride.toFixed(2)}</span>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input type="number" min="0" step="0.01" placeholder="Override amount ($)"
                                value={payoutOverrideInput}
                                onChange={e => setPayoutOverrideInput(e.target.value)}
                                style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 13, background: C.card, color: C.text }} />
                            <button disabled={payoutOverrideSaving} onClick={async () => {
                                setPayoutOverrideSaving(true); setPayoutOverrideError('');
                                try {
                                    const val = payoutOverrideInput.trim() === '' ? null : parseFloat(payoutOverrideInput);
                                    await api.setPayoutOverride(selectedJob.id, val);
                                    setSelectedJob({ ...selectedJob, contractorPayoutOverride: val ?? undefined });
                                    setPayoutOverrideInput('');
                                } catch (e: any) { setPayoutOverrideError(e.message); }
                                finally { setPayoutOverrideSaving(false); }
                            }} style={{ padding: '6px 10px', borderRadius: 6, border: 'none', background: C.green, color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' as const }}>
                                {payoutOverrideSaving ? '…' : 'Set'}
                            </button>
                            {selectedJob.contractorPayoutOverride != null && (
                                <button disabled={payoutOverrideSaving} onClick={async () => {
                                    setPayoutOverrideSaving(true);
                                    try {
                                        await api.setPayoutOverride(selectedJob.id, null);
                                        setSelectedJob({ ...selectedJob, contractorPayoutOverride: undefined });
                                    } catch { }
                                    finally { setPayoutOverrideSaving(false); }
                                }} style={{ padding: '6px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.textMuted, fontSize: 11, cursor: 'pointer' }}>
                                    Clear
                                </button>
                            )}
                        </div>
                        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>Leave blank to use default rate. Clear to remove override.</div>
                        {payoutOverrideError && <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>{payoutOverrideError}</div>}
                    </div>

                    <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Btn size="sm" variant="outline" onClick={() => { setSelectedJob(null); openReschedule(selectedJob); }}>Reschedule</Btn>
                        {selectedJob.contractorId && (
                            <Btn size="sm" variant="outline" onClick={async () => { await handleUnassign(selectedJob.id); setSelectedJob(null); }}>Unassign</Btn>
                        )}
                        {!['Completed', 'Cancelled'].includes(selectedJob.status) && (
                            <Btn size="sm" variant="danger" onClick={() => setCancellingJob(selectedJob)}>Cancel job</Btn>
                        )}
                    </div>
                </div>
            </div>
        )}

        {cancellingJob && (
            <CancelJobModal
                jobId={cancellingJob.id}
                onConfirm={(reason) => handleCancelJob(cancellingJob.id, reason)}
                onClose={() => setCancellingJob(null)}
            />
        )}

        {/* Reschedule modal */}
        {rescheduleTarget && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ background: C.card, borderRadius: 12, padding: 24, width: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>Reschedule Job</div>
                    <div style={{ fontSize: 12, color: C.textSub, marginBottom: 16 }}>{rescheduleTarget.serviceType} · {rescheduleTarget.pickupAddress.split(',')[0]}</div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: C.textSub, display: 'block', marginBottom: 4 }}>Date</label>
                    <input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 14, marginBottom: 12, boxSizing: 'border-box' as any, background: C.bg, color: C.text }} />
                    <label style={{ fontSize: 12, fontWeight: 600, color: C.textSub, display: 'block', marginBottom: 4 }}>Time slot</label>
                    <input type="text" placeholder="e.g. 9am–12pm" value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)}
                        style={{ width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 14, marginBottom: 20, boxSizing: 'border-box' as any, background: C.bg, color: C.text }} />
                    {rescheduleError && <div style={{ fontSize: 12, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>{rescheduleError}</div>}
                    <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 16 }}>Customer will receive a push notification with the updated date and time.</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setRescheduleTarget(null)} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', fontSize: 13, color: C.textSub }}>Cancel</button>
                        <button onClick={saveReschedule} disabled={rescheduleSaving} style={{ flex: 1, padding: '9px 0', borderRadius: 7, border: 'none', background: C.green, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#fff' }}>
                            {rescheduleSaving ? 'Saving…' : 'Save & Notify'}
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}

function DispatchMonthGrid({ year, month, jobsByDate, dragJobId, dropHighlight, isDateBlocked, onDropDate, onDragOver, onSelectJob }: {
    year: number; month: number; jobsByDate: Record<string, CalendarJob[]>;
    dragJobId: string | null; dropHighlight: string | null;
    isDateBlocked: (ymd: string) => boolean;
    onDropDate: (ymd: string) => void; onDragOver: (ymd: string) => void;
    onSelectJob: (j: CalendarJob) => void;
}) {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: (number | null)[] = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
    while (cells.length % 7 !== 0) cells.push(null);
    const today = new Date();
    const isToday = (d: number) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, background: C.border, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                <div key={d} style={{ background: C.bg, padding: '6px 8px', fontSize: 11, fontWeight: 600, color: C.textSub, textAlign: 'center' }}>{d}</div>
            ))}
            {cells.map((day, i) => {
                const key = day ? `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}` : '';
                const dayJobs = day ? (jobsByDate[key] ?? []) : [];
                const isHovered = !!dragJobId && key === dropHighlight;
                const isBlocked = day ? isDateBlocked(key) : false;
                return (
                    <div key={i}
                        onDragOver={day ? (e: React.DragEvent) => { e.preventDefault(); onDragOver(key); } : undefined}
                        onDrop={day ? () => onDropDate(key) : undefined}
                        onDragLeave={day ? () => onDragOver('') : undefined}
                        style={{
                            background: isHovered ? '#F0FDF4' : isBlocked ? '#F9FAFB' : C.card,
                            minHeight: 80, padding: '4px 6px',
                            borderTop: isToday(day!) ? `2px solid ${C.greenMid}` : undefined,
                            outline: isHovered ? `2px solid ${C.greenMid}` : undefined,
                            opacity: isBlocked ? 0.55 : 1, transition: 'all 0.1s',
                        }}>
                        {day && (
                            <>
                                <div style={{ fontSize: 12, fontWeight: isToday(day) ? 700 : 400, color: isToday(day) ? C.greenMid : isBlocked ? C.textMuted : C.text, marginBottom: 4 }}>{day}</div>
                                {isBlocked && <div style={{ fontSize: 9, color: '#9CA3AF', marginBottom: 2 }}>unavailable</div>}
                                {dayJobs.slice(0, 3).map(j => (
                                    <div key={j.id} onClick={() => onSelectJob(j)} style={{ background: STATUS_COLORS[j.status] || '#999', color: '#fff', borderRadius: 3, padding: '1px 5px', fontSize: 10, fontWeight: 500, marginBottom: 2, cursor: 'pointer', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                        {j.serviceType}
                                    </div>
                                ))}
                                {dayJobs.length > 3 && <div style={{ fontSize: 10, color: C.textMuted }}>+{dayJobs.length - 3} more</div>}
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function DispatchWeekGrid({ startDate, jobsByDate, dragJobId, dropHighlight, isDateBlocked, onDropDate, onDragOver, onSelectJob }: {
    startDate: Date; jobsByDate: Record<string, CalendarJob[]>;
    dragJobId: string | null; dropHighlight: string | null;
    isDateBlocked: (ymd: string) => boolean;
    onDropDate: (ymd: string) => void; onDragOver: (ymd: string) => void;
    onSelectJob: (j: CalendarJob) => void;
}) {
    const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(startDate); d.setDate(d.getDate() + i); return d; });
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
            {days.map(day => {
                const key = day.toISOString().split('T')[0];
                const dayJobs = jobsByDate[key] ?? [];
                const isTodayCell = key === new Date().toISOString().split('T')[0];
                const isHovered = !!dragJobId && key === dropHighlight;
                const isBlocked = isDateBlocked(key);
                return (
                    <div key={key}
                        onDragOver={(e: React.DragEvent) => { e.preventDefault(); onDragOver(key); }}
                        onDrop={() => onDropDate(key)}
                        onDragLeave={() => onDragOver('')}
                        style={{
                            minHeight: 100, borderRadius: 8, padding: '6px 8px',
                            background: isHovered ? '#F0FDF4' : isBlocked ? '#F9FAFB' : C.card,
                            border: `2px ${isHovered ? 'solid' : 'dashed'} ${isHovered ? C.greenMid : isBlocked ? '#E5E7EB' : C.border}`,
                            opacity: isBlocked ? 0.6 : 1, transition: 'all 0.1s',
                        }}>
                        <div style={{ fontSize: 12, fontWeight: isTodayCell ? 700 : 500, color: isTodayCell ? C.greenMid : isBlocked ? C.textMuted : C.textSub, textAlign: 'center', marginBottom: 4 }}>
                            {day.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })}
                        </div>
                        {isBlocked && <div style={{ fontSize: 9, color: '#9CA3AF', textAlign: 'center', marginBottom: 4 }}>unavailable</div>}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {dayJobs.length === 0 && !dragJobId && <div style={{ fontSize: 11, color: C.textMuted, textAlign: 'center', padding: 8 }}>—</div>}
                            {dayJobs.map(j => (
                                <div key={j.id} onClick={() => onSelectJob(j)} style={{ background: STATUS_COLORS[j.status] || '#999', color: '#fff', borderRadius: 6, padding: '6px 8px', fontSize: 11, cursor: 'pointer' }}>
                                    <div style={{ fontWeight: 600 }}>{j.serviceType}</div>
                                    <div style={{ opacity: 0.85 }}>{j.pickupAddress.split(',')[0]}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function DispatchDayColumns({ date, contractors, assigned, dragJobId, isAvailableOnDate, routeMap, onDragStart, onDragEnd, onDropContractor, onUnassign, onReschedule, onLoadRoute, onApplyRoute }: {
    date: string; contractors: ContractorListItem[]; assigned: Record<string, CalendarJob[]>;
    dragJobId: string | null; isAvailableOnDate: (id: string, ymd: string) => boolean; routeMap: Record<string, any>;
    onDragStart: (id: string) => void; onDragEnd: () => void; onDropContractor: (id: string) => void;
    onUnassign: (jobId: string) => void; onReschedule: (j: CalendarJob) => void;
    onLoadRoute: (id: string) => void; onApplyRoute: (id: string) => void;
}) {
    return (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 8 }}>
            {contractors.map(c => {
                const avail = isAvailableOnDate(c.id, date);
                return (
                    <div key={c.id} style={{ minWidth: 220, flexShrink: 0 }}
                        onDragOver={(e: React.DragEvent) => e.preventDefault()}
                        onDrop={() => onDropContractor(c.id)}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{c.name}</div>
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                                    <div style={{ fontSize: 11, color: C.textMuted }}>{assigned[c.id]?.length ?? 0} job{(assigned[c.id]?.length ?? 0) !== 1 ? 's' : ''}</div>
                                    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 8, background: avail ? '#DCFCE7' : '#F3F4F6', color: avail ? '#16A34A' : '#9CA3AF' }}>
                                        {avail ? 'Available' : 'Unavailable'}
                                    </span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                                <Btn size="sm" variant="outline" onClick={() => onLoadRoute(c.id)} style={{ fontSize: 10, padding: '2px 6px' }}>Route</Btn>
                                {routeMap[c.id] && <Btn size="sm" onClick={() => onApplyRoute(c.id)} style={{ fontSize: 10, padding: '2px 6px' }}>Apply</Btn>}
                            </div>
                        </div>
                        {routeMap[c.id] && (
                            <div style={{ background: C.greenBg, border: `1px solid ${C.greenMid}`, borderRadius: 6, padding: '6px 10px', marginBottom: 8, fontSize: 11, color: C.green }}>
                                ≈ {routeMap[c.id].totalDistanceMiles} mi · {routeMap[c.id].estimatedDriveMinutes} min
                            </div>
                        )}
                        <div style={{ minHeight: 80, background: dragJobId ? (avail ? '#F0FDF4' : '#FEF9C3') : C.bg, border: `2px dashed ${dragJobId ? (avail ? C.greenMid : '#F59E0B') : C.border}`, borderRadius: 8, padding: 6, display: 'flex', flexDirection: 'column', gap: 6, transition: 'all 0.15s' }}>
                            {(assigned[c.id] ?? []).length === 0 && (
                                <div style={{ fontSize: 11, color: C.textMuted, textAlign: 'center', padding: 16 }}>Drop jobs here</div>
                            )}
                            {(assigned[c.id] ?? []).map((j: any, idx) => (
                                <div key={j.id}
                                    draggable
                                    onDragStart={() => onDragStart(j.id)}
                                    onDragEnd={onDragEnd}
                                    style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `4px solid ${STATUS_COLORS[j.status] || '#999'}`, borderRadius: 6, padding: '6px 8px', cursor: 'grab' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                                                <span style={{ fontSize: 12, fontWeight: 600 }}>#{j.jobSortOrder ?? idx + 1} {j.serviceType}</span>
                                                {j.selfAssigned && <span style={{ background: '#EEF2FF', color: '#4338CA', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700 }}>Self</span>}
                                            </div>
                                            <div style={{ fontSize: 11, color: C.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.pickupAddress.split(',')[0]}</div>
                                            {j.scheduledDate ? (
                                                <div style={{ fontSize: 10, color: C.green, marginTop: 1, fontWeight: 600 }}>📅 {new Date(j.scheduledDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{j.scheduledTimeSlot ? ` · ${j.scheduledTimeSlot}` : ''}</div>
                                            ) : j.requestedDate ? (
                                                <div style={{ fontSize: 10, color: '#D97706', marginTop: 1, fontWeight: 600 }}>📋 {new Date(j.requestedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} (requested)</div>
                                            ) : (
                                                <div style={{ fontSize: 10, color: '#EA580C', marginTop: 1 }}>{j.selfAssigned ? '📋 Use 📅 to assign date' : '⚠ No date set'}</div>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                            <button onClick={() => onReschedule(j)} title="Reschedule" style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 4, color: C.textSub, cursor: 'pointer', fontSize: 11, padding: '1px 5px' }}>📅</button>
                                            <button onClick={() => onUnassign(j.id)} style={{ background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', fontSize: 14, padding: '0 2px' }}>×</button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
